/**
 * 카드받기 (장비대금 카드수금) — /m/cardpay
 *
 * 결제 + 내역 통합 페이지. 레거시 _sky_m_10.php(ontab 분리)를 대체한다.
 * 가맹점 등록 상태(data-card-state)로 진입 화면이 갈린다:
 *   active=결제·내역 탭 / pending=가입 신청 접수 / unreg*=미등록 온보딩(서류등록→가입신청).
 *
 * 결제 탭은 '작업을 골라 담아 한 번에 결제' 구조다. 리스트 행은 요약 + 펼치면 오더 상세이고,
 * 체크는 상시 노출된다. 1건이든 N건이든 [선택하기] → pg-multipay 한 화면으로 모인다.
 * 결제는 이노페이 수기결제(payApi) — 결제창을 띄우지 않고 카드번호·유효기간을 받아 서버가 승인한다.
 */
export default class CardPay {
    /** 결제 필터 기본 기간 = 최근 5개월 */
    static DEFAULT_MONTHS = 5;

    /** 1회 결제 한도(부가세 포함 총액) — CardPaymentService::MAX_PAY_TOTAL 과 같은 값. 넘으면 결제 전에 안내하고 멈춘다 */
    static MAX_PAY_TOTAL = 200000000;

    /** 앱 shareFile 의 data URL 한도 — base64 본문 2.8MB(원본 약 2MB). 넘으면 앱이 PAYLOAD_TOO_LARGE 로 거절한다 */
    static SHARE_DATA_MAX = 2800000;

    /** 관리자 모드 결제취소 사유 — 시안(card_payment_renewal_5) 그대로. 기타는 메모 필수 */
    static ADMIN_CANCEL_REASONS = {
        ext: ['중복 결제', '금액 오류', '고객 요청', '작업 취소', '기타'],
        long: ['중복 결제', '금액 오류', '고객 요청', '기타'],
    };

    /** 중복결제 안내 창 (같은 작업/같은 카드로 이 시간 안에 다시 결제하면 알려준다 — 막지는 않는다) */
    static DUP_WINDOW_MS = 10 * 60 * 1000;

    /** 같은 일회용 키의 첫 결제가 진행 중(res.wait)일 때 다시 묻는 간격·횟수 — 서버가 35초까지만 wait 를 준다(CardPaymentService::REPLAY_WAIT_SEC) */
    static PAY_WAIT_MS = 3000;
    static PAY_WAIT_TRIES = 15;

    /** 가입신청 단계 제목 — 개인은 담당자(3) 단계를 건너뛴다 */
    static STEP_TITLES = { 1: '회사 정보', 2: '대표자 정보', 3: '담당자 정보', 4: '정산 정보' };

    /**
     * 서류 등록 방식 스위치 — true(지금): 여러 장 서류(MULTI_PAGE_DOCS)는 시트가 「한 장: 사진 촬영·갤러리 / 여러 장: 갤러리에서 여러 장 · PDF」.
     *   「갤러리에서 여러 장」은 앱이 2장 이상 고른 사진을 폰에서 PDF 한 파일로 묶어 올린다(need.pdf, 한도 MAX_ATTACH_BYTES 를 넘으면 앱이
     *   안내하고 올리지 않는다). 1장만 고르면 사진 그대로 온다(type 'I' — 수신부는 둘 다 받는다). 「PDF」는 고른 PDF 파일을 그대로 받는다(from:'F').
     *   한 장 서류는 사진 촬영·갤러리만.
     *   사진 여러 장을 서버가 묶는 경로(추가 등록·좌우 넘기기·mergeDocPhotos)는 꺼진다.
     *   둘 다 지원 앱만 — 모르는 구앱(getCapabilities.uploadPdf / uploadFile 없음)은 업데이트 안내.
     * false: 사진만. 등기부등본·주주명부는 여러 장을 받아 '다음'에서 서버가 PDF 로 묶는다(mergeDocPhotos). (2026-10-22) 더 쓰지 않는다
     */
    static DOC_PDF_PICK = true;

    /** 이노페이 첨부 한도(바이트) — CardMerchantService::MAX_ATTACH_BYTES 와 같은 값. 앱이 묶는 PDF 의 한도로 넘긴다(need.pdfMaxBytes) */
    static MAX_ATTACH_BYTES = 5.5 * 1024 * 1024;

    /** 「갤러리에서 여러 장」으로 한 번에 고를 수 있는 장 수 — 앱이 PDF 로 묶는다. 넘치면 용량 한도에서 앱이 막는다 */
    static BUNDLE_MAX_SHEET = 10;

    /** 여러 장(최대 multiDoc)을 받는 서류 — 법인 등기부등본·주주명부만. 나머지(사업자등록증 포함)는 한 장 */
    static MULTI_PAGE_DOCS = ['corpReg', 'shareholders'];

    /** 서류 키 → 표기명 (서류 등록 화면) */
    static DOC_NAMES = { biz: '사업자등록증', idFront: '신분증 원본 앞면', idBack: '신분증 원본 뒷면', corpReg: '법인 등기부등본', shareholders: '주주명부', bank: '통장 사본' };

    /**
     * 가맹 상호 — 한글·영문·숫자·띄어쓰기와 기호 & ' - . · , ( ) 만 남긴다. 서버와 같은 규칙이다(CardMerchantService::cleanCompanyName).
     * 낱자모(ㄱ·ㅏ)는 한글을 치는 중이라 입력 중에는 남기고, 칸을 떠날 때 뺀다.
     */
    static cleanCoName(v) {
        return String(v).replace(/㈜/g, '(주)').replace(/[^가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z0-9\s&',\-.·()]/g, '');
    }

    /** 상호 길이(byte) — 영문·숫자·기호 1, 그 밖 2. 서버 CardMerchantService::companyNameBytes 와 같다 */
    static coNameBytes(v) {
        return Array.from(String(v)).reduce((n, c) => n + (c.charCodeAt(0) < 128 ? 1 : 2), 0);
    }

    /** 입력칸 값을 거른다 — 바뀌었을 때만 쓰고 커서 자리를 지킨다 */
    static stripCoNameInput(el) {
        const clean = CardPay.cleanCoName(el.value);
        if (clean === el.value) return;
        const caret = CardPay.cleanCoName(el.value.slice(0, el.selectionStart)).length;
        el.value = clean;
        el.setSelectionRange(caret, caret);
    }

    /** 서류 사진에서 읽어 가입폼에 채우는 칸 — [OCR 결과 키, 표기명, 가입폼 칸]. 읽은 결과 알리기(ocrReport)가 쓴다 */
    static OCR_FIELDS = {
        biz: [['coNm', '상호', '#rf-biz'], ['coNo', '사업자번호', '#rf-bizno'], ['bossNm', '대표자', '#rf-ceo-name'],
            ['bsKind', '업태', '#rf-uptae'], ['gdKind', '업종', '#rf-upjong']],
        bank: [['settlmnt_acnt_bank_cdNm', '은행', '#rf-bank'], ['settlmnt_acnt_no', '계좌번호', '#rf-acct'], ['settlmnt_acnt_nm', '예금주', '#rf-holder']],
    };

    /**
     * 서류 6종 전부 raw <input type=file> 대신 앱 네이티브 브릿지(카드 OCR과 같은 방식)로
     * 촬영/업로드한다 — 이 웹뷰는 onShowFileChooser 를 구현하지 않아 raw file input이 무반응이다
     * (위 bs-card-scan 주석 참고). PDF는 못 받는다(브릿지가 이미지 전용) — 원래도 이 화면에서
     * PDF 업로드가 동작한 적이 없어서(같은 이유) 잃는 기능은 없다.
     *
     * 카드에서 새로 올린 사진은 회원의 공식 서류(회원 서류 — DocService::rowsForOwner)에 반영되지 않는 1회용이다 —
     * biz·bank 는 새로 안 찍으면(즉 이 세션에 올린 게 없으면) 기존 회원서류를 "읽기 전용"으로
     * 재사용한다(storedDocImages, CardMerchantService::resolveAttachSources 폴백).
     *
     * 태그 carddoc — 서버가 이 태그의 파일을 24시간 뒤에 지운다(FileUploadService::EXPIRE_HOURS). 그 안에는 신청이
     * 거절돼도 다시 찍지 않고 다시 신청한다. 지나면 초안(REG_DRAFT_TTL)도 함께 끝나 다시 등록한다.
     * 참조 검사는 CardPayController::resolveBridgeFile.
     */
    static CARD_DOC_TAG = 'carddoc';

    /**
     * 가맹가입 이어하기 — 찍은 서류 참조·폼 입력값을 이 폰(localStorage)에 둔다. 전화·앱 전환으로 웹뷰가
     * 다시 떠도 다시 찍지 않게. 키에 회원번호를 붙인다(같은 폰의 다른 계정이 보지 않게).
     * 24시간은 서버가 서류 참조를 받아주는 시간(CardPayController::BRIDGE_FILE_HOURS)과 맞춘 값이다 —
     * 그보다 오래된 초안의 사진은 제출해도 '파일 없음'이 되므로 복원하지 않는다. 제출에 성공하면 지운다.
     */
    static REG_DRAFT_KEY = 'm:cardpay:regDraft:';
    static REG_DRAFT_TTL = 24 * 60 * 60 * 1000;
    static REG_FORM_FIELDS = ['#rf-biz', '#rf-bizno', '#rf-corpno', '#rf-zip', '#rf-addr', '#rf-addr2', '#rf-uptae',
        '#rf-upjong', '#rf-item', '#rf-ceo-name', '#rf-ceo-phone', '#rf-ceo-birth', '#rf-ceo-tel', '#rf-ceo-email',
        '#rf-mgr-name', '#rf-mgr-phone', '#rf-mgr-tel', '#rf-mgr-email', '#rf-bank', '#rf-acct', '#rf-holder'];

    /** 카드번호 허용 자리수 — 16자 고정 아님(아멕스 15자리 등). 상한 16은 InnoPay cardNum 필드 자체가 N16(최대 16)이라 그대로 둔다. */
    static CARD_NUM_MIN = 14;
    static CARD_NUM_MAX = 16;

    /** 카드번호 하이픈 그룹 — 카드사마다 실제 인쇄 자릿수가 다르다(4-4-4-4 고정 아님).
     *  BIN(맨 앞자리)으로 판별: [BIN정규식, 그룹배열]. 매칭 안 되면 CARD_GROUP_DEFAULT. */
    static CARD_GROUPS = [
        [/^3[47]/, [4, 6, 5]],             // 아멕스 15자리
        [/^3(?:0[0-5]|[689])/, [4, 6, 4]], // 다이너스 14자리
    ];
    static CARD_GROUP_DEFAULT = [4, 4, 4, 4];

    /** 카드빈 조회 — 직접입력은 자리수가 다 찬 뒤 이만큼 멈추면(또는 칸을 벗어나면) 조회한다 */
    static BIN_DEBOUNCE_MS = 600;

    constructor() {
        this.state = 'active';   // 진입 가맹점 상태 (data-card-state)
        this.today = '';         // 서버 기준 오늘 (data-today)
        this.isOwner = false;    // 소유자 시점 (data-card-owner) — 내역에 소속자 결제건이 섞여 온다
        this.canRegister = false; // 가맹 신청 자격 = 소유자 본인 (data-can-register)
        this.detItem = null;     // 내역 상세/취소 대상 아이템

        // ----- 결제 리스트 필터 -----
        this.keywords = [];      // 검색 칩(모두 포함하는 건만 노출)
        this.range = { start: '', end: '' };
        this.cal = { y: 0, m: 0, start: '', end: '', phase: 'start' };

        // ----- 결제 진행 -----
        this.payItems = [];      // 결제 화면에 담긴 작업들
        this.manualItem = null;  // 직접입력 결제 대상
        this.recentPays = [];    // 중복결제 감시 {ts, wkey, card}

        // ----- 가맹점 등록 -----
        this.regDocs = { biz: false, idFront: false, idBack: false, corpReg: false, shareholders: false, bank: false };
        this.docFields = {};     // OCR 로 뽑아 이노페이에 넘길 값 (저장 안 함 — 이 세션에만)
        this.docPreview = {};    // 촬영 이미지 objectURL (미리보기 전용)
        this.pendingFile = {};   // 아직 OCR 안 돌린 파일 — '다음' 누를 때 한꺼번에 인식한다(biz/bank 전용, 인식 후 비운다)
        this.regFiles = {};      // 가입신청 제출 때까지 들고 있는 서류 원본 — attach_file_nm1~4 로 그대로 전송한다
        this.docRaw = { biz: null, bank: null };   // [개발용] OCR 원문/파싱원본
        this.ocrResult = {};     // 이번 '다음'에서 사진을 읽은 서류 → 읽은 값({} = 못 읽음). 읽은 결과 알리기(ocrReport) 뒤 비운다
        this.uploadKey = null;   // 업로드 진행 중인 서류 키
        this.docPrevKey = null;  // 미리보기 중인 서류 키
        this.curTerms = null;    // 열려 있는 약관 키
        this.regIdx = 0;
        this.regSeq = [1, 2, 4];

        this._pgStack = [];                      // history 로 관리하는 오버레이(.pg) 스택
        this._onPgPop = this._onPgPop.bind(this);

        this.HD = [];  // 결제 내역 — 결제일 묶음 [{date, items}]. 내역 탭을 열 때·바닥에 닿을 때 받는다(loadHistory)
        this.SD = [];  // 결제 대상 작업 — 결제 탭을 열 때·더 예전 기간을 고를 때 받는다(loadSites)
        this.sitesLoaded = false;
        // 결제 대상 받은 범위 — 이 작업일 이후 전부(끝은 열림). '' = 전체 기간. null = 아직 안 받음
        this.sitesFrom = null;
        this.sitesSeq = 0;
        // 내역 무한스크롤 상태 — seq 는 검색을 새로 걸면 늘려서, 늦게 온 이전 응답을 버린다
        this.hist = { loaded: false, loading: false, hasMore: true, offset: 0, q: '', seq: 0 };
        this.RD = {};  // 전표 가맹점 정보
    }

    // 화면모드·글자크기(<html data-theme/data-fs)는 App\Common\ThemeBoot 가 <head> 에서
    // 페인트 전에 붙인다 — 이 페이지는 그 값을 읽기만 한다.
    initialize() {
        this.initData();
        this.initPage();
        this.initHandler();
        if (String(this.state).indexOf('unreg') === 0) this.restoreRegDraft();   // 미가입 화면에서만 이어하기
    }

    /** 데이터·진입 상태 초기화 (HD/SD 는 탭을 열 때 받는다 — showTab) */
    initData() {
        const $app = $('#cardpay-app');
        this.state = $app.attr('data-card-state') || 'active';
        this.today = $app.attr('data-today') || this.ymd(new Date());
        // 소유자 시점이면 내역에 소속자 결제건까지 섞여 온다 — 결제자를 함께 표기한다(item.mine/payerName)
        this.isOwner = $app.attr('data-card-owner') === '1';
        this.canRegister = $app.attr('data-can-register') === '1';
        // 가맹점 수수료율 — 등급별로 다르다(CardPaymentService::feeRate). 결제 화면 계산에 쓴다.
        this.feeRate = parseFloat($app.attr('data-fee-rate')) || 0.014;
        // 페이앳 회원(이노페이 승인 전) — 결제는 구 페이앳 화면이다. 내역만 이 화면(CardMerchantService::usesPayat)
        this.payatPay = $app.attr('data-payat-pay') === '1';
        // 서류 한 건 사진 최대 장수 — 2장 이상이면 '다음'에서 PDF 로 묶는다(docMerge). PDF 선택 방식이면 1(한 장씩)
        this.multiDoc = CardPay.DOC_PDF_PICK ? 1 : (parseInt($app.attr('data-multi-doc'), 10) || 0);

        this.RD = this.readJson('#cardpay-merchant', {});
        // 가입폼 회사정보 폴백(g5_company) — 이번 세션에 사진을 새로 안 올린 경우에만 쓴다(applyCompanyFallback)
        this.FD = this.readJson('#cardpay-form-defaults', {});

        // 이미 등록된 서류 이미지(서버 URL). 새로 촬영하면 objectURL 로 덮인다.
        const stored = this.readJson('#cardpay-docs', {});
        Object.keys(stored).forEach((k) => { if (stored[k]) this.docPreview[k] = stored[k]; });
        this.storedPreview = stored;   // 새로 찍은 사진을 버릴 때 되돌릴 저장서류 미리보기(restoreStoredDoc)

        // 기본 기간 = 최근 5개월 ~ (끝은 안 막는다). 예정된 작업완료건(work_day 가 미래)도
        // 기본 화면에 바로 보여야 해서, 종료일을 오늘로 자르지 않는다 — 위쪽 경계가 없을 뿐
        // 시작일(5개월 전) 아래는 여전히 걸러진다(filteredSites). 완전히 넓히려면 [전체 기간].
        const from = new Date(this.today);
        from.setMonth(from.getMonth() - CardPay.DEFAULT_MONTHS);
        this.range = { start: this.ymd(from), end: '' };

        // 서버가 이미 들고 있는 서류(사업자등록증·통장사본)는 등록완료로 시작한다. 이번 세션에
        // 새로 찍은 사진이 없어도 '다음' 누르는 시점에 그 저장된 이미지로 OCR 을 다시 돌린다
        // (runPendingOcr) — 여기서 미리 회사정보로 채우지 않는다.
        this.regDocs.biz = $app.attr('data-card-biz') === '1';
        this.regDocs.bank = $app.attr('data-card-bank') === '1';
        this.storedDocs = { biz: this.regDocs.biz, bank: this.regDocs.bank };   // 저장서류가 있는가(restoreStoredDoc)

        // (예전엔 개발서버에서 신분증·등기부등본·주주명부를 촬영 없이 등록완료로 쳐주는 편의가 있었다.
        // 이제 이 서류들은 실제 파일이 attach_file_nm 로 그대로 나가야 해서 화면만 속이면 제출이 막힌다
        // — 그 상태로 '완료' 표시를 두면 탭해도 그대로 미리보기만 뜨고 실제로는 찍은 게 없어 헷갈린다.
        // 그래서 편의는 없앴다: 개발서버도 실서버와 똑같이 실제로 찍어야 등록완료로 보인다.)
    }

    /** 진입 가맹점 상태에 따른 첫 화면 */
    initPage() {
        const wantHist = new URLSearchParams(location.search).get('tab') === 'hist';
        switch (this.state) {
            // 가맹 가입 전(미등록·심사중)도 결제/내역 탭은 그대로 — 결제 탭 자리에 등록 안내·심사중 화면(onboardTc),
            // 내역은 그대로 보인다(이전 페이앳 결제·소속 결제가 있을 수 있다)
            case 'pending':
            case 'unreg':
            case 'unreg-1doc':
            case 'unreg-nodoc':
                this.showTab(wantHist ? 'hist' : 'pay');
                break;
            case 'mid-add':
                this.showMidAdd();
                break;
            case 'admin':
                this.showAdmin();
                break;
            default:
                // 드로어 메뉴가 결제/내역을 분리해서 보낸다(Layout.js 5010/5011) — ?tab=hist 로 진입탭 지정
                this.showTab(wantHist ? 'hist' : 'pay');
                break;
        }
    }

    /* ======================================================================
     *  이벤트 바인딩
     * ==================================================================== */

    initHandler() {
        this.bindShell();
        this.bindPayList();
        this.bindPayPage();
        this.bindManual();
        this.bindHistory();
        this.bindCancel();
        this.bindMerchant();
        this.bindAdmin();
    }

    /** 헤더·탭·오버레이 공통 */
    bindShell() {
        const self = this;

        $('#cardpay-back, #cardpay-close').on('click', () => self.exit());
        $('.lt').on('click', function () { self.showTab($(this).data('t')); });

        $('#ov').on('click', () => self.closeBS());
        $('.bsc').on('click', () => self.closeBS());
        $(document).on('click', '[data-consent-detail]', (e) => {
            e.preventDefault(); e.stopPropagation(); self.openBS('bs-consent');
        });
    }

    /** 결제 탭 — 안내·검색·기간·리스트·선택 */
    bindPayList() {
        const self = this;

        $('#pay-info-btn').on('click', function () {
            $(this).toggleClass('on'); $('#pay-info-acc').toggleClass('open');
        });
        $('#btn-direct').on('click', () => self.openManual());

        // 검색어는 칩으로 쌓인다(모두 포함하는 건만 노출)
        $('#pay-search')
            .on('input', function () { $('#pay-searchbar').toggleClass('has', $.trim(this.value) !== ''); })
            .on('keydown', function (e) { if (e.which === 13) { e.preventDefault(); self.addKeyword(); $(this).blur(); } });
        $('#pay-search-go').on('click', () => self.addKeyword());
        $('#pay-search-clear').on('click', () => {
            $('#pay-search').val('').focus(); $('#pay-searchbar').removeClass('has');
        });
        $(document).on('click', '.pay-chip-kw button', function () {
            self.keywords.splice(parseInt($(this).data('i')), 1);
            self.renderKeywords(); self.renderSiteList();
        });

        // 기간 — 시작일 → 종료일 단계 선택
        $('#pp-daterange').on('click', function () {
            const open = $('#pay-period-acc').toggleClass('open').hasClass('open');
            $(this).attr('aria-expanded', open);
            if (!open) { self.syncRangeBtn(); return; }
            self.cal.start = self.range.start;
            self.cal.end = self.range.end;
            self.cal.phase = 'start';
            self.openCalAt(self.cal.end || self.today);
        });
        $('#pp-f-start').on('click', () => { self.cal.phase = 'start'; self.openCalAt(self.cal.start); });
        $('#pp-f-end').on('click', () => { self.cal.phase = 'end'; self.openCalAt(self.cal.end || self.cal.start); });
        $('#pp-cal-prev').on('click', () => self.shiftCal(-1));
        $('#pp-cal-next').on('click', () => self.shiftCal(1));
        $(document).on('click', '#pp-cal-grid .pp-day', function () { self.pickCalDay($(this)); });
        $('#pp-apply').on('click', function () {
            if ($(this).hasClass('dis')) return;
            self.range = { start: self.cal.start, end: self.cal.end };
            self.closePeriod(); self.applyRange();
        });
        $('#pp-all').on('click', () => {
            self.range = { start: '', end: '' };
            self.cal = { y: self.cal.y, m: self.cal.m, start: '', end: '', phase: 'start' };
            self.closePeriod(); self.applyRange();
        });

        // 리스트 — 체크는 선택, 그 외 탭은 오더 상세 펼침
        $(document).on('click', '#pay-site-list .wl-check', function (e) {
            e.stopPropagation();
            const $row = $(this).closest('.wl-card');
            if ($row.hasClass('done')) return;
            $row.toggleClass('sel');
            self.updSelection();
        });
        $(document).on('click', '#pay-site-list .pay-row', function (e) {
            if ($(e.target).closest('.wl-check, .pr-detail').length) return;
            const willOpen = !$(this).hasClass('open');
            $('#pay-site-list .pay-row.open').removeClass('open');   // 한 번에 하나만 펼침
            if (willOpen) $(this).addClass('open');
        });
        $(document).on('click', '.od-equip-toggle', function (e) {
            e.stopPropagation();
            const open = $(this).closest('.od-equip').toggleClass('open').hasClass('open');
            $(this).find('.oet-label').text(open ? '접어두기' : '펼쳐보기');
        });

        $('#mb-clear').on('click', () => {
            $('#pay-site-list .wl-card.sel').removeClass('sel'); self.updSelection();
        });
        $('#b-multipay').on('click', function () {
            if ($(this).hasClass('dis')) return;
            self.openPayPage(self.selectedItems());
        });
    }

    /** 결제 화면 (선택 작업 · 카드정보) */
    bindPayPage() {
        const self = this;

        $('#mp2-back').on('click', () => this.closePg('pg-multipay'));
        $(document).on('change', 'input[name="mp2-vat"]', () => { if (self.payItems.length) self.renderPayPage(); });
        $('#mp2-chk').on('change', () => self.checkPayForm());
        $('#mp2-ci-num')
            .on('input', function () { self.formatCardNum(this); self.checkPayForm(); self.scheduleBin('mp2'); })
            .on('blur', () => self.lookupBin('mp2'));
        $('#mp2-ci-exp').on('input', function () { self.formatCardExp(this); self.checkPayForm(); });
        $('#mp2-ocr-btn').on('click', () => self.openCardScan('mp2'));
        $('#mp2-pay').on('click', function () {
            if ($(this).hasClass('dis')) return;
            self.confirmPay('mp2');
        });

        $('#cardinfo-back').on('click', () => this.closePg('pg-cardinfo'));
        $('#ci-num')
            .on('input', function () { self.formatCardNum(this); self.checkCardInfoForm(); self.scheduleBin('ci'); })
            .on('blur', () => self.lookupBin('ci'));
        $('#ci-exp').on('input', function () { self.formatCardExp(this); self.checkCardInfoForm(); });
        $('#ci-ocr-btn').on('click', () => self.openCardScan('ci'));
        $('#b-cardpay').on('click', function () {
            if ($(this).hasClass('dis')) return;
            self.confirmPay('ci');
        });

        // 카드 촬영/갤러리 선택 — bs-card-scan 에서 고른 값(from)을 그대로 앱 브릿지에 넘긴다.
        // ⚠️ from 을 생략해 앱 자체 선택다이얼로그(showMediaSourceSelectionDialog)에 맡기면 안 된다 —
        //    그 다이얼로그는 태그 구분 없이 촬영 옵션에 GPS 프로바이더 체크가 걸려 있어(diary1 용으로
        //    보이는 게이트), 위치서비스가 꺼진 기기에서는 촬영 버튼이 조용히 아무 반응이 없다(확인됨).
        //    from:'C'/'G' 를 직접 지정하면 이 다이얼로그를 거치지 않고 바로 카메라/갤러리로 간다.
        $('#card-scan-close').on('click', () => self.closeBS());
        $(document).on('click', '#bs-card-scan .up-opt', function () {
            const from = $(this).data('scan');
            self.closeBS();
            // original — 카드번호를 읽어야 해서 앱 리사이즈(1.2MP)를 건너뛴다(구앱은 무시하고 압축).
            // guide:'card' — 촬영(from:'C')이면 시스템 카메라 대신 앱 안 카드 스캔 화면(가이드
            // 프레임)으로 띄운다. 갤러리(from:'G')엔 영향 없고, 구앱은 몰라서 무시(시스템 카메라
            // 로 자연 폴백) — 게이트 없이 항상 켜도 된다.
            $j.F.Upload.addPhotoHere({
                tag: 'cardocr', context: 'cardOcrScan', max_sheet: 1, from,
                original: $j.F.App.originalUploadSupported(),
                guide: 'card',
            });
        });

        // raw <input capture> 는 이 앱 웹뷰가 onShowFileChooser 를 구현하지 않아 아예 무반응이라 쓸 수 없다(확인됨).
        $j.Common.EventBus.on(G5['NS'], 'upload:image', function (e, payload) {
            if ($j.F.Upload.getContext() !== 'cardOcrScan') return;
            const file = Array.isArray(payload) ? payload[0] : payload;
            if (file && file.id) self.scanCard(file);
        });

        // 전표 보기는 완료화면을 닫고 바로 전표를 연다 — history.back() 과 pushState 가 겹치지 않게
        // 여기서만 직접 숨긴다(닫기 버튼은 평소대로 closePg).
        $('#b-done-receipt').on('click', () => {
            $('#pg-done').removeClass('show');
            this._pgStack = this._pgStack.filter((id) => id !== 'pg-done');
            this.showTab('hist');
            this.openReceipt(false, this.doneItem);
        });
        $('#b-done-close').on('click', () => { this.closePg('pg-done'); this.showTab('hist'); });
    }

    /** 직접입력 */
    bindManual() {
        const self = this;

        $('#manual-back, #b-msite').on('click', () => self.closePg('pg-manual'));
        $(document).on('change', 'input[name="m-vat"]', () => self.calcManualVat());
        $(document).on('change', '#m-chk-a', () => self.checkManualForm());
        // 콤마·'원' 자동포맷 — .val() 재대입은 커서를 끝으로 보내므로, 지운 만큼 커서를
        // '앞쪽에 남은 숫자 개수'로 되돌린다(안 하면 중간 수정·Backspace 연타가 씹힌다).
        $('#m-price').on('input', function () {
            const el = this;
            const pos = el.selectionStart || 0;
            const digitsBefore = (el.value.slice(0, pos).match(/[0-9]/g) || []).length;
            const v = el.value.replace(/[^0-9]/g, '');
            el.value = v ? Number(v).toLocaleString() + ' 원' : '';
            let newPos = 0, seen = 0;
            while (newPos < el.value.length && seen < digitsBefore) {
                if (/[0-9]/.test(el.value[newPos])) seen++;
                newPos++;
            }
            try { el.setSelectionRange(newPos, newPos); } catch (_) {}
            self.checkManualForm();
        });
        // 주소는 지도에서 받는다 (칸·검색 버튼 어느 쪽을 눌러도 지도 오픈)
        $(document).on('click', '#m-addr, #btn-addr-search', () => self.searchAddress());
        $('#b-mpay').on('click', function () {
            if ($(this).hasClass('dis')) return;
            self.openCardInfo();
        });

        this.bindWorkDatePicker($('#m-date'), () => self.checkManualForm());
    }

    /** 내역 · 상세 · 전표 */
    bindHistory() {
        const self = this;

        $('#hist-info-btn').on('click', function () {
            $(this).toggleClass('on'); $('#hist-info-acc').toggleClass('open');
            $('#hist-search-area').removeClass('open'); $('#hist-search-btn').removeClass('on');
        });
        $('#hist-search-btn').on('click', function () {
            $(this).toggleClass('on'); $('#hist-search-area').toggleClass('open');
            $('#hist-info-acc').removeClass('open'); $('#hist-info-btn').removeClass('on');
        });
        $('#hist-search-go').on('click', () => self.loadHistory(true));
        $('#history-search').on('keydown', function (e) {
            if (e.which === 13) { e.preventDefault(); self.loadHistory(true); $(this).blur(); }
        });
        // 무한스크롤 — 바닥 근처(300px)에 닿으면 다음 묶음
        $('#tc-hist .tc-scroll').on('scroll', function () {
            if (this.scrollTop + this.clientHeight >= this.scrollHeight - 300) self.loadHistory(false);
        });

        // id = g5_card_payment.num (이전내역도 이 표로 옮겨와 같은 번호 체계다)
        $(document).on('click', '.hc', function () { self.openDetailById($(this).attr('data-id')); });
        $(document).on('click', '#detail-back', () => self.closePg('pg-detail'));
        $(document).on('click', '#det-receipt', () => self.openReceipt(self.detItem.x, self.detItem));
        $(document).on('click', '#det-share, #det-download', function () {
            self.shareReceipt($(this).attr('id') === 'det-share', '매출전표');
        });
        // 취소건은 결제/취소 어느 전표인지 먼저 고른다
        $(document).on('click', '#det-share-select, #det-download-select', function () {
            self._receiptAction = $(this).attr('id') === 'det-share-select' ? 'share' : 'download';
            self.openBS('bs-receipt-sel');
        });
        $(document).on('click', '#bs-receipt-sel .rsel-btn', function () {
            const name = $(this).data('rv') === 'cancel' ? '취소전표' : '결제전표';
            self.closeBS();
            self.shareReceipt(self._receiptAction === 'share', name);
        });
        $('#r-x').on('click', () => self.closePg('pg-r'));
    }

    /** 결제 취소 */
    bindCancel() {
        const self = this;

        // closePg('pg-detail') 뒤에 곧바로 openCancelForm() 을 부르면 안 된다 — closePg 는
        // history.back() 을 걸어놓고 반환할 뿐이라 popstate 는 나중(비동기)에 온다. 그 사이 openPg
        // 가 pg-cf 를 새로 push 해버려서, 뒤늦게 도착한 popstate 가 detail 이 아니라 방금 연 pg-cf
        // 를 팝해 닫아버린다(취소폼이 떴다가 바로 사라짐). cancelPayment() 완료 처리와 같은 방식으로
        // history 를 건드리지 않고 detail 만 직접 지운 뒤 openCancelForm 이 pg-cf 를 새로 push한다.
        $(document).on('click', '#det-cancel-btn', () => {
            $('#pg-detail').removeClass('show');
            self._pgStack = self._pgStack.filter((id) => id !== 'pg-detail');
            self.openCancelForm();
        });
        $(document).on('click', '#det-legacy-cancel-btn', function () { self.cancelLegacyPayment($(this)); });
        $('#cf-back').on('click', () => self.closePg('pg-cf'));
        $('#cf-reason-sel').on('change', function () {
            $('#cf-reason-etc').toggle($(this).val() === '기타');
            if ($(this).val() !== '기타') $('#cf-reason-etc').val('');
            self.checkCancelForm();
        });
        $('#cf-exp').on('input', function () { self.formatCardExp(this); });
        $('#cf-reason-etc, #cf-pw, #cf-exp').on('input', function () {
            $(this).removeClass('err').next('.fe').hide(); self.checkCancelForm();
        });
        $('#b-cfs').on('click', function () { if (!$(this).hasClass('dis')) self.openCancelModal(); });
        $('#b-cancel-done').on('click', () => { self.closePg('pg-cancel-done'); self.showTab('hist'); });
    }

    /** 가맹점 등록 — 미등록 → 서류 등록 → 가입 신청 */
    bindMerchant() {
        const self = this;

        $('#unreg-back').on('click', () => self.exit());
        $('#b-reg').on('click', () => self.openRegDocs());
        // 신청 접수 확인 → 이 페이지를 다시 연다(서버가 state=pending 으로 심사중 화면을 그린다). 뒤로가기로 나가면
        // 카드받기 이전 화면(메뉴·레거시 등)으로 튀어나가 방금 한 가입이 어디 갔는지 모르게 된다. replace 라 히스토리는 그대로
        $('#review-back').on('click', () => self.exit());

        // ----- 가맹점 추가등록(MID 보유) 안내 -----
        $('#midadd-back').on('click', () => self.exit());
        $('#midadd-ok').on('click', () => self.showTab('pay'));
        $('#midadd-dl').on('click', function () {
            const url = $(this).data('url');
            if (!url) return;
            if (!$j.F.App.shareFile(url)) window.open(url, '_blank');
        });
        $(document).on('click', '.ma-copy', function () { self.copyText($(this).data('copy')); });

        // ----- 서류 등록 -----
        $('#regdocs-back').on('click', () => self.closePg('pg-reg-docs'));
        $(document).on('change', 'input[name="biztype"]', function () { self.setBizType(this.value); });
        // 썸네일은 바로 확대보기, 나머지 영역은 등록/보기·수정 시트
        $(document).on('click', '#pg-reg-docs .doc-up-thumb.has', function (e) {
            e.stopPropagation();
            self.zoomDoc($(this).closest('.doc-up').data('k'));
        });
        $(document).on('click', '#pg-reg-docs .doc-up', function () {
            const key = $(this).data('k');
            if ($(this).hasClass('done')) self.openDocPreview(key);
            else self.openUploadSheet(key);
        });
        // 확대는 [눌러서 크게 보기] 버튼으로만 — 사진 자체를 눌러도 열리지 않는다(넘기다 잘못 눌리지 않게)
        $(document).on('click', '#docprev-img .docprev-zoom', () => self.zoomDoc(self.docPrevKey, self.docPrevIdx));
        // 좌우 화살표 — PC(마우스)에서도 넘긴다
        $(document).on('click', '#docprev-img .docprev-nav', function () {
            if (!self.docPrevSw) return;
            if ($(this).hasClass('prev')) self.docPrevSw.slidePrev(); else self.docPrevSw.slideNext();
        });
        $('#upload-close').on('click', () => self.closeBS());
        // 서류 촬영/선택 — 카드 OCR과 같은 네이티브 브릿지(위 CARD_DOC_TAG 주석 참고).
        // original — 사업자등록증·신분증 등 글자를 읽어야 해서 앱 리사이즈를 건너뛴다(구앱은 무시).
        // pdf — 앱이 PDF 선택창을 바로 띄운다(from:'F'), PC 는 PDF 파일창. from:'F' 를 모르는 구앱은 엉뚱한 창이 떠서 업데이트 안내로 막는다.
        //   앱 판정은 window.App — 대리진입 중에도 업로드는 앱으로 가므로(startUploadImage ignoreProxy) isApp() 으로 가르면 안 된다
        // 갤러리 여러 장(multiDoc 장까지) — 수신부가 서류 사진 목록으로 들고 있다가 '다음'에서 PDF 로 묶는다(mergeDocPhotos)
        // PC 파일창도 같다 — multiple 로 여러 장 고르고, batch 로 다 올린 뒤 배열 한 번으로 받는다
        // bundle(갤러리에서 여러 장) — 앱이 고른 사진을 폰에서 PDF 한 파일로 묶어 올린다(need.pdf). 한도(MAX_ATTACH_BYTES)를 넘으면
        //   앱이 화질을 낮춰 다시 묶고, 끝까지 넘으면 올리지 않고 회원에게 「장 수를 줄여 달라」 안내한다(콜백은 {result:false}).
        //   서버 묶기(mergeDocPhotos)는 더 쓰지 않는다. 구앱(getCapabilities.uploadPdf 없음)은 업데이트 안내 — 낱장으로 올라와 버리니 막는다
        $(document).on('click', '#bs-upload .up-opt', function () {
            const up = $(this).data('up');
            const from = { cam: 'C', gallery: 'G', pdf: 'F' }[up] || 'G';
            // 여러 장 서류의 갤러리는 자동으로 묶기(여러 장 가능) — 묶기를 아는 앱일 때만. 모르는 앱·PC 는 한 장 갤러리로 둔다
            const bundle = up === 'gallery' && self.isMultiPageDoc(self.uploadKey) && !!(window.App && $j.F.App.pdfUploadSupported());
            if (from === 'F' && window.App && !$j.F.App.fileUploadSupported()) {
                $j.Common.Util.swalert('앱을 최신 버전으로 업데이트하면 PDF 를 올릴 수 있어요.');
                return;
            }
            // [추가 등록]이면 이미 있는 쪽수만큼 덜 고른다
            const room = self.docLimit(self.uploadKey) - (self.uploadAppend ? self.docPages(self.uploadKey) : 0);
            const multi = from === 'G' && !bundle && room > 1;
            self.closeBS();
            $j.F.Upload.addPhotoHere({
                tag: CardPay.CARD_DOC_TAG, context: 'cardDocUpload', max_sheet: bundle ? CardPay.BUNDLE_MAX_SHEET : (multi ? room : 1), from,
                multiple: multi, batch: multi,
                // 묶으면 앱이 어차피 JPEG 로 줄이므로 원본 요구는 의미가 없다
                original: !bundle && $j.F.App.originalUploadSupported(),
                pdf: bundle, pdfMaxBytes: CardPay.MAX_ATTACH_BYTES,
            });
        });
        $j.Common.EventBus.on(G5['NS'], 'upload:image', function (e, payload) {
            if ($j.F.Upload.getContext() !== 'cardDocUpload') return;
            const files = (Array.isArray(payload) ? payload : [payload]).filter((f) => f && f.id);
            if (!files.length) return;
            // 여러 장은 첫 장이 서류, 나머지는 뒤쪽 사진으로 들고 있다가 '다음'에서 PDF 로 묶는다(mergeDocPhotos)
            if (self.uploadAppend) {
                // [추가 등록] — 붙일 수 없게 됐으면(한도) 기존 서류를 바꾸지 않고 새 사진만 버린다
                if (self.canAppendDoc(self.uploadKey)) self.addDocPhotos(self.uploadKey, files);
                else files.forEach((f) => self.discardBridgeFile({ fileId: f.id, filePath: f.path }));
                return;
            }
            // 한 장(사진·PDF) — 이노페이 첨부 한도를 넘으면 받지 않는다(checkDocSize). 여러 장은 '다음'에서 묶을 때 서버가 줄인다
            if (files.length === 1) {
                const key = self.uploadKey;
                self.checkDocSize(files[0]).then((ok) => {
                    if (!ok) return;   // 넘으면 서버가 이미 지웠다
                    // 확인을 기다리는 사이 다른 서류 칸을 열었으면 이 파일은 갈 곳이 없다 — 남기지 않는다
                    if (self.uploadKey !== key) { self.discardBridgeFile({ fileId: files[0].id, filePath: files[0].path }); return; }
                    self.applyBridgeDoc(files[0]);
                });
                return;
            }
            self.applyBridgeDoc(files[0]);
            self.addDocPhotos(self.uploadKey, files.slice(1));
        });
        $('#docprev-close').on('click', () => self.closeBS());
        $('#docprev-re').on('click', () => { const k = self.docPrevKey; self.closeBS(); self.openUploadSheet(k); });
        $('#docprev-add').on('click', () => { const k = self.docPrevKey; self.closeBS(); self.openUploadSheet(k, true); });
        $('#docprev-del').on('click', () => self.removeDoc());
        $(document).on('change', '.terms-row input', () => self.checkRegDocs());
        $(document).on('click', '.terms-row', function (e) {
            if ($(e.target).is('input')) return;   // 체크는 약관을 읽어야 켜진다
            e.preventDefault();
            // 체크칸을 눌렀고 이미 동의한 약관이면 동의를 푼다(끄는 건 읽지 않아도 된다). 그 밖에는 약관을 연다 — 켜는 건 [확인하고 동의]로만
            const $in = $(this).find('input');
            if ($(e.target).closest('.ck').length && $in.prop('checked')) {
                $in.prop('checked', false);
                self.checkRegDocs();
                return;
            }
            self.openTerms($(this).data('terms'));
        });
        $('#terms-back').on('click', () => self.closePg('pg-terms'));
        $('#terms-confirm').on('click', function () {
            if ($(this).hasClass('is-locked')) { self.scrollTermsToBottom(); return; }   // 아직 다 안 읽음 — 내려준다
            if (self.curTerms) $('#a-' + self.curTerms).prop('checked', true);
            self.checkRegDocs();
            self.closePg('pg-terms');
        });
        $('#b-regdocs-next').on('click', function () {
            if ($(this).hasClass('dis')) return;
            $j.Common.Util.loadingOverlay(true);
            // 여러 장인 서류를 PDF 로 묶은 다음 인식한다 — 묶다 실패하면 여기서 멈춘다(그 서류를 다시 등록)
            self.mergeDocPhotos().then((ok) => {
                if (!ok) { $j.Common.Util.loadingOverlay(false); return; }
                self.ocrResult = {};
                return Promise.all([self.runPendingOcr('biz'), self.runPendingOcr('bank')]).then(() => {
                    $j.Common.Util.loadingOverlay(false);
                    self.ocrWait(false);
                    // 사업자등록증이 고른 구분(개인/법인)과 다르면 가입폼으로 넘기지 않는다 — 서류부터 달라서
                    // (개인=신분증, 법인=등기부등본·주주명부) 그대로 입력해 봐야 신청에서 막힌다. 안내창에서 구분을 바꾸게 한다
                    if (self.blockBizTypeMismatch()) return;
                    self.openRegApply();
                    self.ocrReport();
                });
            });
        });

        // ----- 가입 신청 폼 -----
        $('#regapply-back').on('click', () => {
            if (self.regIdx > 0) self.regGoStep(self.regIdx - 1);
            else self.closePg('pg-reg-apply');
        });
        $('#b-reg-prev').on('click', () => { if (self.regIdx > 0) self.regGoStep(self.regIdx - 1); });
        $('#b-reg-next').on('click', function () { if (!$(this).hasClass('dis')) self.regNext($(this)); });
        $('#rf-addr-search').on('click', () => self.searchCompanyAddress());
        $('#rf-same').on('change', function () {
            if (!this.checked) return;
            $('#rf-mgr-name').val($('#rf-ceo-name').val());
            $('#rf-mgr-phone').val($('#rf-ceo-phone').val());
            $('#rf-mgr-tel').val($('#rf-ceo-tel').val());
            $('#rf-mgr-email').val($('#rf-ceo-email').val());
        });
        $('#rf-bizno').on('input', function () { this.value = self.maskBizNo(this.value); });
        // 조합 중에 값을 바꾸면 한글이 깨진다 — 조합이 끝난 뒤에 거른다
        $('#rf-biz').on('input compositionend', function (e) {
            if (e.type === 'input' && e.originalEvent && e.originalEvent.isComposing) return;
            CardPay.stripCoNameInput(this);
        }).on('blur', function () {
            CardPay.stripCoNameInput(this);
            this.value = this.value.replace(/[ㄱ-ㅎㅏ-ㅣ]/g, '');
        });
        $('#rf-ceo-phone, #rf-mgr-phone').on('input', function () { this.value = self.maskPhone(this.value); });
        $('#rf-ceo-tel, #rf-mgr-tel').on('input', function () { this.value = self.maskTel(this.value); });
        // 유선전화 없음 — 대표자 휴대폰으로 채우고 잠근다. 휴대폰을 고치면 따라간다. 풀면 비운다
        $('#rf-ceo-tel-same').on('change', function () {
            $('#rf-ceo-tel').val(this.checked ? self.maskTel($('#rf-ceo-phone').val() || '') : '')
                .prop('readonly', this.checked).toggleClass('fi-locked', this.checked);
        });
        $('#rf-ceo-phone').on('input', function () {
            if ($('#rf-ceo-tel-same').prop('checked')) $('#rf-ceo-tel').val(self.maskTel(this.value));
        });
        $('#rf-ceo-birth').on('input', function () { this.value = this.value.replace(/\D/g, '').slice(0, 6); });
        $('#rf-acct').on('input', function () { this.value = this.value.replace(/\D/g, '').slice(0, 16); });

        // 정산은행 — #rf-bank(hidden select)가 실제 값을 들고 있다. 코드표에 있는 은행만 신청되므로 직접입력은 끈다.
        // 페이지 로컬 토큰(#cardpay-app 스코프)이 먹도록 그 안에 붙인다.
        $j.Ui.BankField.mount('#rf-bank-field', { select: '#rf-bank', free: false, placeholder: '은행 선택 또는 검색', container: '#cardpay-app' });
        // 가맹가입 입력이 바뀔 때마다 이어하기 초안 갱신(입력 중엔 잠깐 모았다가)
        const saveDraft = $j.Common.Util.debounce(() => self.saveRegDraft(), 500);
        $(document).on('input change', '#pg-reg-apply input, #pg-reg-apply select, input[name="biztype"]', saveDraft);
    }

    /* ======================================================================
     *  화면 전환
     * ==================================================================== */

    /** 페이지 이탈 (안드로이드 웹뷰 뒤로) */
    exit() { history.back(); }

    /** 메인 탭(결제/내역) 전환 */
    showTab(tab) {
        if (tab === 'pay' && this.payatPay) { location.replace('/m/card?tab=1'); return; }
        $('#main-tab').show();
        $('.lt').removeClass('on').filter('[data-t="' + tab + '"]').addClass('on');
        $('.tc').hide();
        // 가맹 가입 전이면 결제 탭 자리에 등록 안내·심사중 화면 — 탭 줄에 뒤로가기가 있어 그 화면 자체 헤더는 감춘다
        const onboard = tab === 'pay' ? this.onboardTc() : '';
        if (onboard) { $(onboard).css('display', 'flex').children('.hd').hide(); return; }
        $('#tc-' + tab).css('display', 'flex');
        // 결과 확인 중인 결제가 있으면 탭에 들어올 때마다 다시 받는다 — 그새 승인·미승인으로 정해졌을 수 있다
        if (tab === 'hist') { if (this.hist.loaded && !this.hasChecking()) this.renderHistory(); else this.loadHistory(true); }
        if (tab === 'pay') { if (this.sitesFrom !== null) this.renderSiteList(); else if (!this._sitesLoading) this.loadSites(); }
    }

    /**
     * 결제 탭 리스트 받기 — 지금 기간의 시작일부터 끝까지(끝은 열어둔다). 받은 범위(sitesFrom) 안의
     * 기간은 화면이 거르고, 그보다 예전을 고를 때만 다시 받는다(applyRange).
     * 서버는 시작일을 인덱스로 자른다 — 전체 기간('')은 큰 가맹점이 수십 초 걸리니 로딩을 띄운다.
     */
    loadSites() {
        const from = this.range.start || '';
        const seq = ++this.sitesSeq;
        this.sitesLoaded = false;
        this._sitesLoading = true;
        $('#empty-pay').hide();
        $('#pay-site-list').show().html('<div class="psl-empty">불러오는 중…</div>');
        $.get('/m/cardpay/sites', { from })
            .done((res) => {
                if (seq !== this.sitesSeq) return;
                this.SD = (res && res.sites) || [];
                this.sitesFrom = from;
                this.sitesLoaded = true;
                this.renderSiteList();
            })
            .fail(() => {
                if (seq !== this.sitesSeq) return;
                $('#pay-site-list').html('<div class="psl-empty">목록을 불러오지 못했습니다</div>');
                $j.Common.Util.tsalert('error', '목록을 불러오지 못했습니다.');
            })
            .always(() => { if (seq === this.sitesSeq) this._sitesLoading = false; });
    }

    /** 기간을 바꾼 뒤 — 받은 범위 안이면 화면에서 거르고, 더 예전(또는 전체)이면 다시 받는다 */
    applyRange() {
        const from = this.range.start || '';
        const covered = this.sitesFrom === '' || (this.sitesFrom !== null && from !== '' && from >= this.sitesFrom);
        if (covered) this.renderSiteList();
        else this.loadSites();
    }

    /**
     * 내역 한 묶음 받기. reset 이면 처음부터(탭 첫 진입·검색), 아니면 이어서(바닥에 닿음).
     * 검색어는 서버가 거른다 — 화면엔 받은 묶음만 있어서다(CardPaymentService::historyItems).
     */
    loadHistory(reset) {
        const h = this.hist;
        if (reset) {
            h.seq++;
            h.offset = 0;
            h.hasMore = true;
            h.loading = false;
            h.q = $.trim($('#history-search').val() || '');
            this.HD = [];
        }
        if (h.loading || !h.hasMore) return;
        h.loading = true;
        const seq = h.seq;
        if (reset) this.renderHistory();

        $.get('/m/cardpay/history', { offset: h.offset, q: h.q })
            .done((res) => {
                if (seq !== h.seq) return;
                const items = (res && res.items) || [];
                h.offset += items.length;
                h.hasMore = !!(res && res.hasMore);
                h.loaded = true;
                this.appendHistory(items);
            })
            .fail(() => { if (seq === h.seq) { h.hasMore = false; $j.Common.Util.tsalert('error', '내역을 불러오지 못했습니다.'); } })
            .always(() => {
                if (seq !== h.seq) return;
                h.loading = false;
                this.renderHistory();
                // 받은 묶음이 화면을 다 못 채우면 스크롤이 안 생겨 다음 묶음을 못 부른다 — 바로 하나 더
                const el = $('#tc-hist .tc-scroll')[0];
                if (h.hasMore && el && el.offsetParent && el.scrollHeight <= el.clientHeight + 300) this.loadHistory(false);
            });
    }

    /**
     * 받은 묶음을 결제일 묶음(HD) 뒤에 잇는다. 결제 직후 맨 위에 얹은 건(afterPay)이 다음 묶음에 다시
     * 오면(서버 순서가 한 칸 밀린다) id 로 걸러 두 번 보이지 않게 한다.
     */
    appendHistory(items) {
        const have = {};
        this.HD.forEach((g) => g.items.forEach((i) => { have[i.id] = true; }));
        items.forEach((i) => {
            if (have[i.id]) return;
            const last = this.HD[this.HD.length - 1];
            if (last && last.date === i.date) last.items.push(i);
            else this.HD.push({ date: i.date, items: [i] });
        });
    }

    /** 가맹점 미등록 온보딩 */
    /** 가맹 가입 전 결제 탭 자리에 뜨는 화면 — 가입한(active) 회원이면 '' */
    onboardTc() {
        if (this.state === 'pending') return '#tc-review';
        return String(this.state).indexOf('unreg') === 0 ? '#tc-unreg' : '';
    }

    showUnreg() {
        $('.lt').removeClass('on');
        $('.tc').hide();
        $('#main-tab').hide();
        $('#tc-unreg').css('display', 'flex').children('.hd').show();
    }

    /**
     * 전자서명·심사 안내 화면을 자체 헤더와 함께 띄운다 — 신청 직후(showPending). 승인 전 재진입은 결제 탭 자리에 뜬다(onboardTc).
     */
    showReview() {
        $('.lt').removeClass('on');
        $('.tc').hide();
        $('#main-tab').hide();
        $('#tc-review').css('display', 'flex').children('.hd').show();
    }

    /**
     * 가맹점 추가등록(MID 보유) 안내 — 진입 즉시 뜨는 1회성 안내. 뒤로가기(exit)로만 나가는 다른
     * 상태 화면(unreg/review)과 달리 '확인'은 정상 카드받기 화면(pay 탭)으로 넘어간다
     * (CardPayController::MID_ADD_NOTICE_MB_NO — 임시 개별 게이트).
     */
    showMidAdd() {
        $('.lt').removeClass('on');
        $('.tc').hide();
        $('#main-tab').hide();
        $('#tc-mid-add').css('display', 'flex');
    }

    /**
     * 가입 신청 직후 — 전자서명·심사 안내(tc-review)로 넘어간다.
     *
     * 서류등록·가입폼이 쌓아둔 history 항목(_pgStack)을 여기서 한번에 되돌린다 — 안 그러면
     * 뒤로가기를 눌러도 이미 비운 _pgStack 때문에 _onPgPop 이 아무 반응을 안 하다가, 쌓인
     * 항목을 다 소진한 뒤에야 카드페이지 밖(홈/서랍)으로 훅 튀어나가는 것처럼 보인다.
     * 여기서 미리 되돌려두면 다음 뒤로가기 한 번으로 바로 이전 화면(홈/서랍 등)으로 나간다.
     */
    showPending() {
        const d = new Date();
        $('#review-date').text(d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'));
        $('#review-name').text($('#rf-biz').val() || '');
        this.state = 'pending';   // 이후 결제 탭을 누르면 등록 안내가 아니라 이 화면(onboardTc)
        $('.pg').removeClass('show');
        if (this._pgStack.length) { history.go(-this._pgStack.length); }
        this._pgStack = [];
        this.showReview();
    }

    /* ======================================================================
     *  공통 유틸
     * ==================================================================== */

    /** 클립보드 복사(mailto 제목/수신처 등 짧은 텍스트) — 실패해도 조용히 넘어가지 않고 프롬프트로 보여준다 */
    copyText(text) {
        text = String(text == null ? '' : text);
        const done = () => $j.Common.Util.tsalert('success', '복사했습니다.');
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(text).then(done, () => window.prompt('아래 내용을 복사하세요', text));
            return;
        }
        const $t = $('<textarea>').val(text).css({ position: 'fixed', top: '-9999px' }).appendTo('body');
        $t[0].select();
        try { document.execCommand('copy'); done(); } catch (e) { window.prompt('아래 내용을 복사하세요', text); }
        $t.remove();
    }

    /** 요율 → '1.6%' (결제 당시 요율이 없으면 이 가맹점 현재 요율) */
    feePct(rate) { return +((rate || this.feeRate) * 100).toFixed(2) + '%, VAT 별도'; }

    /** 상세 정산 내역의 수수료 줄 — 요율은 VAT 별도로 적고, 금액은 수수료 VAT 까지 합친 item.fee (shapeItem) */
    feeRowsHtml(item) {
        return '<div class="det-row"><span class="dl">카드사 수수료 (' + this.feePct(item.feeRate) + ')</span><span class="dv">-' + this.format(item.fee) + ' 원</span></div>';
    }

    /** 천단위 콤마 */
    format(n) { return Number(n || 0).toLocaleString(); }

    /** HTML 이스케이프 — 주소·이름 등 DB 값을 innerHTML 로 그릴 때 반드시 통과시킨다 */
    esc(v) {
        return String(v == null ? '' : v)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    /** 블레이드 주입 JSON(<script type="application/json">) 파싱 — 실패 시 기본값 */
    readJson(sel, fallback) {
        try { return JSON.parse($(sel).text() || 'null') || (fallback === undefined ? [] : fallback); }
        catch (e) { return fallback === undefined ? [] : fallback; }
    }

    /** Date → 'YYYY-MM-DD' */
    ymd(d) {
        return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    }

    /** 'YYYY-MM-DD' → 'YY.MM.DD' */
    shortDate(d) {
        const p = String(d || '').split('-');
        return p.length === 3 ? p[0].slice(2) + '.' + p[1] + '.' + p[2] : '-';
    }

    /** 부가세·수수료 계산 (sep=별도, inc=포함) */
    calcAmounts(amt, vatType) {
        let work, vat;
        if (vatType === 'sep') { work = amt; vat = Math.round(amt * 0.1); }
        else { work = Math.round(amt / 1.1); vat = amt - work; }
        const payTotal = work + vat;
        // 수수료(요율, VAT 별도) + 수수료 VAT 10% — CardPaymentService::feeSplit 과 같은 셈. fee 는 둘의 합
        const feeBase = Math.round(payTotal * this.feeRate);
        const fee = feeBase + Math.round(feeBase * 0.1);
        return { work, vat, payTotal, fee, deposit: payTotal - fee };
    }

    /** 카드번호 입력 마스크 — 숫자 최대 16자리(아멕스 등은 15자리로 끝남) + 카드사별 하이픈 그룹 */
    formatCardNum(el) {
        const v = el.value.replace(/\D/g, '').slice(0, CardPay.CARD_NUM_MAX);
        el.value = this.groupCardNum(v);
    }

    /** 숫자열 → 카드사별 하이픈 그룹 (CARD_GROUPS 에 없으면 4-4-4-4) */
    groupCardNum(digits) {
        const hit = CardPay.CARD_GROUPS.find(([re]) => re.test(digits));
        const groups = hit ? hit[1] : CardPay.CARD_GROUP_DEFAULT;

        let out = '', i = 0;
        for (const g of groups) {
            if (i >= digits.length) break;
            out += (out ? '-' : '') + digits.slice(i, i + g);
            i += g;
        }
        return out;
    }

    /** 카드번호 자리수 유효성 — 16자 고정이 아니라 카드사별로 다르다(아멕스 15자리 등) */
    isValidCardLen(len) {
        return len >= CardPay.CARD_NUM_MIN && len <= CardPay.CARD_NUM_MAX;
    }

    /** 유효기간 입력 마스크 — MM/YY */
    formatCardExp(el) {
        let v = el.value.replace(/\D/g, '').slice(0, 4);
        if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2);
        el.value = v;
    }

    maskBizNo(v) {
        const d = v.replace(/\D/g, '').slice(0, 10);
        if (d.length > 5) return d.slice(0, 3) + '-' + d.slice(3, 5) + '-' + d.slice(5);
        if (d.length > 3) return d.slice(0, 3) + '-' + d.slice(3);
        return d;
    }

    maskPhone(v) {
        const d = v.replace(/\D/g, '').slice(0, 11);
        if (d.length > 7) return d.slice(0, 3) + '-' + d.slice(3, 7) + '-' + d.slice(7);
        if (d.length > 3) return d.slice(0, 3) + '-' + d.slice(3);
        return d;
    }

    /** 유선전화 — 자리수로 그룹이 갈린다: 11자리(031 등 3자리 지역번호)=3-4-4, 10자리(02 등)=2-4-4 */
    maskTel(v) {
        const d = v.replace(/\D/g, '').slice(0, 11);
        if (/^1[5-9]\d{2}/.test(d)) return d.length > 4 ? d.slice(0, 4) + '-' + d.slice(4, 8) : d;   // 대표번호 1588-0000
        // 지역번호는 서울(02)만 2자리 — 길이로 가르면 031-123-4567(10자리)이 03-1123-4567 로 잘못 끊긴다
        const a = d.startsWith('02') ? 2 : 3;
        const head = d.slice(0, a), rest = d.slice(a);
        if (rest.length > 7) return head + '-' + rest.slice(0, 4) + '-' + rest.slice(4);
        if (rest.length > 3) return head + '-' + rest.slice(0, 3) + '-' + rest.slice(3);
        return rest ? head + '-' + rest : head;
    }

    /* ======================================================================
     *  결제 탭 — 검색 / 기간 / 리스트
     * ==================================================================== */

    /** 검색어를 칩으로 추가 (칩은 AND 조건) */
    addKeyword() {
        const v = $.trim($('#pay-search').val());
        if (!v) return;
        if (this.keywords.indexOf(v) < 0) this.keywords.push(v);
        $('#pay-search').val('');
        $('#pay-searchbar').removeClass('has');
        this.renderKeywords();
        this.renderSiteList();
    }

    renderKeywords() {
        const html = this.keywords.map((kw, i) =>
            '<span class="pay-chip-kw">' + this.esc(kw)
            + '<button data-i="' + i + '" aria-label="' + this.esc(kw) + ' 삭제"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button></span>'
        ).join('');
        $('#pay-chips').html(html);
    }

    /** 기간 버튼 활성 표시 — 기본(최근 5개월 ~ 끝없음)이 아니면 강조 */
    syncRangeBtn() {
        const from = new Date(this.today);
        from.setMonth(from.getMonth() - CardPay.DEFAULT_MONTHS);
        const isDefault = this.range.start === this.ymd(from) && this.range.end === '';
        $('#pp-daterange').toggleClass('active', !isDefault);
        const label = this.range.start && this.range.end
            ? this.shortDate(this.range.start) + '~' + this.shortDate(this.range.end)
            : '기간';
        $('#pp-daterange span').text(isDefault ? '기간' : label);
    }

    closePeriod() {
        $('#pay-period-acc').removeClass('open');
        $('#pp-daterange').attr('aria-expanded', false);
        this.syncRangeBtn();
    }

    openCalAt(base) {
        const p = String(base || this.today).split('-');
        this.cal.y = parseInt(p[0]);
        this.cal.m = parseInt(p[1]) - 1;
        this.renderCal();
    }

    shiftCal(delta) {
        this.cal.m += delta;
        if (this.cal.m < 0) { this.cal.m = 11; this.cal.y--; }
        else if (this.cal.m > 11) { this.cal.m = 0; this.cal.y++; }
        this.renderCal();
    }

    /** 시작일 → 종료일 순서로 찍는다. 시작일보다 앞을 찍으면 시작일이 다시 잡힌다. */
    pickCalDay($el) {
        const d = $el.attr('data-d');
        if (!d || $el.hasClass('dis') || $el.hasClass('empty')) return;

        if (this.cal.phase === 'start') {
            this.cal.start = d;
            if (this.cal.end && this.cal.end < d) this.cal.end = '';
            this.cal.phase = 'end';
        } else if (!this.cal.start || d < this.cal.start) {
            this.cal.start = d; this.cal.end = ''; this.cal.phase = 'end';
        } else {
            this.cal.end = d;
        }
        this.renderCal();
    }

    renderCal() {
        const { y, m, start, end } = this.cal;
        $('#pp-cal-title').text(y + '년 ' + (m + 1) + '월');

        const first = new Date(y, m, 1).getDay();
        const days = new Date(y, m + 1, 0).getDate();
        let h = '';
        for (let i = 0; i < first; i++) h += '<div class="pp-day empty"></div>';
        for (let d = 1; d <= days; d++) {
            const ds = y + '-' + String(m + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0');
            let cls = 'pp-day';
            if (start && end) {
                if (ds === start && ds === end) cls += ' startend';
                else if (ds === start) cls += ' start';
                else if (ds === end) cls += ' end';
                else if (ds > start && ds < end) cls += ' inrange';
            } else if (start && ds === start) cls += ' startend';
            h += '<div class="' + cls + '" data-d="' + ds + '">' + d + '</div>';
        }
        $('#pp-cal-grid').html(h);

        $('#pp-fv-start').text(start ? this.shortDate(start) : '-');
        $('#pp-fv-end').text(end ? this.shortDate(end) : '-');
        $('#pp-f-start').toggleClass('on', this.cal.phase === 'start');
        $('#pp-f-end').toggleClass('on', this.cal.phase === 'end');
        $('#pp-apply').toggleClass('dis', !(start && end));
    }

    /** 필터(기간 + 검색칩) 통과한 작업만 */
    filteredSites() {
        const { start, end } = this.range;
        const kws = this.keywords.map((k) => k.toLowerCase());

        return this.SD.filter((i) => {
            // 시작·종료를 각각 따로 본다 — 기본값이 '5개월 전 ~ 끝없음'이라 end 만 비어 있는
            // 경우가 흔하다. 예전처럼 `start && end &&` 로 묶으면 end 가 없을 때 start 까지
            // 통째로 안 걸러졌다(기본 기간보다 오래된 것도 다 보임).
            if (start && i.wd < start) return false;
            if (end && i.wd > end) return false;
            if (!kws.length) return true;
            const hay = (i.addr + ' ' + i.eq + ' ' + (i.ktext || '') + ' ' + (i.ownerName || '')).toLowerCase();
            return kws.every((k) => hay.indexOf(k) > -1);
        });
    }

    /** 결제 대상 작업 리스트 렌더 (일자 구분 + 요약행 + 오더 상세 아코디언) */
    renderSiteList() {
        if (!this.sitesLoaded) return;               // 아직 받는 중 — '작업 없음'을 먼저 띄우지 않는다
        const list = this.filteredSites();
        $('#psl-count').text(list.length);
        this.syncRangeBtn();

        if (!this.SD.length) {                       // 결제 가능한 작업 자체가 없음
            $('#pay-site-list').empty().hide();
            $('#empty-pay').css('display', 'flex');   // flex 칸 — show() 는 block 으로 연다
            this.updSelection();
            return;
        }
        $('#empty-pay').hide();
        $('#pay-site-list').show();

        if (!list.length) {
            $('#pay-site-list').html('<div class="psl-empty">검색 결과가 없습니다</div>');
            this.updSelection();
            return;
        }

        let html = '';
        let lastDate = '';
        list.forEach((i) => {
            if (i.dateLabel !== lastDate) { html += '<div class="wl-date">' + this.esc(i.dateLabel) + '</div>'; lastDate = i.dateLabel; }
            html += this.siteRow(i);
        });
        $('#pay-site-list').html(html);
        this.updSelection();
    }

    /** 작업 1건 = 요약 2줄 + 접힌 오더 상세 */
    siteRow(i) {
        const stCls = i.paid ? 'paid' : (i.st === '작업완료' ? 'done2' : 'working');
        const when = [i.dateLabel, i.time, i.dur].filter(Boolean).join(' · ');

        let h = '<div class="wl-card pay-row' + (i.paid ? ' done' : '') + '" data-id="' + i.id + '">';
        h += '<div class="pr-head">';
        h += '<div class="pr-sum-r1">'
            + '<span class="wl-check"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg></span>'
            + '<span class="pr-status ' + stCls + '">' + this.esc(i.paid ? '수금완료' : i.st) + '</span>'
            + '<span class="pr-when">' + this.esc(when) + '</span>'
            + '<span class="pr-amt">' + this.format(i.amt) + '원</span></div>';
        h += '<div class="pr-sum-r2">'
            + (i.drv ? '<span class="pr-num">' + this.esc(i.drv) + '</span>' : '')
            + (i.ktext ? '<span class="pr-type ' + (i.kind === 'bal' ? 'bal' : '') + '">' + this.esc(i.ktext) + '</span>' : '')
            + '<span class="pr-addr">' + this.esc(i.addr) + '</span>'
            + '<svg class="pr-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg></div>';
        h += '</div>';
        h += '<div class="pr-detail">' + this.orderDetail(i) + '</div>';
        return h + '</div>';
    }

    /** 펼친 오더 상세 — 전달사항 + 연락처 + 장비 정보 (오더 조작 기능은 오더 상세 페이지 담당) */
    orderDetail(i) {
        let h = '';
        if (i.message) h += '<div class="od-note">' + this.esc(i.message) + '</div>';

        h += '<div class="od-body"><div class="od-info-wrap"><div class="od-info">';
        if (i.siteName || i.sitePhone) {
            h += '<div class="od-info-row"><span class="od-il">현장</span><span class="od-iv">' + this.esc([i.sitePhone, i.siteName].filter(Boolean).join(' ')) + '</span></div>';
        }
        if (i.ownerName || i.ownerPhone) {
            h += '<div class="od-info-row"><span class="od-il">발주자</span><span class="od-iv">' + this.esc([i.ownerPhone, i.ownerName].filter(Boolean).join(' ')) + '</span></div>';
        }
        if (i.workerName || i.workerPhone) {
            h += '<div class="od-info-row"><span class="od-il">수주자</span><span class="od-iv od-iv-primary">' + this.esc([i.workerPhone, i.workerName].filter(Boolean).join(' ')) + '</span></div>';
        }
        if (i.sosokName || i.sosokPhone) {
            h += '<div class="od-info-row"><span class="od-il">소속자</span><span class="od-iv od-iv-blue">' + this.esc([i.sosokPhone, i.sosokName].filter(Boolean).join(' ')) + '</span></div>';
        }
        h += '</div>';

        h += '<div class="od-equip">'
            + '<button class="od-equip-toggle" type="button"><span class="oet-label">펼쳐보기</span><svg class="oet-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg></button>'
            + '<div class="od-equip-txt"><div class="od-eq-name">' + this.esc(i.eq) + '</div>'
            + (i.plate ? '<div>' + this.esc(i.plate) + '</div>' : '')
            + (i.carDetail ? '<div>' + this.esc(i.carDetail) + '</div>' : '')
            + '</div>'
            + '<div class="od-equip-detail">'
            + '<div>' + this.esc(i.feeText) + '</div>'
            + '<div>오더번호 ' + this.esc(i.on) + '</div>'
            + (i.caseNo ? '<div>' + this.esc(i.caseLabel) + ' ' + this.esc(i.caseNo) + '</div>' : '')
            + '</div></div>';
        h += '</div>';

        // 하단 메모 — 일지 하단과 같은 My메모(발주 때 적은 전달사항은 위 od-note)
        if (i.memos && i.memos.length) {
            h += '<div class="od-workmemo"><div class="wm-list">' + i.memos.map((m) =>
                '<div class="wm-item"><div class="wm-top"><span class="wm-src">' + this.esc(m.role) + '</span>'
                + '<span class="wm-who">' + this.esc(m.who) + '</span><span class="wm-time">' + this.esc(m.time) + '</span></div>'
                + '<div class="wm-text">' + this.esc(m.text) + '</div></div>').join('') + '</div></div>';
        }

        return h + '</div>';
    }

    /** 체크된 작업 */
    selectedItems() {
        const ids = $('#pay-site-list .wl-card.sel').map(function () { return Number($(this).data('id')); }).get();
        return this.SD.filter((i) => ids.indexOf(Number(i.id)) > -1);
    }

    /** 선택 요약 바 갱신 */
    updSelection() {
        const items = this.selectedItems();
        const sum = items.reduce((a, i) => a + (i.amt || 0), 0);
        $('#mb-cnt').text(items.length);
        $('#mb-sum').text(this.format(sum) + '원');
        $('#multi-bar').css('display', items.length ? 'flex' : 'none');
        $('#b-multipay').toggleClass('dis', items.length === 0);
    }

    /* ======================================================================
     *  결제 화면 (선택 작업 1건·N건 공용)
     * ==================================================================== */

    openPayPage(items) {
        $j.Ui.DebugLog.push('openPayPage', ['items=' + items.length, 'ids=' + items.map((i) => i.id).join(',')]);
        if (!items.length) return;
        this.payItems = items.slice();
        $('#mp2-chk').prop('checked', false);
        $('#mp2-ci-num, #mp2-ci-exp').val('');
        $('input[name="mp2-vat"][value="sep"]').prop('checked', true);   // 직전 결제의 '포함'이 남지 않게 (openManual 과 같게)
        this.renderBin('mp2', null);
        this.renderPayPage();
        $('#mp2-pay').addClass('dis');
        this.openPg('pg-multipay');
    }

    renderPayPage() {
        const items = this.payItems;
        let h = '';
        let work = 0;

        items.forEach((i) => {
            work += (i.amt || 0);
            const chip = i.ktext ? '<span class="pr-type ' + (i.kind === 'bal' ? 'bal' : '') + '">' + this.esc(i.ktext) + '</span>' : '';
            h += '<div class="mp-item"><div class="mp-r1">'
                + (i.drv ? '<span class="mp-badge">' + this.esc(i.drv) + '</span>' : '')
                + '<span class="nm">' + this.esc(i.eq || '작업') + '</span>'
                + '<span class="mp-amt">' + this.format(i.amt) + '원</span></div>'
                + '<div class="mp-sub">' + chip + '<span class="mp-det">'
                + this.esc([i.dateLabel, i.time, i.dur, i.addr].filter(Boolean).join(' · '))
                + '</span></div></div>';
        });

        const r = this.calcAmounts(work, $('input[name="mp2-vat"]:checked').val());
        $('#mp2-list').html(h);
        $j.Ui.DebugLog.push('renderPayPage', [
            'payItems=' + items.length, 'htmlLen=' + h.length,
            '#mp2-list found=' + $('#mp2-list').length, '#mp2-list children=' + $('#mp2-list').children().length,
        ]);
        $('#mp2-cnt').text(items.length);
        $('#mp2-w').text(this.format(r.work) + ' 원');
        $('#mp2-v').text(this.format(r.vat) + ' 원');
        $('#mp2-total').text(this.format(r.payTotal) + ' 원');
        $('#mp2-f').text('-' + this.format(r.fee) + ' 원');   // 수수료 + 수수료 VAT 합계
        $('#mp2-t').text(this.format(r.deposit) + ' 원');
        this.checkPayForm();
    }

    checkPayForm() {
        const cardOk = this.isValidCardLen($('#mp2-ci-num').val().replace(/\D/g, '').length) && $('#mp2-ci-exp').val().length === 5;
        this.showDupGuard('mp2', this.payItems, $('#mp2-ci-num').val());   // 안내만 — 버튼은 잠그지 않는다
        $('#mp2-pay').toggleClass('dis', !($('#mp2-chk').is(':checked') && cardOk));
    }

    /* ----- 직접입력 결제의 카드 정보 화면 ----- */

    openCardInfo() {
        const raw = parseInt($('#m-price').val().replace(/[^0-9]/g, ''));
        const r = this.calcAmounts(raw, $('input[name="m-vat"]:checked').val());

        // amt 는 입력한 장비대금 그대로다 — 부가세 별도/포함 해석은 서버가 vat 값으로 한다
        this.manualItem = {
            wd: $('#m-date').val(),
            addr: this.manualAddress(),
            amt: raw,
            memo: $('#m-memo').val(),
        };
        $('#ci-amt').text(this.format(r.payTotal) + '원');
        $('#ci-num, #ci-exp').val('');
        this.renderBin('ci', null);
        $('#b-cardpay').addClass('dis');
        this.checkCardInfoForm();
        this.openPg('pg-cardinfo');
    }

    checkCardInfoForm() {
        const cardOk = this.isValidCardLen($('#ci-num').val().replace(/\D/g, '').length) && $('#ci-exp').val().length === 5;
        this.showDupGuard('ci', this.manualItem ? [this.manualItem] : [], $('#ci-num').val());   // 안내만 — 버튼은 잠그지 않는다
        $('#b-cardpay').toggleClass('dis', !cardOk);
    }

    /* ----- 중복결제(10분) 가드 ----- */

    /** 같은 작업(주소+금액) 또는 같은 카드로 10분 내 결제한 적이 있으면 안내를 띄운다 — 결제는 막지 않는다(취소 후 재결제 등) */
    showDupGuard(prefix, items, cardInput) {
        const now = Date.now();
        this.recentPays = this.recentPays.filter((r) => now - r.ts < CardPay.DUP_WINDOW_MS);

        const card = String(cardInput || '').replace(/\D/g, '');
        const workDup = items.some((i) => this.recentPays.some((r) => r.wkey === this.payKey(i)));
        const cardDup = this.isValidCardLen(card.length) && this.recentPays.some((r) => r.card === card);

        let msg = '';
        if (workDup) msg = '<b>10분 이내</b> 같은 작업을 결제한 내역이 있어요. 중복 결제가 아닌지 확인해 주세요.';
        else if (cardDup) msg = '이 카드로 <b>10분 이내</b> 결제한 내역이 있어요. 중복 결제가 아닌지 확인해 주세요.';

        $('#' + prefix + '-dup-tx').html(msg);
        $('#' + prefix + '-dup').toggleClass('on', !!msg);
        return workDup || cardDup;
    }

    payKey(i) { return 'a:' + (i.addr || '') + '|m:' + (i.amt || 0); }

    recordPay(items, card) {
        const ts = Date.now();
        items.forEach((i) => this.recentPays.push({ ts, wkey: this.payKey(i), card: card || '' }));
    }

    /* ----- 카드 OCR ----- */

    openCardScan(prefix) {
        this._cardScanTarget = prefix;
        // 촬영/갤러리 선택은 bs-card-scan 이 고른 from 값으로 addPhotoHere 를 부른다(위 bindPayPage 참고).
        // tag 'cardocr' — 앱은 태그를 그대로 흘려보낼 뿐 화이트리스트가 없어 새 태그를 자유롭게 쓸 수 있다.
        // 서버(cardOcr)가 이 태그로 업로드분만 인식하고 즉시 삭제한다(FileHost::deleteImg) — 카드사진은 남기지 않는다.
        this.openBS('bs-card-scan');
    }

    /**
     * 카드 촬영 → 서버 OCR(카드번호·유효기간만).
     * 앱 브릿지 업로드는 이미지서버를 한 번 거치지만, 인식 직후 서버가 바로 삭제한다
     * (CardPayController::cardOcr) — 인식값만 이 화면의 입력칸에 채운다.
     */
    scanCard(file) {
        const self = this;
        const prefix = this._cardScanTarget === 'ci' ? 'ci' : 'mp2';
        const numSel = prefix === 'ci' ? '#ci-num' : '#mp2-ci-num';
        const expSel = prefix === 'ci' ? '#ci-exp' : '#mp2-ci-exp';

        $j.Common.Util.loadingOverlay(true);
        $.ajax({
            type: 'POST', url: '/m/cardpay/card/ocr', data: { fileId: file.id, filePath: file.path },
            success(res) {
                $j.Common.Util.loadingOverlay(false);
                if (!res.result) { self.alertScanFailed(prefix, res.message); return; }

                $(numSel).val(self.groupCardNum(String(res.cardNum || '')));
                // 서버는 YYMM(2001=2020년 01월), 입력칸은 MM/YY 표기
                const yymm = String(res.cardExpire || '');
                if (yymm.length === 4) $(expSel).val(yymm.slice(2) + '/' + yymm.slice(0, 2));

                if (prefix === 'ci') self.checkCardInfoForm(); else self.checkPayForm();
                self.lookupBin(prefix);
            },
            error() {
                $j.Common.Util.loadingOverlay(false);
                self.alertScanFailed(prefix);
            },
        });
    }

    /**
     * 카드 촬영 인식 실패 안내 — 토스트 대신 확인창(alert)으로 분명히 띄우고, 두 가지 다음
     * 행동을 그 자리에서 바로 고르게 한다.
     *   · 직접 입력(취소버튼 자리) → 카드번호 입력칸에 커서를 둔다(이어서 타이핑)
     *   · 다시 촬영(확인버튼 자리) → 촬영 방법 시트를 바로 다시 연다(같은 대상 prefix)
     */
    alertScanFailed(prefix, message) {
        const numSel = prefix === 'ci' ? '#ci-num' : '#mp2-ci-num';
        $j.Ui.Dialog.open({
            title: '카드번호를 읽지 못했어요',
            desc: message || '다시 촬영하거나 직접 입력해 주세요.',
            cancel: '직접 입력',
            confirm: '다시 촬영',
            onCancel: () => $(numSel).trigger('focus'),
            onConfirm: () => this.openCardScan(prefix),
        });
    }

    /* ----- 카드빈 조회 (국내/해외 구분) ----- */

    /**
     * 직접입력 중 — 자리수가 다 차면 잠깐 멈췄을 때 조회한다. 자리수가 모자라면 결과줄을 바로 지운다.
     * (칸을 벗어나는 blur 는 lookupBin 을 바로 부른다)
     */
    scheduleBin(prefix) {
        this._binTimer = this._binTimer || {};
        clearTimeout(this._binTimer[prefix]);
        if (!this.isValidCardLen(this.cardDigits(prefix).length)) { this.renderBin(prefix, null); return; }
        this._binTimer[prefix] = setTimeout(() => this.lookupBin(prefix), CardPay.BIN_DEBOUNCE_MS);
    }

    /**
     * 카드번호 앞6자리로 이노페이 카드빈 조회 → 발급사명 또는 해외카드 안내를 입력칸 아래에 띄운다.
     * 이노페이 조회는 국내카드만 나와서, 안 나오면 해외카드로 본다(CardPaymentService::lookupBin).
     * 해외 수기결제 가능 여부는 이노페이 확인 전이라 안내만 하고 결제는 막지 않는다.
     *
     * ⚠️ 서버로는 앞6자리만 보낸다. 같은 BIN 은 다시 묻지 않는다(_binCache).
     */
    lookupBin(prefix) {
        const self = this;
        this._binTimer && clearTimeout(this._binTimer[prefix]);
        this._binCache = this._binCache || {};

        const digits = this.cardDigits(prefix);
        if (!this.isValidCardLen(digits.length)) { this.renderBin(prefix, null); return; }

        const bin = digits.slice(0, 6);
        // 캐시는 응답이 아니라 요청 자체 — 입력 멈춤(debounce)과 blur 가 같은 순간 불러도 이노페이로는 한 번만 간다
        if (!this._binCache[bin]) {
            this._binCache[bin] = $.ajax({ type: 'POST', url: '/m/cardpay/card/bin', data: { bin } })
                .then((res) => {
                    if (!res.result) return $.Deferred().reject();
                    return res;
                })
                .fail(() => { delete self._binCache[bin]; });   // 실패는 담아두지 않는다 — 다음에 다시 묻는다
        }
        this._binCache[bin].done((res) => {
            // 응답 오는 사이 번호를 고쳤으면 옛 결과를 그리지 않는다
            if (self.cardDigits(prefix).slice(0, 6) === bin) self.renderBin(prefix, res);
        });
    }

    cardDigits(prefix) {
        return String($(prefix === 'ci' ? '#ci-num' : '#mp2-ci-num').val() || '').replace(/\D/g, '');
    }

    /** 조회결과 한 줄 — 해외카드(조회에 없음)일 때만 오류문구(.fe)를 띄우고, 국내카드·판정불가는 비운다 */
    renderBin(prefix, res) {
        const foreign = !!res && res.kind === 'foreign';
        $(prefix === 'ci' ? '#ci-bin' : '#mp2-ci-bin').text(foreign ? '해외카드이거나 번호가 틀렸을 수 있어요.' : '');
    }

    /* ----- 결제 실행 ----- */

    /**
     * 오더 없는 결제(직접입력·관리자 모드) 1건의 일회용 키 — 서버가 주문번호 뒷자리로 써서 같은 결제가 두 번 나가는 걸 막는다
     * (CardPaymentService::payKeyin). 서버는 승인했는데 응답만 못 받은 경우, 다시 눌러도 같은 키가 나가 첫 결제 결과가 돌아온다.
     * 결제 내용(카드 말고 금액·주소 등)이 같으면 같은 키다 — 화면을 닫았다 다시 열어도. 내용이 바뀌면 새 키.
     * 서버가 끝난 답(승인·거절)을 주면 버린다(dropPayKey). 화면을 새로 받으면 키도 사라진다.
     */
    payKeyFor(data) {
        const rest = Object.assign({}, data);
        delete rest.cardNum; delete rest.cardExpire; delete rest.payKey;
        const sig = JSON.stringify(rest);
        if (!this._payKey || this._payKeySig !== sig) {
            const bytes = new Uint8Array(8);
            window.crypto.getRandomValues(bytes);
            this._payKey = Array.from(bytes, (x) => ('0' + x.toString(16)).slice(-2)).join('');
            this._payKeySig = sig;
        }
        return this._payKey;
    }

    /** 서버가 끝난 답을 줬으면 키를 버린다. 답을 못 받았거나(통신 오류) 결과 확인 중(retry)이면 그대로 둔다 */
    dropPayKey(res) {
        if (res && !res.retry) this._payKey = null;
    }

    /**
     * 결제 확인창 — 한도(MAX_PAY_TOTAL)를 넘으면 안내만 하고 멈춘다.
     * 오더 결제는 수금완료 시점을 함께 알린다: 작업완료 오더는 승인 즉시, 진행 중 오더는 작업완료로 바뀔 때(CardOrderService).
     */
    confirmPay(prefix) {
        const isManual = prefix === 'ci';
        const amt = isManual ? this.manualItem.amt : this.payItems.reduce((s, i) => s + (i.amt || 0), 0);
        const r = this.calcAmounts(amt, $(isManual ? 'input[name="m-vat"]:checked' : 'input[name="mp2-vat"]:checked').val());
        if (r.payTotal > CardPay.MAX_PAY_TOTAL) {
            $j.Common.Util.swalert('1회 결제는 2억 원까지입니다.\n나눠서 결제해 주세요.');
            return;
        }
        // 담긴 오더 상태(st = G5_ORDER_STATUS 이름)에 따라 수금완료 시점을 한 가지로 말한다 — 섞였을 때만 둘 다
        const done = this.payItems.filter((i) => i.st === '작업완료').length;
        const when = done === this.payItems.length ? '결제 승인 즉시 수금완료됩니다.'
            : (done === 0 ? '작업완료되면 수금완료됩니다.' : '작업완료 오더는 바로, 나머지는 작업완료되면 수금완료됩니다.');
        const msg = isManual
            ? '카드 결제를 진행하시겠습니까?'
            : '카드 결제를 진행하시겠습니까?\n' + when;
        $j.Common.Util.swalert(msg, () => this.pay(prefix), ['확인', '취소']);
    }

    /**
     * 카드 수기결제 — 서버가 이노페이 payApi 를 호출해 그 자리에서 승인까지 끝낸다(동기).
     * 금액·상품명·구매자는 서버가 오더에서 확정한다(앱이 보낸 금액을 신뢰하지 않는다).
     * ⚠️ 카드번호는 이 요청으로만 나가고 어디에도 저장하지 않는다.
     */
    pay(prefix) {
        const self = this;
        const isManual = prefix === 'ci';
        const $btn = $(isManual ? '#b-cardpay' : '#mp2-pay');
        if ($btn.data('busy')) return;

        const cardNum = $(isManual ? '#ci-num' : '#mp2-ci-num').val().replace(/\D/g, '');
        const exp = $(isManual ? '#ci-exp' : '#mp2-ci-exp').val().replace(/\D/g, '');   // MMYY
        const items = isManual ? [this.manualItem] : this.payItems;

        const data = {
            cardNum,
            cardExpire: exp.slice(2) + exp.slice(0, 2),   // MMYY → YYMM (이노페이 규격)
            vat: $(isManual ? 'input[name="m-vat"]:checked' : 'input[name="mp2-vat"]:checked').val(),
        };
        if (isManual) {
            data.direct = 1;
            data.workDate = this.manualItem.wd;
            data.addr = this.manualItem.addr;
            data.amt = this.manualItem.amt;
            data.memo = this.manualItem.memo || '';
            data.payKey = this.payKeyFor(data);   // 오더가 없어 서버가 선점으로 못 막는다 — 키로 막는다
        } else {
            data.ordernums = items.map((i) => i.on);
        }

        $btn.data('busy', 1).addClass('dis').html('<span class="spin" aria-hidden="true"></span>');
        let asked = 0;
        const send = () => $.ajax({
            type: 'POST', url: '/m/cardpay/pay', data,
            success(res) {
                // 같은 키의 첫 결제가 아직 진행 중 — 안내를 띄우지 않고 같은 요청으로 다시 물어, 결과가 나오면 그 화면을 보여준다
                if (res.wait && asked++ < CardPay.PAY_WAIT_TRIES) { setTimeout(send, CardPay.PAY_WAIT_MS); return; }
                $btn.data('busy', 0).text(isManual ? '카드 결제' : '결제하기');
                self.dropPayKey(res);
                if (!res.result) {
                    $btn.removeClass('dis');
                    $j.Common.Util.tsalert('error', res.message || '결제에 실패했습니다.');
                    // 이미 결제 중인 오더가 섞였거나 같은 키의 앞선 결제가 아직 결과 확인 중이다 — 결제 과정을 닫고 목록을 다시 받는다
                    // (이노페이 응답을 못 받은 결제는 서버가 그 자리에서 실패로 닫는다 — retry 가 아니라 그냥 실패라 바로 다시 누를 수 있다)
                    if (res.retry) {
                        self.closePayFlow();
                        self.loadSites();
                        self.hist.loaded = false;   // 내역 탭에 '결과 확인 중'으로 뜬다 — 다음에 열 때 다시 받는다
                    }
                    return;
                }
                self.recordPay(items, cardNum);
                self.afterPay(res.item, items);
            },
            error() {
                $btn.data('busy', 0).removeClass('dis').text(isManual ? '카드 결제' : '결제하기');
                $j.Common.Util.tsalert('error', '잠시 후 다시 시도해주세요');
            },
        });
        send();
    }

    /** 승인 성공 — 내역에 얹고 결제완료 화면으로. */
    afterPay(item, paidItems) {
        if (item && this.hist.loaded) {              // 내역을 아직 안 받았으면 탭을 열 때 같이 온다
            const group = this.HD.find((g) => g.date === item.date);
            if (group) group.items.unshift(item);
            else this.HD.unshift({ date: item.date, items: [item] });
        }
        if (item) this.doneItem = item;
        // 결제한 작업은 서버에서 수금 표시가 되어 결제 목록에서 빠진다 — 다시 받기 전에 화면에서도 뺀다
        const paidIds = (paidItems || []).map((i) => String(i.on));
        this.SD = this.SD.filter((s) => paidIds.indexOf(String(s.on)) < 0);

        this.fillDoneBox(item, paidItems);

        this.closePayFlow();
        this.renderSiteList();
        this.renderHistory();
        this.openPg('pg-done');
    }

    /** 결제 과정 화면들을 닫고 스택에서도 뺀다(뒤로가기가 결제 화면으로 되돌아가지 않게) — 고른 건도 비운다 */
    closePayFlow() {
        const closing = ['pg-multipay', 'pg-cardinfo', 'pg-manual'];
        $('#' + closing.join(', #')).removeClass('show');
        this._pgStack = this._pgStack.filter((id) => closing.indexOf(id) < 0);
        this.payItems = [];
        this.manualItem = null;
    }

    /** 결제완료 요약 박스 */
    fillDoneBox(item, paidItems) {
        const total = item ? (item.amt + (item.vat || 0)) : 0;
        let h = '';
        h += '<div class="pd-row"><span class="pdl">결제일시</span><span class="pdv">' + this.esc(item ? item.payDate : '-') + '</span></div>';
        h += '<div class="pd-row"><span class="pdl">카드사</span><span class="pdv">' + this.esc((item && item.cardCo) || '-') + '</span></div>';
        h += '<div class="pd-row"><span class="pdl">승인번호</span><span class="pdv">' + this.esc((item && item.approvalNo) || '-') + '</span></div>';

        if (paidItems.length > 1) {
            const works = paidItems.map((i) =>
                '<div class="pd-work">' + this.esc([i.dateLabel, i.time, i.eq].filter(Boolean).join(' ')) + '</div>'
            ).join('');
            h += '<div class="pd-workblock"><div class="pdw-title">결제 작업</div>' + works + '</div>';
            h += '<div class="pd-row"><span class="pdl">결제 건수</span><span class="pdv">총 ' + paidItems.length + '건</span></div>';
        } else {
            h += '<div class="pd-row"><span class="pdl">현장주소</span><span class="pdv">' + this.esc(item ? item.addr : '') + '</span></div>';
        }
        h += '<div class="pd-row pd-total"><span class="pdl">결제금액</span><span class="pdv">' + this.format(total) + '원</span></div>';
        $('#done-box').html(h);
    }

    /* ======================================================================
     *  직접입력
     * ==================================================================== */

    openManual() {
        $('#m-date, #m-addr, #m-price, #m-memo').val('');
        $('input[name="m-vat"][value="sep"]').prop('checked', true);
        $('#m-chk-a').prop('checked', false);
        $('#m-pb, #m-chk').hide();
        $('#b-mpay').addClass('dis');
        this.openPg('pg-manual');
    }

    calcManualVat() {
        const raw = ($('#m-price').val() || '').replace(/[^0-9]/g, '');
        if (!raw) return;
        const r = this.calcAmounts(parseInt(raw), $('input[name="m-vat"]:checked').val());
        $('#mpv-w').text(this.format(r.work) + ' 원');
        $('#mpv-v').text(this.format(r.vat) + ' 원');
        $('#mpv-total').text(this.format(r.payTotal) + ' 원');
        $('#mpv-f').text('-' + this.format(r.fee) + ' 원');   // 수수료 + 수수료 VAT 합계
        $('#mpv-t').text(this.format(r.deposit) + ' 원');
    }

    checkManualForm() {
        const date = $('#m-date').val();
        const addr = $('#m-addr').val();
        const rawPrice = ($('#m-price').val() || '').replace(/[^0-9]/g, '');

        if (rawPrice) { $('#m-pb, #m-chk').show(); this.calcManualVat(); }
        else { $('#m-pb, #m-chk').hide(); $('#m-chk-a').prop('checked', false); }

        $('#b-mpay').toggleClass('dis', !(date && rawPrice && addr && $('#m-chk-a').is(':checked')));
    }

    /**
     * 현장주소 선택 — 카카오맵(상세주소 없는 모드, detail='0').
     *
     * 지도는 iframe(/m/map)으로 뜨고, [적용] 을 누르면 부모 window 의 전역 callbackAddress(arr) 를
     * 호출한다(resources/views/mobile/pages/map/searchMap.blade.php). 상세주소는 받지 않는다.
     */
    searchAddress() {
        window.callbackAddress = (arr) => {
            $('#m-addr').val(arr.addr || '');
            this.checkManualForm();
        };
        $j.F.KakaoMap.open('', '0', $('#m-addr').val() || '');
    }

    /** 결제 품목명 = 지도에서 고른 현장주소 */
    manualAddress() {
        return ($('#m-addr').val() || '').trim();
    }

    /* ----- 작업일 선택기 ----- */

    /**
     * 직접입력·관리자 모드 작업일 공용 — 발주 작업날짜처럼 바텀시트 달력이고, 날짜를 누르면 바로 반영하고 닫는다.
     * @param {jQuery} $el 작업일 칸
     * @param {Function} onPick 칸에 값을 넣은 뒤 부른다 (폼 검사)
     */
    bindWorkDatePicker($el, onPick) {
        $el.on('click.workdate', () => this.openWorkDatePicker($el, onPick));
    }

    openWorkDatePicker($el, onPick) {
        if (typeof mobiscroll === 'undefined') return;
        const pick = (date) => {
            $el.val(dayjs(date).format('YYYY-MM-DD'));
            onPick();
            this.closeBS();
        };
        const value = $el.val();
        const $cal = $('#dp-cal');

        $cal.mobiscroll('destroy');
        this.openBS('bs-dp');
        $cal.mobiscroll().datepicker({
            controls: ['calendar'],
            display: 'inline',
            locale: mobiscroll.localeKo,
            defaultSelection: value ? dayjs(value).toDate() : null,
            buttons: [],
            onCellClick: (args) => { if (args.date) pick(args.date); },
        });
        // 머리의 '오늘' — 칸 클릭과 같게 반영한다 (내장 버튼은 onCellClick 을 안 태운다)
        $j.Common.Mobiscroll.onToday($('#bs-dp'), pick);
    }

    /* ======================================================================
     *  내역 / 상세 / 전표
     * ==================================================================== */

    /**
     * 내역 리스트 렌더 — 받은 묶음(HD) 그대로. 검색은 서버가 이미 걸렀다(loadHistory).
     * 구 페이앳에서 옮겨온 건(legacy)이 처음 나오는 자리 위에 '이전내역' 경계선을 한 줄 긋는다.
     */
    renderHistory() {
        const h = this.hist;
        let html = '';
        let count = 0;
        let lined = false;
        const joined = this.state === 'active';   // 이노페이 가맹 승인 상태

        this.HD.forEach((g) => {
            g.items.forEach((i, idx) => {
                if (i.legacy && !lined) {
                    // '이전내역' 경계선과 문의처 안내는 가맹 가입(승인) 뒤에만 — 그 전엔 전부 구 결제라 '이전'이라고 가를 새 내역이 없다
                    if (joined) html += '<div class="hist-legacy-line">이전내역</div><p class="hist-legacy-note">결제 문의 : 페이앳 1661-3480</p>';
                    lined = true;
                }
                if (idx === 0 || (i.legacy && lined && !g.items[idx - 1].legacy)) html += '<div class="dg-t">' + this.esc(g.date) + '</div>';
                html += this.histRow(i);
                count++;
            });
        });
        if (h.loading) html += '<div class="hist-more">불러오는 중…</div>';

        $('#hist-list').html(html).toggle(count > 0 || h.loading);
        $('#empty-hist').css('display', count === 0 && !h.loading ? 'flex' : 'none');
        $('#empty-hist .es-title').text(h.q ? '검색 결과가 없습니다' : '결제 내역이 없습니다');
    }

    /**
     * 결제자 뱃지 — 소유자 화면에서 소속자가 결제한 건에만 붙인다.
     * 소속자 화면은 전부 본인 건이라(서버가 mb_no 로 잘라 보낸다) 아무것도 안 붙는다.
     * 이름 대신 단축번호(payerVnum — 결제 탭 번호와 같은 기준)를 쓰고, 번호가 없으면 '-' 다(결제 탭 drv 와 같다).
     */
    payerBadge(i) {
        if (!this.isOwner || i.mine) return '';
        return '<span class="pr-num">' + this.esc(i.payerVnum || '-') + '</span>';   // 결제 탭 단축번호와 같은 모양
    }

    /** 내역 카드 1건 (묶음은 '묶음' 뱃지 + 대표 1건 외 N건) */
    histRow(i) {
        const total = i.amt + (i.vat || 0);
        const st = this.histStatus(i);
        const bundle = !!(i.works && i.works.length > 1);

        let head;
        let when = '';
        let addr = i.addr;
        if (bundle) {
            head = '<span class="hc-bundle">묶음</span>' + this.esc(i.works[0].eq) + ' 외 ' + (i.works.length - 1) + '건';
            addr = i.works[0].addr;
        } else if (i.direct) {
            head = '직접입력';
        } else {
            head = this.esc(i.eq);
            when = [i.time, i.type].filter(Boolean).join(' · ');
        }

        return '<div class="hc' + (i.x || i.failed ? ' off' : '') + '" data-id="' + i.id + '">'
            + '<div class="hc-r1"><span class="hc-head' + (i.direct ? ' direct' : '') + '">'
            + (i.orderCanceled ? '<span class="hc-oc">오더취소</span>' : '') + head
            + (when ? ' <span class="hc-when">' + this.esc(when) + '</span>' : '') + '</span>'
            + '<span class="hc-amt">' + this.format(total) + '원</span></div>'
            + '<div class="hc-r2">' + this.payerBadge(i) + '<span class="hc-addr">' + this.esc(addr) + '</span>'
            + '<span class="hc-st ' + st.cls + '">' + st.text + '</span></div></div>';
    }

    /** 내역에 결과 확인 중인 결제가 있는가 — 있으면 내역 탭이 들어올 때마다 다시 받는다 */
    hasChecking() {
        return this.HD.some((g) => g.items.some((i) => i.checking));
    }

    histStatus(i) {
        // 결과를 아직 모르는 결제 / 확인 중이다가 미승인으로 끝난 결제(당일까지) — CardPaymentService::historySql
        if (i.checking) return { cls: 'pending', text: '결과 확인 중' };
        if (i.failed) return { cls: 'canceled', text: '승인 안 됨' };
        // 이전내역(구 페이앳)은 정산 여부를 알 수 없어 승인건을 '결제완료'로 보인다(정산대기·정산완료 아님). 당일취소(td)는 서버가 안 켠다
        if (i.x) return { cls: 'canceled', text: '취소완료' };
        if (i.td) return { cls: 'cancelable', text: '취소가능' };
        if (i.legacy) return { cls: 'settled', text: '결제완료' };
        if (i.settled) return { cls: 'settled', text: '정산완료' };
        return { cls: 'pending', text: '정산대기' };
    }

    statusChip(i) {
        if (i.checking) return '<span class="status-chip" data-status="pending">결과 확인 중</span>';
        if (i.failed) return '<span class="status-chip" data-status="canceled">승인 안 됨</span>';
        if (i.x) return '<span class="status-chip" data-status="canceled">취소완료</span>';
        if (i.td) return '<span class="status-chip" data-status="cancelable">취소가능</span>';
        if (i.legacy) return '<span class="status-chip" data-status="settled">결제완료</span>';
        if (i.settled) return '<span class="status-chip" data-status="settled">정산완료</span>';
        return '<span class="status-chip" data-status="pending">정산대기</span>';
    }

    openDetailById(id) {
        let item = null;
        this.HD.forEach((g) => g.items.forEach((i) => { if (String(i.id) === String(id)) item = i; }));
        if (!item && this.admin) item = this.admin.payat.items.find((i) => String(i.id) === String(id)) || null;   // 관리자 모드 페이앳 내역
        if (!item) return;
        this.openDetail(item);       // 저장값으로 바로 그린다 — 지연 없이 즉시 진입
        if (!item.legacy) this.fetchLiveDetail(id, item);   // 이전내역은 이노페이 거래가 아니다
    }

    /**
     * InnoPay 거래조회(GET /m/cardpay/history/{id}) 병행 — 저장값(item)으로 이미 그려둔 화면에
     * 실제 카드사·승인번호 등이 있으면 덮어써 다시 그린다. 응답에 없거나 요청이 실패하면 그냥
     * 저장값 그대로 둔다(필수 아님, 조용히 무시).
     */
    fetchLiveDetail(id, item) {
        $.ajax({ url: '/m/cardpay/history/' + id, type: 'GET', dataType: 'json' }).done((res) => {
            const live = res && res.result ? res.live : null;
            if (!live || this.detItem !== item) return;   // 그새 다른 건을 열었으면 반영 안 함
            // fnName = 발급사(카드 발급은행). acquCardName 은 매입사라 다른 값이라 쓰면 안 된다
            // (예: 하나카드로 결제해도 매입처리가 외환 계약망을 타면 acquCardName="외환"으로 옴).
            if (live.card && live.card.fnName) item.cardCo = live.card.fnName;
            if (live.authNum) item.approvalNo = live.authNum;
            if (live.receiptUrl) item.receiptUrl = live.receiptUrl;
            this.openDetail(item);
        });
    }

    /** 결제 상세 */
    openDetail(item) {
        this.detItem = item;
        const total = item.amt + (item.vat || 0);
        const works = (item.works && item.works.length) ? item.works : null;
        const bundle = !!(works && works.length > 1);

        let h = '<div class="p20 pt20">';

        // 결제 작업 (단건·묶음 레이아웃 통일 / 종류는 칩으로 구분)
        h += '<div class="det-section"><div class="det-sec-head"><div class="det-section-title">결제 작업</div>'
            + (bundle ? '<span class="det-kind bundle">묶음 ' + works.length + '건</span>' : '') + '</div>';
        if (works) {
            works.forEach((w) => {
                h += this.detWorkCard(w.eq, [w.time, w.type].filter(Boolean).join(' · '), w.addr, w.amt + (w.vat || 0));
            });
        } else if (item.direct) {
            h += this.detWorkCard('직접입력', item.memo || item.workDate || '', item.addr, total);
        } else {
            h += this.detWorkCard(item.eq, [item.workDate, item.time, item.type].filter(Boolean).join(' · '), item.addr, total);
        }
        h += '</div>';

        // 결제 내역
        h += '<div class="det-section"><div class="det-section-title">결제 내역</div>';
        const open = item.checking || item.failed;   // 승인된 적 없는 결제 — 전표·정산·취소가 없다
        h += '<div class="det-row"><span class="dl">' + (open ? '요청일시' : '결제일시') + '</span><span class="dv">' + this.esc(item.payDate || '-') + '</span></div>';
        // 소유자 화면에서만 — 소속자가 받은 건인지 한눈에 보이게 한다(마스터 취소 전 확인용)
        if (this.isOwner && item.payerName) {
            h += '<div class="det-row"><span class="dl">결제자</span><span class="dv">' + this.esc(item.payerName) + (item.mine ? ' (본인)' : '') + '</span></div>';
        }
        if (item.cardCo) h += '<div class="det-row"><span class="dl">카드사</span><span class="dv">' + this.esc(item.cardCo) + '</span></div>';
        if (item.approvalNo) h += '<div class="det-row"><span class="dl">승인번호</span><span class="dv">' + this.esc(item.approvalNo) + '</span></div>';
        const amtCls = (item.x || open) ? 'highlight-black' : ((item.settled || item.legacy) ? 'highlight-dark' : 'highlight-blue');
        h += '<div class="det-row ' + amtCls + '"><span class="dl">' + (open ? '요청금액' : '결제금액') + ' (VAT 포함)</span><span class="dv">' + this.format(total) + ' 원</span></div></div>';

        // 결과를 아직 모르는 결제 / 미승인으로 끝난 결제 — 안내만 하고 끝낸다
        if (open) {
            h += '<div class="det-section"><div class="det-oc' + (item.checking ? ' info' : '') + '">'
                + (item.checking
                    ? '카드 승인 결과를 확인하고 있습니다. 10분 안에 이 내역에 결과가 반영됩니다. 그동안 같은 작업을 다시 결제하지 마세요.'
                    : '이 결제는 승인되지 않았습니다. 필요하면 다시 결제해 주세요.')
                + '</div></div></div>';
            $('#detail-body').html(h);
            $('#detail-footer').html('').hide();
            this.openPg('pg-detail');
            return;
        }

        // 취소 내역
        if (item.x) {
            h += '<div class="det-section"><div class="det-sec-head"><div class="det-section-title">취소 내역</div><span class="status-chip" data-status="canceled">취소완료</span></div>';
            h += '<div class="det-row"><span class="dl">취소일시</span><span class="dv">' + this.esc(item.cancelDate || '-') + '</span></div>';
            if (item.cancelReason) h += '<div class="det-row"><span class="dl">취소사유</span><span class="dv">' + this.esc(item.cancelReason) + '</span></div>';
            h += '<div class="det-row danger-lg"><span class="dl">취소금액</span><span class="dv">-' + this.format(total) + ' 원</span></div></div>';
        }

        // 결제는 살아 있는데 오더가 취소됐다 — 막지 않고 알리기만 한다. 취소 여부는 회원이 정한다
        if (item.orderCanceled) {
            h += '<div class="det-section"><div class="det-oc">이 결제에 묶인 오더가 취소되었습니다. 카드결제는 자동으로 취소되지 않으니 필요하면 결제를 취소해 주세요.'
                + (item.td ? '' : ' 취소 기한이 지난 결제는 고객센터에 문의해 주세요.') + '</div></div>';
        }

        // 이전내역(구 페이앳) — 페이앳의 실제 수수료·입금액은 우리 데이터에 없어(구 화면이 안내하던 고정 요율뿐)
        // 정산 내역이 실제와 맞지 않는다 — 정산 내역은 빼고 전표(옛 전표 보기·공유·저장)만 남긴다. 취소는 페이앳으로 요청한다(cancelLegacyPayment)
        if (item.legacy) {
            h += '<div class="det-section"><div class="det-row"><span class="dl">구분</span><span class="dv">이전내역 (구 카드결제)</span></div></div>';
        }

        // 정산 내역
        if (!item.x && !item.legacy) {
            h += '<div class="det-section"><div class="det-section-title">정산 내역</div>';
            if (item.settled) {
                h += '<div class="det-row"><span class="dl">정산상태</span><span class="dv v-green">정산완료</span></div>';
                h += this.feeRowsHtml(item);
                h += '<div class="det-row highlight-blue"><span class="dl">입금금액</span><span class="dv">' + this.format(item.settleAmt) + ' 원</span></div>';
                if (item.settledAt) h += '<div class="det-row"><span class="dl">정산일</span><span class="dv">' + this.esc(item.settledAt) + '</span></div>';
            } else {
                const chip = item.td
                    ? '<span class="status-chip" data-status="cancelable">취소가능</span>'
                    : '<span class="status-chip" data-status="not-cancelable">취소불가</span>';
                h += '<div class="det-row"><span class="dl">정산상태</span><span class="dv"><span class="settle-val"><span class="v-primary">정산대기</span>' + chip + '</span></span></div>';
                h += this.feeRowsHtml(item);
                h += '<div class="det-row highlight-dark"><span class="dl">예상 입금액</span><span class="dv">' + this.format(item.settleAmt) + ' 원</span></div>';
            }
            h += '</div>';
        }

        // 전표 버튼 (취소건은 결제/취소 전표 중 고르게 한다 — 이전내역은 옛 전표 한 장에 취소가 같이 찍혀 고를 게 없다)
        const sel = (item.x && !item.legacy) ? '-select' : '';
        h += '<div class="det-btns">'
            + '<button class="det-btn" id="det-receipt"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>전표보기</button>'
            + '<button class="det-btn" id="det-share' + sel + '"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>전표공유</button>'
            + '<button class="det-btn" id="det-download' + sel + '"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>전표저장</button>'
            + '</div>';

        h += '</div>';
        $('#detail-body').html(h);

        // 푸터 — 승인 후 취소 기한(CardPaymentService::CANCEL_WINDOW_HOURS) 안의 건만 취소 가능. 기한은 서버가 cancelUntil 로 준다
        let fh = '';
        if (item.legacy) fh = item.legacyCancel ? '<button class="btn btn-r" id="det-legacy-cancel-btn">결제 취소</button>' : '';
        else if (!item.x && item.td) fh = '<button class="btn btn-r" id="det-cancel-btn">결제 취소' + (item.cancelUntil ? ' (' + this.esc(item.cancelUntil) + '까지)' : '') + '</button>';
        else if (!item.x && !item.settled) fh = '<button class="btn btn-na">취소불가 (취소 기한 지남)</button>';
        $('#detail-footer').html(fh).toggle(!!fh);

        this.openPg('pg-detail');
    }

    detWorkCard(eq, sub, addr, amt) {
        return '<div class="det-work"><div class="det-work-t">' + this.esc(eq)
            + (sub ? '<span class="det-work-sub">' + this.esc(sub) + '</span>' : '') + '</div>'
            + '<div class="det-work-b"><span class="det-work-addr">' + this.esc(addr || '-') + '</span>'
            + '<span class="det-work-amt">' + this.format(amt) + '원</span></div></div>';
    }

    // 공유·저장 둘 다 같은 전표 이미지를 만들어야 해서 saveReceiptImage 로 합친다 —
    // navigator.share(웹 표준)는 앱 웹뷰에서 파일 공유를 지원하지 않는다(텍스트만 됨).
    // 카카오톡 등이 뜨는 진짜 공유는 다른 화면(ZoomPhoto·Gallery·Diary)과 같은
    // $j.F.App.shareFile() 브릿지를 써야 한다.
    shareReceipt(isShare, name) {
        if (this.detItem && this.detItem.legacy) {
            this.legacyReceiptCanvas(this.detItem).then((canvas) => this.deliverReceipt(canvas, name, isShare));
            return;
        }
        const isCancel = name === '취소전표';
        this.saveReceiptImage(this.detItem, isCancel, name, isShare);
    }

    /**
     * 전표를 이미지로 저장 — 화면엔 안 띄우고(pg-r 을 열지 않고도 바로) 캡처용 사본을 화면 밖에
     * 그려서 html2canvas 로 찍는다. 실제 전표(pg-r)와 같은 공용 전표($j.F.CardReceipt)를 그리므로
     * 스타일은 항상 화면과 같다.
     *
     * 캡처 결과는 deliverReceipt 가 앱에 넘긴다 — 공유는 data URL 을 아는 앱이면 그대로, 아니면(저장 포함) 업로드 후 URL 로.
     */
    saveReceiptImage(item, isCancel, name, isShare) {
        if (!item) return;
        const self = this;

        $j.Common.Util.loadingOverlay(true);
        this._ensureHtml2Canvas().then((html2canvas) => {
            // .pg 규칙(top·bottom:0)이 걸리면 사본 높이가 화면 높이로 고정돼 긴 전표가 잘린다 — 높이는 내용대로
            // 제목은 전표 안에 있다 — 저장본은 전표 한 장뿐(둘레 여백·테두리 없음)
            const $cap = $('<div class="pg" style="position:fixed;left:-9999px;top:0;right:auto;bottom:auto;height:auto;width:360px;background:var(--card-surface);display:block">'
                + '<div class="pgb">' + this.buildReceipt(item, isCancel) + '</div>'
                + '</div>').appendTo('#cardpay-app');   // 이 화면 CSS·색 토큰은 #cardpay-app 아래에만 걸린다 — body 에 그리면 글씨만 찍힌다

            return html2canvas($cap[0], { backgroundColor: getComputedStyle($cap[0]).backgroundColor || '#fff', scale: 2 })
                .then((canvas) => {
                    $cap.remove();
                    self.deliverReceipt(canvas, name, isShare);
                })
                .catch(() => { $cap.remove(); throw new Error('capture failed'); });
        }).catch(() => {
            $j.Common.Util.loadingOverlay(false);
            $j.Common.Util.tsalert('error', '이미지 생성에 실패했습니다.');
        });
    }

    /**
     * 구운 전표 이미지를 공유(isShare) 또는 저장한다.
     * 공유 — 앱이 data URL 공유를 알면(Android.shareDataSupported) 서버에 올리지 않고 그대로 넘긴다.
     *        전표가 서버에도 폰에도 남지 않는다(앱이 공유시트에서 돌아올 때 지운다).
     * 그 밖(구앱·웹·저장·앱 거절) — 예전처럼 서버에 잠깐 올린 주소로 넘긴다(uploadReceiptImage: 1회용 태그, 1일 뒤 정리).
     */
    deliverReceipt(canvas, name, isShare) {
        const A = $j.F.App;
        if (isShare && A.shareDataSupported()) {
            const img = this.receiptDataUrl(canvas);
            if (img && A.shareFile(img.url, name + '.' + img.ext)) { $j.Common.Util.loadingOverlay(false); return; }
        }
        canvas.toBlob((blob) => {
            if (!blob) { $j.Common.Util.loadingOverlay(false); $j.Common.Util.tsalert('error', '이미지 생성에 실패했습니다.'); return; }
            this.uploadReceiptImage(blob, name, isShare);
        }, 'image/png');
    }

    /**
     * 전표 data URL — 앱 shareFile 의 data 한도(base64 본문 2.8MB) 안으로. PNG 가 넘으면 JPEG(0.9)로, 그래도 넘으면 null(업로드 경로).
     * @returns {{url:string, ext:string}|null}
     */
    receiptDataUrl(canvas) {
        const fits = (u) => u.length - u.indexOf(',') - 1 <= CardPay.SHARE_DATA_MAX;
        const png = canvas.toDataURL('image/png');
        if (fits(png)) return { url: png, ext: 'png' };
        const jpg = canvas.toDataURL('image/jpeg', 0.9);
        return fits(jpg) ? { url: jpg, ext: 'jpg' } : null;
    }

    /** html2canvas 지연 로드 — 전표 이미지 저장 시에만 필요(public/js/vendor, 작업확인서 캡처와 공용) */
    _ensureHtml2Canvas() {
        if (window.html2canvas) return Promise.resolve(window.html2canvas);
        return new Promise((res, rej) => {
            const s = document.createElement('script');
            s.src = '/js/vendor/html2canvas.1.4.1.min.js';
            s.onload = () => res(window.html2canvas);
            s.onerror = () => rej(new Error('html2canvas 로드 실패'));
            document.head.appendChild(s);
        });
    }

    /**
     * (구앱·웹 경로) 캡처된 전표 이미지를 업로드하고, isShare 면 공유시트(shareFile) / 아니면 다운로드(downloadImage)로 넘긴다.
     *
     * ⚠️ 두 브릿지 다 "네이티브에 전달했는지"만 확인 가능하고 실제 완료 여부는 알 수 없다
     *    (AppBridge.js 주석: runAppBroker → boolean 은 수락 여부일 뿐). 그래서 다른 화면(Gallery.js)과
     *    같이 다운로드는 성공 토스트를 띄우지 않는다 — 안 띄웠는데 띄웠다고 하는 게 더 나쁘다.
     *    공유는 브릿지가 없는(구버전 앱) 경우만 확실히 알 수 있어 그 경우에만 안내한다.
     */
    uploadReceiptImage(blob, name, isShare) {
        const fd = new FormData();
        fd.append('file', blob, name + '.png');
        fd.append('tag', 'cardpay_receipt');

        $.ajax({
            type: 'POST', url: '/api/upload', data: fd, processData: false, contentType: false,
            success(res) {
                $j.Common.Util.loadingOverlay(false);
                if (!res || !res.id) { $j.Common.Util.tsalert('error', '저장에 실패했습니다.'); return; }
                // 원본(/i/) 주소 — 이미지 서버에는 '/d/'(download) 경로가 없어 404 가 난다
                const url = $j.Common.Util.getImageUrl(res.path, res.host);
                if (isShare) {
                    if (!$j.F.App.shareFile(url)) $j.Common.Util.tsalert('error', '앱을 업데이트해주세요.');
                } else {
                    $j.F.App.downloadImage(url);
                }
            },
            error() {
                $j.Common.Util.loadingOverlay(false);
                $j.Common.Util.tsalert('error', '잠시 후 다시 시도해주세요');
            },
        });
    }

    /**
     * 이전내역(구 Payat 결제) 전표 — 새 전표(buildReceipt)가 아니라 그때 쓰던 옛 전표 그대로.
     * 옛 전표 HTML(/m/card/{num}, PayatController@getReceipt)을 화면 밖에 그려 html2canvas 로 굽는다.
     * 옛 전표는 폭 460px 고정 배경이미지라 폰 폭에 맞추려고 이미지로 만들어 보여준다(구 Card.js 와 같은 방식).
     * 성공하면 canvas 를 넘기고 로딩은 호출부가 끈다. 실패는 여기서 알리고 끝낸다.
     */
    legacyReceiptCanvas(item) {
        const num = item.legacyNum;   // 옛 전표는 페이앳 번호로 연다 — g5_card_payment.info.payat.num
        $j.Common.Util.loadingOverlay(true);
        return new Promise((resolve) => {
            const fail = () => { $j.Common.Util.loadingOverlay(false); $j.Common.Util.tsalert('error', '전표를 불러오지 못했습니다.'); };
            $.get('/m/card/' + num).done((html) => {
                this._ensureHtml2Canvas().then((html2canvas) => {
                    const $cap = $('<div style="position:fixed;left:-9999px;top:0">' + html + '</div>').appendTo('body');
                    html2canvas($cap.find('#cardcond')[0], { backgroundColor: '#fff', scale: 2 })
                        .then((canvas) => { $cap.remove(); resolve(canvas); })
                        .catch(() => { $cap.remove(); fail(); });
                }).catch(fail);
            }).fail(fail);
        });
    }

    openLegacyReceipt(item) {
        this.legacyReceiptCanvas(item).then((canvas) => {
            $j.Common.Util.loadingOverlay(false);
            $('#r-tabs-area').html('');
            $('#r-body').html('<img class="r-legacy" src="' + canvas.toDataURL('image/png') + '" alt="매출전표">');
            this.setReceiptTitle(false, true);
            this.openPg('pg-r');
        });
    }

    /** 전표 페이지 (취소건은 결제/취소 전표 페이저) */
    openReceipt(isCancel, item) {
        const d = item || this.detItem;
        const self = this;
        if (d && d.legacy) { this.openLegacyReceipt(d); return; }

        if (isCancel) {
            $('#r-tabs-area').html('<div class="receipt-tabs"><div class="receipt-tab" data-rp="0">결제전표</div><div class="receipt-tab on" data-rp="1">취소전표</div></div>');
            $('#r-body').html('<div class="receipt-pager" id="r-pager">'
                + '<div class="receipt-page" data-page="0">' + this.buildReceipt(d, false) + '</div>'
                + '<div class="receipt-page" data-page="1">' + this.buildReceipt(d, true) + '</div></div>');
            this.setReceiptTitle(true);
            this.openPg('pg-r');
            setTimeout(() => $('#r-pager').scrollLeft($('#r-pager').width()), 50);

            $(document).off('click', '.receipt-tab').on('click', '.receipt-tab', function () {
                const idx = parseInt($(this).data('rp'));
                $('.receipt-tab').removeClass('on'); $(this).addClass('on');
                $('#r-pager').scrollLeft(idx * $('#r-pager').width());
                self.setReceiptTitle(idx === 1);
            });
            $('#r-pager').off('scroll').on('scroll', function () {
                const idx = Math.round($(this).scrollLeft() / $(this).width());
                $('.receipt-tab').removeClass('on').filter('[data-rp="' + idx + '"]').addClass('on');
                self.setReceiptTitle(idx === 1);
            });
            return;
        }

        $('#r-tabs-area').html('');
        $('#r-body').html(this.buildReceipt(d, false));
        this.setReceiptTitle(false);
        this.openPg('pg-r');
    }

    /** 머리줄 제목 — 새 전표는 종이 안에 제목이 있어 비운다. 구 전표(이미지)만 여기 제목을 쓴다 */
    setReceiptTitle(isCancel, legacy) {
        $('#r-title').text(legacy ? '신용카드 매출전표' : '').toggleClass('is-cancel', false);
        $('#pg-r').toggleClass('r-float', !legacy);   // 새 전표는 닫기만 띄우고 전표 전체를 스크롤한다
    }

    /** 매출/취소 전표 마크업 — 공용 전표($j.F.CardReceipt, 관리자와 같은 것)에 이 가맹점 정보(RD)를 실어 그린다 */
    buildReceipt(src, isCancel) {
        return $j.F.CardReceipt.build(src || {}, { cancel: !!isCancel, merchant: this.RD || {} });
    }

    /* ======================================================================
     *  결제 취소
     * ==================================================================== */

    openCancelForm() {
        $('#cf-reason-sel').val('');
        $('#cf-reason-etc').val('').hide();
        $('#cf-card').val((this.detItem && this.detItem.cardCo) || '-');
        $('#cf-exp, #cf-pw').val('').removeClass('err');
        $('#e-pw, #e-exp').hide();
        $('#b-cfs').addClass('dis');
        this.openPg('pg-cf');
    }

    checkCancelForm() {
        const sel = $('#cf-reason-sel').val();
        const reason = sel === '기타' ? $('#cf-reason-etc').val() : sel;
        $('#b-cfs').toggleClass('dis', !(reason && $('#cf-exp').val().length === 5 && $('#cf-pw').val()));
    }

    openCancelModal() {
        $j.Ui.Dialog.open({
            tone: 'danger',
            title: '결제를 취소할까요?',
            desc: '취소 후에는 되돌릴 수 없어요.',
            cancel: '아니요',
            confirm: '결제 취소',
        }).then((ok) => { if (ok) this.cancelPayment(); });
    }

    /** 이전내역(구 페이앳) 결제 취소 — 페이앳에 요청하고, 내역의 취소 표시는 페이앳 콜백이 처리되면 바뀐다(mirrorPayat) */
    cancelLegacyPayment($btn) {
        const num = this.detItem && this.detItem.legacyNum;
        if (!num || $btn.data('busy')) return;
        $j.Ui.Dialog.open({
            tone: 'danger',
            title: '결제를 취소할까요?',
            desc: '취소 후에는 되돌릴 수 없어요.',
            cancel: '아니요',
            confirm: '결제 취소',
        }).then((ok) => {
            if (!ok) return;
            $btn.data('busy', 1).addClass('dis');
            const fail = () => {
                $btn.data('busy', 0).removeClass('dis');
                $j.Common.Util.tsalert('error', '취소 요청에 실패했어요. 잠시 후 다시 시도해 주세요.');
            };
            $.ajax({ type: 'DELETE', url: '/m/ajax/card/' + num, dataType: 'json' })
                .done((res) => {
                    if (!res || !res.result) { fail(); return; }
                    $j.Common.Util.swalert('취소를 요청했어요.\n처리되면 내역에 취소로 표시돼요.');
                    this.closePg('pg-detail');
                    if ($('#pg-admin-payat').hasClass('show')) this.loadAdminPayat(true);   // 관리자 모드 페이앳 내역에서 취소
                    else this.loadHistory(true);
                })
                .fail(fail);
        });
    }

    cancelReason() {
        const sel = $('#cf-reason-sel').val();
        return sel === '기타' ? $('#cf-reason-etc').val() : sel;
    }

    /**
     * 결제 취소 실행 — 이노페이 cancelApi 연동(승인 후 취소 기한 안의 건만, 전액취소).
     * 비밀번호·카드 유효기간은 이 요청에 실어 서버가 대조한다 — 틀리면 field('pw'|'exp')로 그 칸에 표시한다.
     */
    cancelPayment() {
        const self = this;
        const item = this.detItem;
        if (!item) return;

        const $btn = $('#b-cfs');
        if ($btn.data('busy')) return;
        $btn.data('busy', 1).addClass('dis').html('<span class="spin" aria-hidden="true"></span>');

        $.ajax({
            type: 'POST',
            url: '/m/cardpay/history/' + item.id + '/cancel',
            data: {
                reason: this.cancelReason(),
                password: $('#cf-pw').val(),
                cardExpire: (() => { const e = $('#cf-exp').val().replace(/\D/g, ''); return e.slice(2) + e.slice(0, 2); })(),   // MMYY → YYMM (저장값과 같은 형식)
            },
            success(res) {
                $btn.data('busy', 0).text('결제 취소');
                if (!res.result) {
                    self.checkCancelForm();
                    $j.Common.Util.tsalert('error', res.message || '취소에 실패했습니다.');
                    return;
                }

                // 취소폼·상세는 닫고 스택에서 뺀다(뒤로가기가 취소폼으로 되돌아가지 않게)
                const closing = ['pg-cf', 'pg-detail'];
                $('#' + closing.join(', #')).removeClass('show');
                self._pgStack = self._pgStack.filter((id) => closing.indexOf(id) < 0);

                item.x = true;
                item.td = false;
                item.cancelReason = self.cancelReason();
                const now = new Date();
                item.cancelDate = self.ymd(now) + ' ' + String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0');

                $('#cd-date').text(item.cancelDate);
                $('#cd-canceler').text((typeof MEMBER !== 'undefined' && MEMBER.mem_name) || '-');
                $('#cd-reason').text(item.cancelReason || '-');
                $('#cd-addr').text(item.addr);
                $('#cd-amt').text('-' + self.format(item.amt + (item.vat || 0)) + '원');

                self.renderHistory();
                self.openPg('pg-cancel-done');
            },
            // 거절은 422 로 온다(CardPayController::cancel) — 비밀번호·유효기간 불일치면 그 칸에 표시한다
            error(xhr) {
                $btn.data('busy', 0).text('결제 취소');
                const res = (xhr && xhr.responseJSON) || {};
                if (res.field === 'pw' || res.field === 'exp') {
                    $('#cf-' + res.field).addClass('err');
                    $('#e-' + res.field).text(res.message).show();
                } else {
                    $j.Common.Util.tsalert('error', res.message || '잠시 후 다시 시도해주세요');
                }
                self.checkCancelForm();
            },
        });
    }

    /* ======================================================================
     *  가맹점 등록 — 서류 등록
     * ==================================================================== */

    openRegDocs() {
        // 가맹 신청은 소유자 본인만 — 소속 기사가 서류·폼을 끝까지 채운 뒤 제출에서 거절당하지 않게 여기서 막는다
        if (!this.canRegister) {
            $j.Ui.Dialog.alert('소유자만 신청할 수 있어요', {
                desc: '카드 가맹 신청은 소유자(사장님) 본인만 할 수 있어요. 소유자에게 가입을 요청해 주세요. 소유자가 가입하면 기사님도 그 가맹점으로 카드결제를 받을 수 있어요.',
            });
            return;
        }
        this.setBizType($('input[name="biztype"]:checked').val() || 'personal');
        this.openPg('pg-reg-docs');
    }

    /** 개인/법인에 따라 필요 서류와 가입폼 단계가 갈린다 */
    setBizType(v) {
        const corp = (v === 'corp');
        // 서류 버튼은 flex 칸이라 toggle() 대신 display 를 직접 준다 (법인 서류는 처음엔 data-init-off 로 숨김)
        $('#pg-reg-docs .doc-up[data-only="personal"]').css('display', corp ? 'none' : 'flex');
        $('#pg-reg-docs .doc-up[data-only="corp"]').css('display', corp ? 'flex' : 'none');
        $('#corp-doc-hint').toggle(corp);
        $('#corpno-fg').toggle(corp);
        $('#rf-type-view').val(corp ? '법인사업자' : '개인사업자');
        this.checkRegDocs();
    }

    isCorp() { return $('input[name="biztype"]:checked').val() === 'corp'; }

    /** 지금 사업체 구분에서 제출하는 서류 — 개인/법인 공통(biz·bank) + 그 구분의 서류 */
    activeDocKeys() {
        return ['biz', 'bank'].concat(this.isCorp() ? ['corpReg', 'shareholders'] : ['idFront', 'idBack']);
    }

    /**
     * 서류 한 건에 받는 사진 장수 — 여러 장은 MULTI_PAGE_DOCS 만, 나머지는 한 장.
     * PDF 선택 방식(DOC_PDF_PICK)이면 multiDoc 이 1 이라 사진은 전부 한 장 — 여러 장은 PDF 로 받는다.
     */
    docLimit(key) {
        return CardPay.MULTI_PAGE_DOCS.indexOf(key) > -1 ? (this.multiDoc || 1) : 1;
    }

    /** 필수 서류 + 필수 약관(약관 표에 있는 것만 화면에 나온다)이 모두 채워져야 다음 단계 */
    checkRegDocs() {
        const d = this.regDocs;
        const docsOk = d.biz && d.bank && (this.isCorp() ? (d.corpReg && d.shareholders) : (d.idFront && d.idBack));
        const agreeOk = $('.terms-row[data-required="1"] input').toArray().every((el) => el.checked);
        $('#b-regdocs-next').toggleClass('dis', !(docsOk && agreeOk));
    }

    /** 여러 장을 받는 서류인가(법인 등기부등본·주주명부) — 등록 방법 시트의 PDF 버튼·갤러리 여러 장 묶기가 이걸 본다 */
    isMultiPageDoc(key) {
        return CardPay.DOC_PDF_PICK && CardPay.MULTI_PAGE_DOCS.indexOf(key) > -1;
    }

    /** @param {boolean} [append] [추가 등록] — 고른 사진을 지금 서류 뒤에 붙인다(addDocPhotos) */
    openUploadSheet(key, append = false) {
        this.uploadKey = key;
        this.uploadAppend = !!append;
        // 한 장/여러 장 제목과 PDF 버튼 — 여러 장 서류일 때만. 한 장 서류는 사진 촬영·갤러리만
        $('#bs-upload [data-pdf-only]').prop('hidden', !this.isMultiPageDoc(key));
        this.openBS('bs-upload');
    }

    /**
     * 서류 촬영/선택 결과 처리 — 6종 전부 네이티브 브릿지(카드 OCR과 같은 방식, 위 CARD_DOC_TAG 주석).
     *
     * 사업자등록증·통장사본은 여기서 바로 인식하지 않는다 — 미리보기만 띄우고 참조를 들고 있다가,
     * '다음'(서류 등록 화면 하단)을 눌러 다음 화면(가입폼)으로 넘어가는 시점에 한꺼번에 인식한다
     * (runPendingOcr). 신분증·등기부등본·주주명부는 인식 대상이 아니라 등록 여부만 표시한다.
     *
     * 브릿지가 우리 이미지서버에 이미 올려준 파일이라 raw File이 아니라 {fileId, filePath} 참조만
     * regFiles 에 들고 있다가, submitRegister 때 서버가 그 참조로 원본을 읽어 이노페이에 전달한다.
     * 전달에 성공하면 바로 지우고, 끝나지 않으면 올린 지 24시간에 지운다(CARD_DOC_TAG). 5.5MB 초과 여부는
     * (raw File처럼 여기서 바로 못 재서) 제출(submitRegister) 시점에 서버가 g5_file.filesize 로 판정한다.
     */
    applyBridgeDoc(file) {
        const key = this.uploadKey;
        if (!key || !file || !file.id) return;

        this.discardDocFiles(key);   // 교체 — 이전 촬영본(뒤쪽 사진 포함)은 바로 지운다

        this.docPreview[key] = $j.Common.Util.getImageUrl(file.path, file.host);
        const ref = { fileId: file.id, filePath: file.path };
        // PDF(from:'F') — 앱 콜백 type 'F'. 이미지서버가 이미지 아닌 파일을 .txt 로 저장해 주소로는 못 가른다
        // name — 앱이 준 원래 파일명. 화면 확인용으로만 쓴다(제출·DB 에는 안 실음). 그 전 앱은 안 줘서 빈값
        if (file.type === 'F') { ref.pdf = 1; ref.name = String(file.name || ''); }
        // pages — 앱이 묶은 PDF(need.pdf)의 쪽수. 화면 표시용(docPages). [추가 등록](merged)은 서버 묶기 경로라 더 켜지 않는다
        if (file.pages) ref.pages = file.pages;
        this.regFiles[key] = ref;   // 가입신청 제출까지 보관

        if (key !== 'biz' && key !== 'bank') { this.markDocDone(key); this.saveRegDraft(); return; }

        this.clearDocFields(key);   // 새 사진 — 이전 인식값(또는 회사정보 폴백) 흔적을 지우고 새로 시작
        this.pendingFile[key] = ref;   // 인식은 '다음' 누를 때(runPendingOcr)
        this.markDocDone(key);
        this.saveRegDraft();
    }

    /**
     * 서류 뒤쪽 사진을 더한다(여러 장 선택의 2장째부터 · [추가 등록]). 아직 묶지 않고 regFiles[key].extra 에 들고 있다가
     * '다음'에서 mergeDocPhotos 가 PDF 1개로 묶는다. 한도(multiDoc)를 넘는 장은 받지 않고 바로 지운다.
     * 사업자등록증·통장사본은 사진이 바뀌었으니 '다음'에서 다시 인식한다(pendingFile).
     */
    addDocPhotos(key, files) {
        const ref = this.regFiles[key];
        if (!ref || !files.length) return;
        ref.extra = ref.extra || [];
        files.forEach((f) => {
            const p = { fileId: f.id, filePath: f.path, url: $j.Common.Util.getImageUrl(f.path, f.host) };
            if (this.docPages(key) < this.docLimit(key)) ref.extra.push(p);
            else this.discardBridgeFile(p);
        });
        if (key === 'biz' || key === 'bank') { this.clearDocFields(key); this.pendingFile[key] = ref; }
        this.markDocDone(key);
        this.saveRegDraft();
    }

    /**
     * '다음' — 뒤쪽 사진(extra)이 있는 서류를 서버가 PDF 1개로 묶는다(docMerge). 첫 장이 사진이면 [첫 장 + 뒤쪽],
     * 앞서 묶은 PDF 면 그 PDF 를 base 로 뒤에 붙인다. 묶은 사진·base 는 서버가 지운다.
     * 한 장짜리 서류는 사진 그대로 보낸다. 하나라도 실패하면 false — 그 서류만 비우고 다시 등록하게 한다.
     * @returns {Promise<boolean>}
     */
    mergeDocPhotos() {
        // 지금 사업체 구분의 서류만 — 법인으로 올렸다가 개인으로 바꾼 법인 서류는 제출하지 않으니 묶지도 않는다
        const keys = this.activeDocKeys().filter((k) => this.regFiles[k] && (this.regFiles[k].extra || []).length);
        return keys.reduce((chain, key) => chain.then((ok) => ok && this.mergeDocKey(key)), Promise.resolve(true));
    }

    mergeDocKey(key) {
        const self = this;
        const ref = this.regFiles[key];
        const photos = (ref.merged ? [] : [ref]).concat(ref.extra);
        const fd = new FormData();
        photos.forEach((f) => { fd.append('fileIds[]', f.fileId); fd.append('filePaths[]', f.filePath); });
        if (ref.merged) { fd.append('baseId', ref.fileId); fd.append('basePath', ref.filePath); }

        return new Promise((resolve) => {
            $.ajax({
                type: 'POST', url: '/m/cardpay/merchant/doc/merge', data: fd, processData: false, contentType: false,
                success(res) {
                    if (!res.result) {
                        $j.Common.Util.tsalert('error', (CardPay.DOC_NAMES[key] || '서류') + ': ' + (res.message || '다시 등록해주세요'));
                        // 서버가 새 사진은 이미 지웠다. 앞서 묶은 PDF(base)는 남아 있으니 그건 그대로 두고, 아니면 서류를 비운다
                        if (ref.merged) { ref.extra = []; self.syncDocThumb(key); } else self.clearDocSlot(key);
                        self.checkRegDocs();
                        self.saveRegDraft();
                        resolve(false);
                        return;
                    }
                    const f = res.file;
                    const pdf = { fileId: f.id, filePath: f.path, pdf: 1, merged: 1, pages: f.pages,
                        name: (CardPay.DOC_NAMES[key] || '서류') + '_' + f.pages + '장.pdf' };
                    self.regFiles[key] = pdf;
                    self.docPreview[key] = $j.Common.Util.getImageUrl(f.path, f.host);
                    if (self.pendingFile[key]) self.pendingFile[key] = pdf;
                    self.syncDocThumb(key);
                    self.saveRegDraft();
                    resolve(true);
                },
                error() { $j.Common.Util.tsalert('error', '잠시 후 다시 시도해주세요'); resolve(false); },
            });
        });
    }

    /**
     * 사진을 다시 고를 때(교체) 그 서류의 인풋을 비운다 — 이전 사진의 인식값이나 회사정보 폴백이
     * 새 사진 뒤에 그대로 남아있으면 안 되기 때문(사진이 최신 진실이다). runPendingOcr 이 이 빈칸을
     * 다시 채운다(성공하면 OCR 값, 실패하면 빈칸 — 새 사진은 회사정보로 채우지 않는다).
     */
    clearDocFields(key) {
        delete this.docFields[key];
        if (key === 'biz') {
            $('#rf-biz, #rf-bizno, #rf-uptae, #rf-upjong, #rf-ceo-name').val('');
        } else if (key === 'bank') {
            $('#rf-bank').val('').trigger('change');
            $('#rf-acct, #rf-holder').val('');
        }
    }

    /**
     * '다음' 누를 때 서류를 인식한다 — 이번 세션에 새로 찍은 사진이 있으면 그 파일로, 없으면(이미
     * 등록된 서류를 그대로 둔 경우) 서버가 저장된 이미지로 대신 인식한다(docOcr 컨트롤러가 분기).
     * 이 서류 자체가 없으면(등록 안 됨) 할 게 없어 즉시 resolve. 실패해도 reject 하지 않는다
     * (다음 화면 진입을 막지 않는다).
     *
     * 사진이 안 바뀌었고(this.pendingFile 없음) 회사정보(g5_company)가 이 서류의 항목을 전부 갖고
     * 있으면 재인식 없이 그 값을 그대로 쓴다(hasCompanyInfo) — 매번 CLOVA OCR 을 다시 태울 필요가
     * 없다. 항목이 하나라도 비어 있으면(예: 업태·업종이 옛 데이터엔 없는 경우가 흔함) 채울 기회를
     * 줘야 하므로 평소대로 인식한다.
     *
     * 회사정보(g5_company) 폴백 여부는 "새 사진이었는가"로 가른다 — 새로 올린 사진의 인식이 실패했으면
     * 그 사진이 최신 진실인데 옛 회사정보를 슬쩍 채우면 안 되므로 빈칸으로 둔다. 반대로 기존에 등록된
     * 이미지를 다시 인식했는데 실패했으면(글자가 흐릿해졌다거나) 폴백으로 회사정보를 채운다.
     */
    runPendingOcr(key) {
        const file = this.pendingFile[key];
        if (!file && !this.regDocs[key]) return Promise.resolve();   // 이 서류 자체가 없다

        if (!file && this.hasCompanyInfo(key)) {
            this.applyCompanyFallback(key);
            return Promise.resolve();   // 사진 그대로 + 정보 전부 있음 → 재인식 생략
        }

        const self = this;
        const fd = new FormData();
        if (file) { fd.append(key + 'FileId', file.fileId); fd.append(key + 'FilePath', file.filePath); }
        if (file && file.pdf) fd.append(key + 'Pdf', '1');   // OCR 형식 — 주소로는 PDF 인지 모른다
        fd.append('doc', key);

        this.ocrResult[key] = {};   // 못 읽은 것으로 시작 — 성공하면 읽은 값으로 바뀐다
        this.ocrWait(true);

        return new Promise((resolve) => {
            $.ajax({
                type: 'POST', url: '/m/cardpay/merchant/doc/ocr', data: fd, processData: false, contentType: false,
                success(res) {
                    if (!res.result) {
                        // 여기서는 알림을 띄우지 않는다 — 다음 화면이 열린 뒤 읽은 결과 알리기(ocrReport)가 한꺼번에 말한다
                        self.devOcrLog(key, false, [res.message || '인식 실패']);
                        if (!file) self.applyCompanyFallback(key);   // 새 사진이 아닐 때만 회사정보로 폴백
                        resolve();
                        return;
                    }

                    delete self.pendingFile[key];   // 인식 성공 — 이 사진은 끝났다
                    self.docFields[key] = res.fields;
                    self.ocrResult[key] = res.fields || {};
                    self.docRaw[key] = res.raw || null;
                    self.applyOcrToForm(res.fields);
                    self.devOcrLog(key, true, Object.keys(res.fields || {}).map((k) => k + '=' + res.fields[k]));
                    resolve();
                },
                error() {
                    delete self.ocrResult[key];   // 사진을 못 읽은 게 아니라 통신이 끊겼다 — 읽은 결과 알리기(ocrReport)는 이 서류를 말하지 않는다
                    $j.Common.Util.tsalert('error', '잠시 후 다시 시도해주세요');
                    self.devOcrLog(key, false, ['네트워크 오류']);
                    if (!file) self.applyCompanyFallback(key);   // 새 사진이 아닐 때만 회사정보로 폴백
                    resolve();
                },
            }).always(() => self.saveRegDraft());
            // 실패하면 pendingFile 을 남긴다 — 지우면 다음 '다음'에서 새 사진이 아닌 것으로 보고 회사정보로 채우거나
            // 저장된 옛 사진으로 다시 인식한다. 남겨 두면 다시 눌렀을 때 바뀐 사진으로 다시 인식하고, 실패해도 빈칸 그대로다
        });
    }

    /**
     * 기다리는 동안 — 흐린 막 + 돌기만으로는 멈춘 것인지 오류인지 모른다. 무엇을 하는 중인지 카드로 또렷하게 말한다.
     * 카드 하나를 서류 읽기(ocrWait)·가입 신청(submitRegister)이 같이 쓰고, 그림만 다르다 —
     *   scan  : 서류 위를 줄이 오르내린다 (읽는 중)
     *   write : 종이 위에 펜이 줄을 끄적인다 (신청서를 쓰는 중)
     * 다시 부르면 문구만 바꿔 그린다. title 이 없으면 끈다.
     * 페이지 토큰(#cardpay-app 스코프)을 타야 해서 body 가 아니라 #cardpay-app 에 붙인다.
     */
    waitCard(title, desc, kind = 'scan') {
        $('#cp-wait').remove();
        if (!title) return;
        $j.Common.Util.loadingOverlay(false);   // 카드가 대신 말한다 — 돌기 겹쳐 띄우지 않는다
        const ic = kind === 'write'
            ? `<span class="cp-wait-ic is-write" aria-hidden="true"><svg viewBox="0 0 56 64" fill="none" stroke-linecap="round">
                <path class="cp-wait-ink i1" pathLength="1" d="M10 22q4.25-3 8.5 0t8.5 0t8.5 0t8.5 0"/>
                <path class="cp-wait-ink i2" pathLength="1" d="M10 33q4.25-3 8.5 0t8.5 0t8.5 0t8.5 0"/>
                <path class="cp-wait-ink i3" pathLength="1" d="M10 44q4-3 8 0t8 0t8 0"/>
                <g class="cp-wait-pen"><g transform="rotate(-45)"><path class="tip" d="M0 0L6-2.6V2.6z"/><rect class="body" x="6" y="-2.6" width="17" height="5.2" rx="1.2"/></g></g>
            </svg></span>`
            : `<span class="cp-wait-ic" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5M9 13h6M9 17h6"/></svg><span class="cp-wait-scan"></span></span>`;
        $('#cardpay-app').append(`<div id="cp-wait" class="cp-wait" role="status" aria-live="polite"><div class="cp-wait-card">
            ${ic}
            <b>${title}</b>
            <span>${desc}</span>
        </div></div>`);
    }

    /** 서류 읽는 중 — 두 서류를 같이 읽으면 두 번 불려 이름을 다시 그린다. 끄는 것은 '다음' 핸들러가 둘 다 끝난 뒤 한 번 */
    ocrWait(on) {
        if (!on) { this.waitCard(); return; }
        const names = Object.keys(this.ocrResult).map((k) => CardPay.DOC_NAMES[k]).join(' · ');
        this.waitCard('사진에서 글자를 읽고 있어요', `${names}의 내용을 찾아<br>칸을 채워 드릴게요.<br>보통 몇 초면 끝나요`);
    }

    /**
     * 읽은 결과 알리기 — 가입폼이 열린 뒤 그 위에 띄운다. 이번 '다음'에서 사진을 읽은 서류만 말한다
     * (사진 그대로 + 회사정보가 다 있어 읽기를 건너뛴 서류는 말하지 않는다).
     * 다 읽었으면 토스트 한 줄. 못 읽은 칸이 있으면 확인창 — 못 읽은 칸에는 **예전 값이 그대로 남아 있을 수 있어**
     * 말없이 두면 새 통장을 넣었는데 옛 계좌가 그대로 보이는 것을 회원이 새로 읽은 값으로 안다.
     */
    ocrReport() {
        const keys = Object.keys(this.ocrResult);
        if (!keys.length) return;
        const rows = [];
        let missed = 0;
        keys.forEach((key) => {
            const got = this.ocrResult[key];
            CardPay.OCR_FIELDS[key].forEach(([k, label, sel]) => {
                if (got[k]) { rows.push({ k: label, v: '읽었어요' }); return; }
                missed++;
                rows.push({ k: label, v: $(sel).val() ? '못 읽음 — 예전 값 그대로' : '못 읽음 — 비어 있음', tone: 'danger' });
            });
        });
        const readAny = rows.length > missed;
        this.ocrResult = {};

        if (!missed) {
            $j.Ui.Toast.show('사진에서 글자를 읽어 칸을 채웠어요. 맞는지 확인해 주세요', { type: 'success' });
            return;
        }
        $j.Ui.Dialog.open({
            tone: 'warn', align: 'left',
            title: readAny ? '사진에서 일부만 읽었어요' : '사진에서 값을 읽지 못했어요',
            desc: '못 읽은 칸은 직접 적어 주세요. 예전 값이 남아 있으면 새 서류와 맞는지 꼭 확인하고 고쳐 주세요.',
            rows,
            hint: '글자가 가려지거나 흐리면 못 읽어요. 밝은 곳에서 서류 전체가 보이게 다시 찍으면 잘 읽혀요.',
            confirm: '확인',
        });
    }

    /**
     * [개발서버 전용] OCR 성공/실패를 화면 상단에 투명 배너로 보여준다 — $j.Ui.DebugLog 재사용
     * (좌상단 스택, 몇 초 뒤 자동 소멸, 탭하면 전체 제거). 실서버는 G5.isDebug 가 꺼져 있어 no-op.
     */
    devOcrLog(key, ok, lines) {
        if (typeof G5 === 'undefined' || !G5 || !G5.isDebug) return;
        $j.Ui.DebugLog.enabled = true;
        const name = key === 'biz' ? '사업자등록증' : '통장사본';
        $j.Ui.DebugLog.push('[OCR] ' + name + ' ' + (ok ? '성공' : '실패'), lines || [], { kind: ok ? 'recv' : 'error', timeout: 0 });
    }

    /** OCR 로 읽힌 값만 가입폼에 덮어쓴다(빈 값으로 기존 자동채움을 지우지 않는다) */
    applyOcrToForm(f) {
        const map = {
            coNm: '#rf-biz', bsKind: '#rf-uptae', gdKind: '#rf-upjong', bossNm: '#rf-ceo-name',
            settlmnt_acnt_bank_cdNm: '#rf-bank', settlmnt_acnt_no: '#rf-acct', settlmnt_acnt_nm: '#rf-holder',
            // addr1: '#rf-addr', addr2: '#rf-addr2',   // 뺌 — 우편번호 짝 문제로 CardMerchantService 쪽에서 보류
        };
        Object.keys(map).forEach((k) => { if (f[k]) $(map[k]).val(f[k]).trigger('change'); });
        if (f.coNo) $('#rf-bizno').val(this.maskBizNo(f.coNo));
        // 개인/법인은 회원이 고른 값을 그대로 둔다 — OCR 이 바꾸면 필요한 서류가 몰래 달라진다.
        // 다른 구분으로 읽혔는지는 서류 화면 [다음]이 판정해 막는다(blockBizTypeMismatch)
    }

    /**
     * 회사정보(g5_company) 폴백 — runPendingOcr 이 "새로 올린 사진이 아닌" 경우에만 부른다: 정보가
     * 전부 있어 재인식을 생략했을 때(hasCompanyInfo), 또는 저장된 이미지로 재인식했는데도 실패했을 때.
     * 새로 올린 사진의 인식이 실패했을 때는 쓰지 않는다 — 사진이 최신 진실인데 옛 회사정보를 슬쩍
     * 채우면 사진 내용과 다른 값이 제출될 수 있어서다. 그 경우는 그냥 빈칸으로 두고 사용자가 직접 입력한다.
     */
    applyCompanyFallback(key) {
        const d = this.FD || {};
        if (key === 'biz') {
            if (!$('#rf-biz').val() && d.coNm) $('#rf-biz').val(d.coNm);
            if (!$('#rf-bizno').val() && d.coNo) $('#rf-bizno').val(d.coNo);
            if (!$('#rf-uptae').val() && d.bsKind) $('#rf-uptae').val(d.bsKind);
            if (!$('#rf-upjong').val() && d.gdKind) $('#rf-upjong').val(d.gdKind);
            if (!$('#rf-ceo-name').val() && d.bossNm) $('#rf-ceo-name').val(d.bossNm);
            // 개인/법인은 회원이 고른 값을 그대로 둔다(applyOcrToForm 과 같은 이유) — 회사정보로도 바꾸지 않는다.
        } else if (key === 'bank') {
            if (!$('#rf-bank').val() && d.bankNm) {
                $('#rf-bank').val(d.bankNm).trigger('change');
            }
            if (!$('#rf-acct').val() && d.acctNo) $('#rf-acct').val(d.acctNo);
            if (!$('#rf-holder').val() && d.acctNm) $('#rf-holder').val(d.acctNm);
        }
    }

    /**
     * 회사정보(g5_company)가 이 서류의 항목을 전부 갖고 있는지 — 하나라도 비면 false.
     * "일부만 있어도 스킵"이 아니라 "전부 있어야 스킵": 업태·업종처럼 옛 데이터엔 흔히 비어 있는
     * 항목이 있는데, 부분 일치로 재인식을 건너뛰면 그 항목은 영영 채워질 기회를 못 얻는다.
     */
    hasCompanyInfo(key) {
        const d = this.FD || {};
        if (key === 'biz') return !!(d.coNm && d.coNo && d.bsKind && d.gdKind && d.bossNm);
        if (key === 'bank') return !!(d.bankNm && d.acctNo && d.acctNm);
        return false;
    }

    markDocDone(key) {
        this.regDocs[key] = true;
        const $row = $('#pg-reg-docs .doc-up[data-k="' + key + '"]').addClass('done');
        $row.find('.doc-up-st').html('<span class="dus-sub">보기·수정</span>');
        this.syncDocThumb(key);
        this.checkRegDocs();
    }

    /** 행 썸네일 갱신 — 이미지가 있으면 아이콘 대신 썸네일을, PDF 면 'PDF' 딱지를 보여준다 */
    syncDocThumb(key) {
        const src = this.docPreview[key] || '';
        const $thumb = $('#pg-reg-docs .doc-up[data-k="' + key + '"] .doc-up-thumb');
        $thumb.toggleClass('has', !!src).toggleClass('is-pdf', this.isPdfDoc(key)).find('img').attr('src', src);
        const n = this.docPages(key);
        $thumb.find('.doc-up-cnt').text(n > 1 ? n + '장' : '');   // 여러 장 — 썸네일 위 장수
        $thumb.closest('.doc-up').find('.doc-up-file').text(this.isPdfDoc(key) && !(this.regFiles[key].extra || []).length ? this.pdfName(key) : '');
    }

    /** 올린 PDF 의 원래 파일명 — 앱이 안 줬으면(그 전 앱) 빈값 */
    pdfName(key) {
        return (this.regFiles[key] && this.regFiles[key].name) || '';
    }

    /** 이번에 올린 서류의 쪽수 — 첫 장(사진 1 · 묶은 PDF 는 그 쪽수) + 아직 안 묶은 뒤쪽 사진 */
    docPages(key) {
        const ref = this.regFiles[key];
        return ref ? (ref.pages || 1) + (ref.extra || []).length : 0;
    }

    /**
     * [추가 등록] 가능한가 — 이번에 올린 사진이거나 우리가 묶은 PDF 이고, 그 서류 한도(docLimit)가 남았을 때.
     * 앱에서 고른 PDF 는 안 된다(형식이 제각각이라 서버가 쪽을 못 읽을 수 있다). 기존 등록 서류(regFiles 없음)도 안 된다
     */
    canAppendDoc(key) {
        const ref = this.regFiles[key];
        return !!(ref && (!ref.pdf || ref.merged) && this.docPages(key) < this.docLimit(key));
    }

    /** 이번에 올린 서류가 PDF 인가 — 미리보기를 이미지로 그릴 수 없어 따로 다룬다 */
    isPdfDoc(key) {
        return !!(this.regFiles[key] && this.regFiles[key].pdf);
    }

    /**
     * 서류 전체화면 보기 (공용 뷰어). PDF 는 뷰어가 못 그리고, 저장 주소가 .txt 라 앱 shareFile(확장자로
     * 받는다)로도 못 연다 — 미리보기 시트(다시 등록·삭제)로 대신 보낸다.
     */
    zoomDoc(key, index = 0) {
        const photos = this.docPhotos(key);
        if (!photos.length) { if (this.isPdfDoc(key)) this.openDocPreview(key); return; }
        // 확대는 누른 그 장만 — 넘기기는 미리보기 시트에서 한다(renderDocPrevPhotos)
        $j.Ui.ZoomPhoto.open(photos[Math.min(index, photos.length - 1)]);
    }

    /** 이 서류에서 사진으로 볼 수 있는 것 — 첫 장(사진일 때) + 아직 안 묶은 뒤쪽 사진. 묶은 PDF 는 빠진다 */
    docPhotos(key) {
        const ref = this.regFiles[key];
        const first = (!ref || !ref.pdf) && this.docPreview[key] ? [this.docPreview[key]] : [];
        return first.concat(((ref && ref.extra) || []).map((p) => p.url));
    }

    /**
     * 미리보기 시트 사진 — 여러 장이면 좌우로 밀어 넘기는 캐러셀(Swiper, 불러톡 카드와 같은 번들)에 우상단 'i / n'.
     * 시트가 열리는 중이라 처음엔 폭이 0 일 수 있어 observer 로 스스로 다시 잰다.
     */
    renderDocPrevPhotos() {
        const photos = this.docPhotos(this.docPrevKey);
        if (this.docPrevSw) { this.docPrevSw.destroy(true, true); this.docPrevSw = null; }
        this.docPrevIdx = 0;
        if (!photos.length) return;
        const alt = this.esc(CardPay.DOC_NAMES[this.docPrevKey] || '등록된 서류');
        const many = photos.length > 1;
        $('#docprev-img').addClass('has-img').html(
            '<div class="docprev-sw swiper"><div class="swiper-wrapper">'
            + photos.map((u) => '<div class="swiper-slide"><img src="' + u + '" alt="' + alt + '"></div>').join('')
            + '</div></div><button type="button" class="docprev-zoom">눌러서 크게 보기</button>'
            + (many ? '<span class="docprev-cnt">1 / ' + photos.length + '</span>'
                + '<button type="button" class="docprev-nav prev is-end" aria-label="이전">&lsaquo;</button>'
                + '<button type="button" class="docprev-nav next" aria-label="다음">&rsaquo;</button>' : '')
        );
        if (!many || typeof Swiper === 'undefined') return;
        const self = this;
        this.docPrevSw = new Swiper($('#docprev-img .docprev-sw')[0], {
            observer: true,
            observeParents: true,
            on: {
                slideChange() {
                    self.docPrevIdx = this.activeIndex;
                    $('#docprev-img .docprev-cnt').text((this.activeIndex + 1) + ' / ' + photos.length);
                    $('#docprev-img .docprev-nav.prev').toggleClass('is-end', this.isBeginning);
                    $('#docprev-img .docprev-nav.next').toggleClass('is-end', this.isEnd);
                },
            },
        });
    }

    openDocPreview(key) {
        this.docPrevKey = key;
        const name = CardPay.DOC_NAMES[key] || '등록된 서류';
        const src = this.docPreview[key];

        $('#docprev-title').text(name);
        $('#docprev-add').prop('hidden', !this.canAppendDoc(key));
        if (src && this.isPdfDoc(key)) {
            $('#docprev-img').removeClass('has-img').html(
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" width="34" height="34"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/></svg>'
                + '<span class="docprev-name">' + this.esc(this.pdfName(key) || 'PDF 문서') + '</span>'
                + '<span class="docprev-sub">등록됨</span>'
            );
        } else if (src) {
            this.renderDocPrevPhotos();
        } else {
            $('#docprev-img').removeClass('has-img').html(
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" width="34" height="34"><rect x="3.5" y="2.5" width="17" height="19" rx="2.5"/><path d="M7.5 7h9M7.5 11h9M7.5 15h6"/></svg>'
                + '<span class="docprev-name">' + this.esc(name) + '</span>'
                + '<span class="docprev-sub">등록된 이미지가 없습니다</span>'
            );
        }
        this.openBS('bs-docprev');
    }

    removeDoc() {
        const key = this.docPrevKey;
        if (!key) return;

        this.discardDocFiles(key);   // 삭제 — 이번에 찍은 사진(뒤쪽 사진 포함)이면 바로 지운다
        this.clearDocSlot(key);
        this.saveRegDraft();
        this.checkRegDocs();
        this.closeBS();
    }

    /**
     * 사업자등록증·통장사본 칸을 이번 세션 전 상태로 — 저장서류가 있으면 그 미리보기로 '등록됨', 없으면 미등록.
     * 새로 찍은 사진의 인식값도 지운다 — '다음'에서 저장서류로 다시 인식한다(runPendingOcr).
     */
    restoreStoredDoc(key) {
        this.clearDocSlot(key);
        delete this.pendingFile[key];
        this.clearDocFields(key);
        if (this.storedDocs[key] && this.storedPreview[key]) {
            this.docPreview[key] = this.storedPreview[key];
            this.markDocDone(key);
        }
    }

    /** 서류 슬롯 하나를 미등록 상태로 되돌린다 (상태값 + 화면) — removeDoc·clearSessionOnlyDocs 공용 */
    clearDocSlot(key) {
        this.regDocs[key] = false;
        delete this.docFields[key];
        delete this.docPreview[key];
        delete this.regFiles[key];
        $('#pg-reg-docs .doc-up[data-k="' + key + '"]')
            .removeClass('done')
            .find('.doc-up-st').html('<span class="dus-none">미등록</span>');
        this.syncDocThumb(key);
    }

    /**
     * 사업자 구분이 안 맞아 거절됐을 때 다른 구분 서류를 비운다 — 서버가 그 서류를 지웠다(CardMerchantService::register).
     * 사업자등록증·통장사본은 서버도 남겨 두므로 화면도 그대로 둔다. 구분만 바꿔 다시 신청하면 된다.
     * 다른 거절에는 부르지 않는다 — 서류를 그대로 두고 다시 신청한다.
     */
    clearSessionOnlyDocs() {
        ['idFront', 'idBack', 'corpReg', 'shareholders'].forEach((k) => this.clearDocSlot(k));
        this.checkRegDocs();
        this.saveRegDraft();
    }

    /** 이 서류의 이번 세션 파일을 다 지운다 — 첫 장 + 뒤쪽 사진. extraOnly 면 뒤쪽 사진만 */
    discardDocFiles(key, extraOnly = false) {
        const ref = this.regFiles[key];
        if (!ref) return;
        if (!extraOnly) this.discardBridgeFile(ref);
        (ref.extra || []).forEach((p) => this.discardBridgeFile(p));
    }

    /**
     * 방금 올린 서류 한 건이 이노페이 첨부 한도(5.5MB, CardMerchantService::MAX_ATTACH_BYTES)를 넘는지 서버에 묻는다.
     * 넘으면 서버가 파일을 지우고 여기서 안내 — 끝까지 입력하고 제출 때 막히지 않게 올리는 즉시 막는다.
     * 확인을 못 하면(통신 오류) 받아둔다 — 제출 때 서버가 같은 기준으로 다시 본다.
     * @returns {Promise<boolean>} 받아도 되면 true
     */
    checkDocSize(file) {
        const fd = new FormData();
        fd.append('docFileId', file.id);
        fd.append('docFilePath', file.path);
        $j.Common.Util.loadingOverlay(true);
        return new Promise((resolve) => {
            $.ajax({ type: 'POST', url: '/m/cardpay/merchant/doc/check', data: fd, processData: false, contentType: false, dataType: 'json' })
                .done((res) => {
                    if (res && res.tooLarge) {
                        $j.Common.Util.swalert((file.type === 'F' ? 'PDF' : '사진') + ' 용량이 5.5MB 를 넘어 올릴 수 없어요.\n더 작은 파일로 다시 등록해주세요.');
                        return resolve(false);
                    }
                    if (res && res.result === false) { $j.Common.Util.swalert(res.message || '다시 등록해주세요.'); return resolve(false); }
                    resolve(true);
                })
                .fail(() => resolve(true))
                .always(() => $j.Common.Util.loadingOverlay(false));
        });
    }

    /**
     * 이번에 찍은 서류 사진을 서버에서 지운다(교체·삭제) — 결과를 기다리지 않는다. 실패해도 올린 지
     * 24시간에 지워진다(CARD_DOC_TAG). 기존 회원 서류는 regFiles 에 없으니 여기 올 일이 없다.
     */
    discardBridgeFile(ref) {
        if (!ref || !ref.fileId) return;
        const fd = new FormData();
        fd.append('docFileId', ref.fileId);
        fd.append('docFilePath', ref.filePath);
        $.ajax({ type: 'POST', url: '/m/cardpay/merchant/doc/discard', data: fd, processData: false, contentType: false });
    }

    /* ----- 가맹가입 이어하기 (REG_DRAFT_*) ----- */

    regDraftKey() {
        return CardPay.REG_DRAFT_KEY + this.regDraftOwner();
    }

    /** 초안 주인 — 지금 회원번호. 모르면 0(그때는 초안을 쓰지 않는다 — 회원끼리 같은 칸을 나눠 쓰게 된다) */
    regDraftOwner() {
        return String(((typeof MEMBER !== 'undefined' && MEMBER) ? MEMBER.num : 0) || 0);
    }

    /**
     * 초안 읽기 — 없거나 깨졌거나 24시간 지났거나 다른 회원 것이면 null(지운다).
     * 키에 회원번호가 들어 있지만 초안 안에도 주인을 적어 두고 다시 맞춘다 — 회원번호를 못 읽어 0 칸에
     * 쌓인 초안이 계정을 바꾼 뒤 남의 주소·주요판매상품으로 되살아나면 안 된다.
     */
    readRegDraft() {
        if (this.regDraftOwner() === '0') return null;
        try {
            const d = JSON.parse(localStorage.getItem(this.regDraftKey()) || 'null');
            if (d && String(d.mb || '') === this.regDraftOwner() && Date.now() - (d.at || 0) < CardPay.REG_DRAFT_TTL) return d;
            localStorage.removeItem(this.regDraftKey());
        } catch (e) { /* 저장소를 못 쓰는 웹뷰 — 이어하기만 안 된다 */ }
        return null;
    }

    /** 초안 저장 — 찍은 서류 참조(파일번호·경로·미리보기)·인식값·폼 입력값. 시각은 처음 저장한 때를 유지한다 */
    saveRegDraft() {
        const files = {};
        Object.keys(this.regFiles).forEach((k) => {
            files[k] = Object.assign({}, this.regFiles[k], { url: this.docPreview[k] || '' });
        });
        const form = {};
        CardPay.REG_FORM_FIELDS.forEach((sel) => { form[sel] = $(sel).val() || ''; });

        if (this.regDraftOwner() === '0') return;
        const prev = this.readRegDraft();
        try {
            localStorage.setItem(this.regDraftKey(), JSON.stringify({
                mb: this.regDraftOwner(),
                at: (prev && prev.at) || Date.now(),
                biz: $('input[name="biztype"]:checked').val() || '',
                files,
                pending: Object.keys(this.pendingFile),
                fields: this.docFields,
                form,
            }));
        } catch (e) { /* noop */ }
    }

    clearRegDraft() {
        try { localStorage.removeItem(this.regDraftKey()); } catch (e) { /* noop */ }
    }

    /** 다시 들어왔을 때 — 찍어둔 서류·입력값을 되살린다(24시간 안의 초안만) */
    restoreRegDraft() {
        const d = this.readRegDraft();
        if (!d) return;

        if (d.biz) $('input[name="biztype"][value="' + d.biz + '"]').prop('checked', true);
        this.docFields = d.fields || {};
        Object.keys(d.files || {}).forEach((k) => {
            const f = d.files[k];
            if (!f || !f.fileId) return;
            this.regFiles[k] = { fileId: f.fileId, filePath: f.filePath };
            if (f.pdf) Object.assign(this.regFiles[k], { pdf: 1, name: f.name || '' });
            if (f.merged) Object.assign(this.regFiles[k], { merged: 1, pages: f.pages || 0 });
            if (Array.isArray(f.extra) && f.extra.length) this.regFiles[k].extra = f.extra;
            if (f.url) this.docPreview[k] = f.url;
            if ((d.pending || []).indexOf(k) > -1) this.pendingFile[k] = this.regFiles[k];
            this.markDocDone(k);
        });
        Object.keys(d.form || {}).forEach((sel) => { if (d.form[sel]) $(sel).val(d.form[sel]); });
        $('#rf-biz').val(CardPay.cleanCoName($('#rf-biz').val()));   // 규칙이 생기기 전에 저장된 이어하기 값
        $('#rf-bank').trigger('change');   // 은행 선택 표시 맞추기
        this.setBizType($('input[name="biztype"]:checked').val() || 'personal');
        this.draftCheck = this.verifyDraftDocs();   // 신청(submitRegister)은 이 확인이 끝난 뒤에 나간다
    }

    /**
     * 복원한 서류가 서버에 아직 있는지 하나씩 묻는다(doc/check). 초안은 이 폰에만 있어서, 신청 도중 화면을 나가
     * 응답을 못 받으면 서버가 이미 지운 서류가 '등록됨'으로 되살아난다 — 그대로 신청하면 '신분증 원본 앞면 서류를
     * 등록해주세요'로 막힌다. 없는 서류는 미등록으로 되돌린다(사업자등록증·통장사본은 저장서류가 있으면 그 상태로).
     * 확인을 못 하면(통신 오류) 그대로 둔다 — 제출 때 서버가 다시 본다.
     * @returns {Promise<void>}
     */
    verifyDraftDocs() {
        const refs = [];
        Object.keys(this.regFiles).forEach((key) => {
            refs.push({ key, ref: this.regFiles[key], main: true });
            (this.regFiles[key].extra || []).forEach((p) => refs.push({ key, ref: p }));
        });
        return Promise.all(refs.map((r) => this.docExists(r.ref).then((ok) => Object.assign(r, { ok }))))
            .then((list) => {
                const gone = [];
                list.filter((r) => r.main && !r.ok).forEach((r) => {
                    this.discardDocFiles(r.key, true);   // 남은 뒤쪽 사진도 붙일 데가 없다
                    if (r.key === 'biz' || r.key === 'bank') this.restoreStoredDoc(r.key);
                    else this.clearDocSlot(r.key);
                    if (!this.regDocs[r.key]) gone.push(CardPay.DOC_NAMES[r.key] || '서류');   // 저장서류로 채워졌으면 안내하지 않는다
                });
                // 뒤쪽 사진만 없어졌으면 그 장만 뺀다
                list.filter((r) => !r.main && !r.ok && this.regFiles[r.key]).forEach((r) => {
                    const ref = this.regFiles[r.key];
                    ref.extra = (ref.extra || []).filter((p) => p.fileId !== r.ref.fileId);
                    this.syncDocThumb(r.key);
                });
                if (!list.some((r) => !r.ok)) return;
                this.checkRegDocs();
                this.saveRegDraft();
                if (gone.length) $j.Common.Util.tsalert('error', '등록했던 서류가 지워져 다시 등록해 주세요: ' + gone.join(', '));
            });
    }

    /** 서류 파일이 서버에 아직 있는가 — 없거나 첨부 한도를 넘어 지워졌으면 false, 확인 못 하면 true */
    docExists(ref) {
        const fd = new FormData();
        fd.append('docFileId', ref.fileId);
        fd.append('docFilePath', ref.filePath);
        return new Promise((resolve) => {
            $.ajax({ type: 'POST', url: '/m/cardpay/merchant/doc/check', data: fd, processData: false, contentType: false, dataType: 'json' })
                .done((res) => resolve(!!res && res.result !== false && !res.tooLarge))
                .fail(() => resolve(true));
        });
    }

    /**
     * 약관 본문 — blade 에 숨겨둔 원문을 옮겨 그린다. 끝까지 읽어야 [확인하고 동의] 가 열린다.
     * 위치정보 동의 약관 뷰어(F/Mobile/LocationGate._openTerm)와 같은 방식이다 — 다 읽기 전 버튼은
     * '끝까지 읽어주세요 ↓' 이고, 누르면 본문을 맨 아래로 내려준다. 잠금을 .dis(누름 막힘)가 아니라
     * .is-locked 로 하는 이유 — 눌러야 내려줄 수 있다.
     */
    openTerms(key) {
        const $src = $('.terms-doc[data-terms="' + key + '"]');
        if (!$src.length) return;

        this.curTerms = key;
        $('#pg-terms-title').text($src.data('title') || '약관');
        $('#pg-terms-body').html($src.html());

        const already = $('#a-' + key).prop('checked');
        this.setTermsLocked(!already, already ? '동의 완료' : '확인하고 동의');

        this.openPg('pg-terms');

        const el = $('#pg-terms .pgb')[0];
        if (!el) return;
        el.scrollTop = 0;
        $(el).off('scroll.terms').on('scroll.terms', () => this.checkTermsScroll(el));
        setTimeout(() => this.checkTermsScroll(el), 120);   // 본문이 짧아 스크롤이 없으면 바로 활성
    }

    checkTermsScroll(el) {
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 4 && $('#terms-confirm').hasClass('is-locked')) this.setTermsLocked(false, '확인하고 동의');
    }

    setTermsLocked(locked, text) {
        $('#terms-confirm').toggleClass('is-locked', locked)
            .html(locked ? '끝까지 읽어주세요 <span aria-hidden="true">↓</span>' : text);
    }

    /** 잠긴 [확인하고 동의]를 누르면 — 본문을 맨 아래로 부드럽게 내린다. 바닥에 닿으면 scroll 이 checkTermsScroll 을 돌린다 */
    scrollTermsToBottom() {
        const el = $('#pg-terms .pgb')[0];
        if (!el) return;
        const top = el.scrollHeight - el.clientHeight;
        try { el.scrollTo({ top, behavior: 'smooth' }); } catch (e) { el.scrollTop = top; }
        setTimeout(() => this.checkTermsScroll(el), 400);   // smooth 미지원 웹뷰·중간에 멈춘 경우
        setTimeout(() => this.checkTermsScroll(el), 900);
    }

    /* ======================================================================
     *  가맹점 등록 — 가입 신청 폼
     * ==================================================================== */

    openRegApply() {
        // 담당자(3) 단계는 법인만 — 개인은 대표자가 곧 계약담당자다
        this.regSeq = this.isCorp() ? [1, 2, 3, 4] : [1, 2, 4];
        this.regGoStep(0);
        this.openPg('pg-reg-apply');
    }

    regGoStep(idx) {
        this.regIdx = idx;
        const step = this.regSeq[idx];
        const total = this.regSeq.length;

        $('#pg-reg-apply .rstep').hide().filter('[data-step="' + step + '"]').show();
        $('#reg-prog-title').text(CardPay.STEP_TITLES[step]);
        $('#reg-prog-txt').text((idx + 1) + ' / ' + total);
        $('#reg-prog-bar').css('width', ((idx + 1) / total * 100) + '%');
        $('#b-reg-prev').css('display', idx > 0 ? 'flex' : 'none');
        $('#b-reg-next').removeClass('dis');
        $('#pg-reg-apply .pgb').scrollTop(0);
    }

    /** 다음 단계 — 마지막이면 제출 */
    regNext($btn) {
        const miss = this.regStepMissing(this.regSeq[this.regIdx]);
        if (miss) { $j.Common.Util.tsalert('error', miss + ' 을(를) 입력해주세요.'); return; }
        // 사진 인식값도 있어 칸에서 자르지 않는다 — 잘린 상호가 계약에 들어가지 않게 회원이 직접 줄인다. 40 은 COMPANY_NAME_MAX_BYTES 와 같은 값
        if ($('#rf-biz').is(':visible') && CardPay.coNameBytes($.trim($('#rf-biz').val() || '')) > 40) {
            $j.Common.Util.tsalert('error', '상호명은 40byte(한글 20자)까지 입력할 수 있어요. 줄여 주세요.');
            return;
        }
        // 사진 인식값은 칸의 maxlength 를 거치지 않는다. 50 은 CardMerchantService::BIZ_KIND_MAX 와 같은 값
        const longKind = [['#rf-uptae', '업태'], ['#rf-upjong', '종목']]
            .find(([sel]) => $(sel).is(':visible') && $.trim($(sel).val() || '').length > 50);
        if (longKind) { $j.Common.Util.tsalert('error', longKind[1] + ' 칸을 50자 이내로 확인해 주세요. 다른 글자가 섞였다면 지워 주세요.'); $(longKind[0]).trigger('focus'); return; }
        // 이메일은 형식까지 본다 — 지금 단계에 보이는 칸만(대표자=2단계, 담당자=3단계)
        const badEmail = [['#rf-ceo-email', '대표자 이메일'], ['#rf-mgr-email', '담당자 이메일']]
            .find(([sel]) => $(sel).is(':visible') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test($.trim($(sel).val() || '')));
        if (badEmail) { $j.Common.Util.tsalert('error', badEmail[1] + ' 형식이 올바르지 않습니다.'); $(badEmail[0]).trigger('focus'); return; }
        // 고른 구분과 사업자번호가 다르면 다음 단계로도, 제출로도 못 간다(서버도 같은 검사로 되돌려 보낸다)
        if (this.blockBizTypeMismatch()) return;
        if (this.regIdx < this.regSeq.length - 1) {
            this.regGoStep(this.regIdx + 1);
            return;
        }
        // 화면을 열어 둔 채 24시간이 지나면 사진이 지워져 있다 — 보내기 전에 다시 확인한다(submitRegister 가 끝나길 기다린다)
        this.draftCheck = this.verifyDraftDocs();
        this.submitRegister($btn);
    }

    /**
     * 사업자등록증·사업자번호가 말하는 구분이 고른 구분과 다르면 안내창을 띄우고 true(= 진행 막음).
     * 기준은 가입폼 사업자번호 칸(인식값·회사정보·직접 입력), 칸이 비었으면 사업자등록증 인식 결과의 구분.
     */
    blockBizTypeMismatch() {
        let expected = this.bizTypeOfNo($('#rf-bizno').val());
        const f = this.docFields.biz || {};
        if (!expected && f.coCl) expected = f.coCl === '2' ? 'corp' : 'personal';
        return !!expected && this.warnBizTypeMismatch(expected);
    }

    /** 사업자번호 가운데 두 자리로 본 구분 — 81~88 법인. CardMerchantService::bizTypeOfNo 와 같은 규칙. 10자리가 아니면 '' */
    bizTypeOfNo(no) {
        const d = String(no || '').replace(/\D/g, '');
        if (d.length !== 10) return '';
        const mid = parseInt(d.substr(3, 2), 10);
        return (mid >= 81 && mid <= 88) ? 'corp' : 'personal';
    }

    /**
     * 사업자등록증이 가리키는 구분(expected)이 회원이 고른 구분과 다르면 안내창을 띄운다 — 다르면 true.
     * 구분은 회원이 [구분 바꾸고 서류 다시 등록]을 눌렀을 때만 바꾼다. 서류가 달라서(개인=신분증,
     * 법인=등기부등본·주주명부) 바꾸면 서류 등록 화면으로 돌려보낸다.
     */
    warnBizTypeMismatch(expected, message) {
        const current = this.isCorp() ? 'corp' : 'personal';
        if (expected === current) return false;

        const corp = expected === 'corp';
        $j.Ui.Dialog.open({
            title: corp ? '법인사업자로 확인됐어요' : '개인사업자로 확인됐어요',
            desc: message || (corp
                ? '사업자등록증의 사업자번호가 법인사업자예요. 구분을 법인으로 바꾸고 법인 등기부등본·주주명부를 등록해야 신청할 수 있어요. 사업자등록증을 잘못 올렸다면 다시 올려 주세요.'
                : '사업자등록증의 사업자번호가 개인사업자예요. 구분을 개인으로 바꾸고 신분증 앞·뒷면을 등록해야 신청할 수 있어요. 사업자등록증을 잘못 올렸다면 다시 올려 주세요.'),
            cancel: '닫기',
            confirm: corp ? '법인으로 바꾸고 서류 등록' : '개인으로 바꾸고 서류 등록',
            onConfirm: () => {
                $('input[name="biztype"][value="' + expected + '"]').prop('checked', true).trigger('change');
                // 가입폼 위에서 떴으면 폼을 닫아 아래 서류 등록 화면으로 돌아간다
                if ($('#pg-reg-apply').hasClass('show')) this.closePg('pg-reg-apply');
            },
        });
        return true;
    }

    /** 단계별 필수값 검사 — 비어 있는 첫 항목명을 돌려준다 */
    regStepMissing(step) {
        const required = {
            1: [['#rf-biz', '상호명'], ['#rf-bizno', '사업자번호'], ['#rf-addr', '사업장 주소'],
                ['#rf-uptae', '업태'], ['#rf-upjong', '종목'], ['#rf-item', '주요판매상품']],
            2: [['#rf-ceo-name', '대표자명'], ['#rf-ceo-phone', '대표자 휴대폰'],
                ['#rf-ceo-birth', '대표자 생년월일'], ['#rf-ceo-tel', '대표자 유선전화'],
                ['#rf-ceo-email', '대표자 이메일']],
            3: [['#rf-mgr-name', '담당자명'], ['#rf-mgr-phone', '담당자 휴대폰'],
                ['#rf-mgr-email', '담당자 이메일']],
            4: [['#rf-bank', '정산은행'], ['#rf-acct', '정산 계좌번호'], ['#rf-holder', '정산 계좌주']],
        };
        const list = (required[step] || []).slice();
        if (step === 1 && this.isCorp()) list.splice(2, 0, ['#rf-corpno', '법인등록번호']);

        const empty = list.find(([sel]) => !$.trim($(sel).val() || ''));
        return empty ? empty[1] : '';
    }

    /**
     * 사업장 주소 — 우편번호 검색($j.F.Postcode). 카카오맵 검색은 우편번호를 안 주지만
     * (키워드 검색 결과엔 zone_no 자체가 없다), 다음 우편번호 검색은 주소·우편번호를 항상
     * 짝으로 준다 — 가맹 신청엔 우편번호가 필수라 이쪽을 쓴다.
     */
    searchCompanyAddress() {
        $j.F.Postcode.open((data) => {
            $('#rf-addr').val(data.roadAddress || data.jibunAddress || data.address || '');
            $('#rf-zip').val(data.zonecode || '');
        });
    }

    /** 가입 신청 폼 → registMemberContract 파라미터 (서버가 검증·보정한다) */
    registerPayload() {
        return {
            coCl: this.isCorp() ? '2' : '1',
            coNm: $('#rf-biz').val(),
            coNo: ($('#rf-bizno').val() || '').replace(/\D/g, ''),
            corpNo: ($('#rf-corpno').val() || '').replace(/\D/g, ''),
            zipCode: $('#rf-zip').val(),
            addr1: $('#rf-addr').val(),
            addr2: $('#rf-addr2').val(),
            bsKind: $('#rf-uptae').val(),
            gdKind: $('#rf-upjong').val(),
            productNm: $('#rf-item').val(),
            bossNm: $('#rf-ceo-name').val(),
            hpNo: ($('#rf-ceo-phone').val() || '').replace(/\D/g, ''),
            birth: $('#rf-ceo-birth').val(),
            telNo: ($('#rf-ceo-tel').val() || '').replace(/\D/g, ''),
            email: $('#rf-ceo-email').val(),
            contNm: $('#rf-mgr-name').val(),
            contCp: ($('#rf-mgr-phone').val() || '').replace(/\D/g, ''),
            contTel: ($('#rf-mgr-tel').val() || '').replace(/\D/g, ''),
            contEmail: $('#rf-mgr-email').val(),
            bankNm: $('#rf-bank').val(),
            acctNo: ($('#rf-acct').val() || '').replace(/\D/g, ''),
            acctNm: $('#rf-holder').val(),
        };
    }

    /**
     * 가맹점 가입 신청 — 가입폼 값 + 서류 원본(attach_file_nm1~4 대상)을 한 번에 multipart 로 보낸다.
     * 서버가 이노페이 registMemberContract 전송 후 MID 를 저장한다.
     */
    submitRegister($btn) {
        const self = this;
        if ($btn.data('busy')) return;

        // 복원한 서류 확인(verifyDraftDocs)이 아직이면 끝난 뒤 다시 — 지워진 서류를 그대로 보내지 않게
        if (this.draftCheck) {
            $btn.data('busy', 1);
            this.draftCheck.then(() => {
                this.draftCheck = null;
                $btn.data('busy', 0);
                this.submitRegister($btn);
            });
            return;
        }
        // 확인에서 빠진 서류가 있다 — 보내면 서버가 막는다. 서류 등록 화면으로 돌려보낸다(안내는 확인이 이미 띄웠다)
        if (!this.activeDocKeys().every((k) => this.regDocs[k])) {
            this.closePg('pg-reg-apply');
            return;
        }

        const fd = new FormData();
        const payload = this.registerPayload();
        Object.keys(payload).forEach((k) => fd.append(k, payload[k] == null ? '' : payload[k]));
        // 체크한 약관 — 서버가 필수 누락을 다시 보고, 선택 약관 동의/거부를 이력에 남긴다
        $('.terms-row').each(function () {
            if ($(this).find('input').prop('checked')) fd.append('terms[]', $(this).data('terms'));
        });

        // 서류 원본 — 개인/법인 공통(biz·bank) + 해당 사업체 구분의 서류만 실어 보낸다.
        // 6종 전부 raw File이 아니라 {fileId,filePath} 참조다 — 서버가 그걸로 원본을 읽어온다
        // (CardPayController::merchantRegister → resolveBridgeFile).
        const docKeys = this.activeDocKeys();
        docKeys.forEach((k) => {
            const v = this.regFiles[k];
            if (!v) return;
            fd.append(k + 'FileId', v.fileId);
            fd.append(k + 'FilePath', v.filePath);
        });

        $btn.data('busy', 1).addClass('dis');
        this.waitCard('이노페이에 가입 신청을 넣고 있어요', '서류와 신청서를 보내는 중이에요.<br>잠시만 기다려 주세요', 'write');
        $.ajax({
            type: 'POST',
            url: '/m/cardpay/merchant/register',
            data: fd,
            processData: false,
            contentType: false,
            dataType: 'json',
        }).done((res) => {
            if (res && res.success) {
                // 보낸 서류는 서버가 지웠다. 사업체 구분을 바꾸기 전에 올린 다른 구분 서류(신분증·등기부등본 등)는
                // 제출되지 않아 남아 있다 — 개인정보라 24시간을 기다리지 않고 지금 지운다
                Object.keys(self.regFiles).filter((k) => docKeys.indexOf(k) < 0).forEach((k) => self.discardDocFiles(k));
                self.clearRegDraft(); self.showPending(); return;
            }
            // 거절돼도 서류는 그대로 둔다 — 서버도 안 지운다(CardMerchantService::register). 구분이 틀렸을 때만 다른 구분 서류를 비운다
            if (res && res.bizType) self.clearSessionOnlyDocs();
            if (res && res.bizType && self.warnBizTypeMismatch(res.bizType, res.message)) return;
            $j.Common.Util.swalert((res && res.message) || '신청에 실패했습니다.');
        }).fail((xhr) => {
            const res = xhr.responseJSON;
            if (res && res.bizType) self.clearSessionOnlyDocs();
            if (res && res.bizType && self.warnBizTypeMismatch(res.bizType, res.message)) return;
            $j.Common.Util.swalert((res && res.message) || '신청 중 오류가 발생했습니다.');
        }).always(() => {
            self.waitCard();
            $btn.data('busy', 0).removeClass('dis');
        });
    }

    /* ======================================================================
     *  관리자 모드 — 지정 관리자가 회사 MID 로 받는 외부배차·장기선결제 (partials/admin·admin_pages)
     *  결제는 일반 결제와 같은 /m/cardpay/pay 로 보낸다(adminKind). 장기선결제는 금액표대로 받기만 하고
     *  선결제 등록은 하지 않는다(기사편집에서 따로). 내역·취소는 /m/cardpay/admin/*.
     * ==================================================================== */

    /** 관리자 모드 진입 — 결제·내역 탭 대신 관리자 내역 화면 */
    showAdmin() {
        $('.lt').removeClass('on');
        $('.tc').hide();
        $('#main-tab').hide();
        $('#tc-admin').css('display', 'flex');
        $('#r-admin-bar').css('display', 'flex');   // 전표 화면에 저장·공유 버튼(관리자 모드만)
        this.admin = { kind: 'ext', items: [], driver: null, period: 0, seq: 0, busy: false, payat: { items: [], hasMore: false, loading: false, seq: 0 } };
        this.adminPrices = this.readJson('#cardpay-prepay-prices', {});
        this.renderAdminPeriods();
        this.loadAdminList();
    }

    bindAdmin() {
        const self = this;
        let rqTimer = null;
        let dqTimer = null;

        $('#admin-back').on('click', () => self.exit());
        $(document).on('click', '.atab-b', function () {
            self.admin.kind = String($(this).data('kind'));
            $('.atab-b').removeClass('on').filter(this).addClass('on');
            $('#admin-rq').val('');
            $('#admin-rsb').removeClass('has');
            self.loadAdminList();
        });
        $('#admin-rq').on('input', function () {
            $('#admin-rsb').toggleClass('has', $.trim(this.value) !== '');
            clearTimeout(rqTimer);
            rqTimer = setTimeout(() => self.loadAdminList(), 300);
        });
        $('#admin-rq-clear').on('click', () => { $('#admin-rq').val(''); $('#admin-rsb').removeClass('has'); self.loadAdminList(); });
        $('#admin-add-btn').on('click', () => self.openAdminPay());

        // 페이앳 내역
        let payatTimer = null;
        $('#admin-payat-btn').on('click', () => {
            $('#admin-payat-q').val('');
            $('#admin-payat-sb').removeClass('has');
            self.openPg('pg-admin-payat');
            self.loadAdminPayat(true);
        });
        $('#admin-payat-back').on('click', () => self.closePg('pg-admin-payat'));
        $('#admin-payat-q').on('input', function () {
            $('#admin-payat-sb').toggleClass('has', $.trim(this.value) !== '');
            clearTimeout(payatTimer);
            payatTimer = setTimeout(() => self.loadAdminPayat(true), 300);
        });
        $('#admin-payat-q-clear').on('click', () => {
            clearTimeout(payatTimer);
            $('#admin-payat-q').val('');
            $('#admin-payat-sb').removeClass('has');
            self.loadAdminPayat(true);
        });
        $('#admin-payat-scroll').on('scroll', function () {
            if (this.scrollTop + this.clientHeight >= this.scrollHeight - 300) self.loadAdminPayat(false);
        });

        $(document).on('click', '.arec-dl', function () { self.openAdminReceipt(Number($(this).data('id'))); });
        $(document).on('click', '.arec-cxbtn', function () { self.openAdminCancel(Number($(this).data('id'))); });
        $(document).on('click', '.ac-copy', function (e) { e.stopPropagation(); self.copyText(String($(this).data('copy'))); });

        // 결제 입력
        $('#admin-pay-back').on('click', () => self.closePg('pg-admin-pay'));
        $('#admin-q').on('input', function () {
            const v = $.trim(this.value);
            $('#admin-sb').toggleClass('has', v !== '');
            clearTimeout(dqTimer);
            if (!v) { $('#admin-dlist').empty(); return; }
            dqTimer = setTimeout(() => self.searchAdminDrivers(v), 300);
        });
        $('#admin-q-clear').on('click', () => { $('#admin-q').val('').focus(); $('#admin-sb').removeClass('has'); $('#admin-dlist').empty(); });
        $(document).on('click', '#admin-dlist .adrv', function () { self.pickAdminDriver(self._adminDrivers[Number($(this).data('i'))]); });
        $(document).on('click', '#admin-change-drv', () => { self.admin.driver = null; $('#admin-driver-chip').hide(); $('#admin-drv-field').show(); $('#admin-q').val('').focus(); self.checkAdminPay(); });
        $(document).on('click', '#admin-period .apb', function () { self.admin.period = Number($(this).data('m')); self.renderAdminPeriods(); self.checkAdminPay(); });
        $('#admin-m-price').on('input', function () {
            const v = this.value.replace(/\D/g, '').slice(0, 9);
            this.value = v ? Number(v).toLocaleString() : '';
            self.calcAdminExt();
        });
        $('input[name="admin-m-vat"]').on('change', () => self.calcAdminExt());
        $('#admin-m-addr').on('input change', () => self.checkAdminPay());
        this.bindWorkDatePicker($('#admin-m-date'), () => self.checkAdminPay());
        $('#admin-ci-num').on('input', function () {
            this.value = this.value.replace(/\D/g, '').slice(0, 16).replace(/(\d{4})(?=\d)/g, '$1-');
            self.checkAdminPay();
        });
        $('#admin-ci-exp').on('input', function () { self.formatCardExp(this); self.checkAdminPay(); });
        $('#admin-paybtn').on('click', function () { if (!$(this).hasClass('dis')) self.confirmAdminPay(); });
        $('#admin-done-ok').on('click', () => { self.closePg('pg-admin-done'); self.loadAdminList(); });

        // 전표 저장·공유 (관리자 모드에서만 보이는 바)
        $('#r-admin-save, #r-admin-share').on('click', function () {
            if (!self.detItem) return;
            self.shareReceipt($(this).attr('id') === 'r-admin-share', self.detItem.x ? '취소전표' : '매출전표');
        });
    }

    /** 내역 받기 — 탭(kind)·검색어. 늦게 온 이전 응답은 버린다 */
    loadAdminList() {
        const a = this.admin;
        const seq = ++a.seq;
        const label = a.kind === 'long' ? '장기선결제' : '외부배차';
        $('#admin-list-name').text(label);
        $('#admin-add-label').text(label + ' 추가');
        $('#admin-rq').attr('placeholder', a.kind === 'long' ? '기사 이름·회원번호 검색' : '현장주소 검색');
        $.get('/m/cardpay/admin/list', { kind: a.kind, q: $.trim($('#admin-rq').val() || '') })
            .done((res) => {
                if (seq !== a.seq) return;
                a.items = (res && res.items) || [];
                this.renderAdminList();
            })
            .fail(() => { if (seq === a.seq) $j.Common.Util.tsalert('error', '내역을 불러오지 못했습니다.'); });
    }

    /** 페이앳 내역 받기 — reset 이면 처음부터, 아니면 다음 묶음. 늦게 온 이전 응답은 버린다 */
    loadAdminPayat(reset) {
        const p = this.admin.payat;
        if (reset) { p.seq++; p.items = []; p.hasMore = true; p.loading = false; }
        if (p.loading || !p.hasMore) return;
        p.loading = true;
        const seq = p.seq;
        $.get('/m/cardpay/admin/payat', { offset: p.items.length, q: $.trim($('#admin-payat-q').val() || '') })
            .done((res) => {
                if (seq !== p.seq) return;
                p.loading = false;
                p.items = p.items.concat((res && res.items) || []);
                p.hasMore = !!(res && res.hasMore);
                this.renderAdminPayat();
                // 첫 묶음이 화면보다 짧으면 스크롤이 안 생겨 다음 묶음을 못 받는다
                const el = $('#admin-payat-scroll')[0];
                if (p.hasMore && el && el.scrollHeight <= el.clientHeight + 300) this.loadAdminPayat(false);
            })
            .fail(() => {
                if (seq !== p.seq) return;
                p.loading = false;
                $j.Common.Util.tsalert('error', '내역을 불러오지 못했습니다.');
            });
    }

    /** 페이앳 내역 — 회원 내역과 같은 카드(histRow)를 결제일로 묶어 그린다. 누르면 같은 상세(.hc → openDetailById) */
    renderAdminPayat() {
        const q = $.trim($('#admin-payat-q').val() || '');
        let html = '';
        let date = null;
        this.admin.payat.items.forEach((i) => {
            if (i.date !== date) { date = i.date; html += '<div class="dg-t">' + this.esc(date) + '</div>'; }
            html += this.histRow(i);
        });
        $('#admin-payat-list').html(html
            ? '<p class="hist-legacy-note">결제 문의 : 페이앳 1661-3480</p>' + html
            : '<div class="psl-empty">' + (q ? '검색 결과가 없습니다' : '페이앳 내역이 없습니다') + '</div>');
    }

    renderAdminList() {
        const a = this.admin;
        const copyIc = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
        const dlIc = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>';
        const html = a.items.map((i) => {
            const tag = i.x ? '<span class="adv-tag cxl">취소</span>' : '<span class="adv-tag st-pay">결제</span>';
            const who = i.kind === 'long'
                ? this.esc(i.targetName) + ' <span class="arec-mno ac-copy" data-copy="' + i.targetNo + '">#' + i.targetNo + copyIc + '</span>'
                : this.esc(i.addr || '외부배차');
            const acts = '<button class="arec-dl" data-id="' + i.id + '">' + dlIc + '전표</button>'
                + (!i.x && i.td ? '<button class="arec-cxbtn" data-id="' + i.id + '">취소</button>' : '');
            const reason = i.x && i.cancelReason
                ? '<div class="arec-cxreason">사유 · ' + this.esc(i.cancelReason) + (i.cancelByName ? ' (' + this.esc(i.cancelByName) + ')' : '') + '</div>' : '';
            return '<div class="arec' + (i.x ? ' cxd' : '') + '">'
                + '<div class="arec-r1"><span class="arec-nm">' + tag + ' ' + who + '</span>'
                + '<span class="arec-amt' + (i.x ? ' cx' : '') + '">' + this.format(i.amt + (i.vat || 0)) + '원</span></div>'
                + '<div class="arec-r2b"><span class="arec-meta">결제 ' + this.esc(i.payDate.slice(2, 16)) + ' · ' + this.esc(i.byName) + '</span>'
                + '<span class="arec-acts">' + acts + '</span></div>' + reason + '</div>';
        }).join('');
        const q = $.trim($('#admin-rq').val() || '');
        $('#admin-rlist').html(html || '<div class="psl-empty">' + (q ? '검색 결과가 없습니다' : (a.kind === 'long' ? '장기선결제' : '외부배차') + ' 내역이 없습니다') + '</div>');
        $('#admin-rcount').text(a.items.length);
    }

    findAdminItem(id) { return this.admin.items.find((i) => i.id === id) || null; }

    openAdminReceipt(id) {
        const item = this.findAdminItem(id);
        if (!item) return;
        this.detItem = item;   // 저장·공유(shareReceipt)가 이 값을 본다
        this.openReceipt(item.x, item);
    }

    /* ----- 결제 입력 ----- */

    openAdminPay() {
        const kind = this.admin.kind;
        this.admin.driver = null;
        this.admin.period = 0;
        $('#admin-pay-title').text(kind === 'long' ? '장기선결제' : '외부배차');
        $('#admin-long-fields').toggle(kind === 'long');
        $('#admin-ext-fields').toggle(kind === 'ext');
        $('#admin-q, #admin-memo, #admin-m-addr, #admin-m-price, #admin-ci-num, #admin-ci-exp').val('');
        $('#admin-sb').removeClass('has');
        $('#admin-dlist').empty();
        $('#admin-driver-chip').hide();
        $('#admin-drv-field').show();
        $('#admin-m-date').val(this.today);
        $('input[name="admin-m-vat"][value="sep"]').prop('checked', true);
        this.renderAdminPeriods();
        this.calcAdminExt();
        this.openPg('pg-admin-pay');
    }

    /** 기간 버튼 — 금액표(MemberService::getPrepayPriceList)의 개월 수대로 */
    renderAdminPeriods() {
        const months = Object.keys(this.adminPrices || {}).map(Number).sort((x, y) => x - y);
        if (!this.admin.period && months.length) this.admin.period = months[0];
        $('#admin-period').html(months.map((m) =>
            '<button type="button" class="apb' + (m === this.admin.period ? ' on' : '') + '" data-m="' + m + '">' + this.periodLabel(m) + '</button>').join(''));
        $('#admin-amt').text(this.format((this.adminPrices || {})[this.admin.period] || 0) + '원');
    }

    periodLabel(m) { return m >= 12 ? (m / 12) + '년' : m + '개월'; }

    searchAdminDrivers(q) {
        $.get('/m/cardpay/admin/drivers', { q }).done((res) => {
            if ($.trim($('#admin-q').val()) !== q) return;   // 그새 검색어가 바뀌었다
            this._adminDrivers = (res && res.list) || [];
            const arrow = '<svg class="adv-arrow" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>';
            $('#admin-dlist').html(this._adminDrivers.map((d, i) =>
                '<div class="adrv" data-i="' + i + '"><div class="adv-main"><div class="adv-nm">' + this.esc(d.name) + this.paidTag(d.paid) + '</div>'
                + '<div class="adv-sub">' + this.esc(d.phone) + ' · 회원번호 ' + d.num + '</div></div>' + arrow + '</div>').join('')
                || '<div class="psl-empty">검색 결과가 없습니다</div>');
        });
    }

    paidTag(paid) { return paid ? '<span class="adv-tag paid">유료</span>' : '<span class="adv-tag free">무료</span>'; }

    pickAdminDriver(d) {
        if (!d) return;
        this.admin.driver = d;
        $('#admin-driver-chip').html('<div class="ac-main"><div class="ac-nm">' + this.esc(d.name) + this.paidTag(d.paid) + '</div>'
            + '<div class="ac-sub">' + this.esc(d.phone) + ' · 회원번호 ' + d.num + '</div></div>'
            + '<button class="ac-change" id="admin-change-drv">변경</button>').css('display', 'flex');
        $('#admin-drv-field').hide();
        $('#admin-dlist').empty();
        this.checkAdminPay();
    }

    /** 외부배차 금액 — 부가세 별도면 +10%, 포함이면 입력값이 총액(서버 buildKeyinPayload 와 같은 계산) */
    calcAdminExt() {
        const raw = Number(($('#admin-m-price').val() || '').replace(/\D/g, '')) || 0;
        const inc = $('input[name="admin-m-vat"]:checked').val() === 'inc';
        const total = inc ? raw : raw + Math.round(raw * 0.1);
        const work = inc ? Math.round(raw / 1.1) : raw;
        $('#admin-mpv-w').text(this.format(work) + '원');
        $('#admin-mpv-v').text(this.format(total - work) + '원');
        $('#admin-mpv-total').text(this.format(total) + '원');
        $('#admin-m-pb').toggle(raw > 0);
        this.checkAdminPay();
        return total;
    }

    checkAdminPay() {
        const card = this.isValidCardLen($('#admin-ci-num').val().replace(/\D/g, '').length) && $('#admin-ci-exp').val().length === 5;
        const ok = this.admin.kind === 'long'
            ? card && !!this.admin.driver && !!this.admin.period
            : card && Number(($('#admin-m-price').val() || '').replace(/\D/g, '')) > 0 && $('#admin-m-date').val() !== '' && $.trim($('#admin-m-addr').val()) !== '';
        $('#admin-paybtn').toggleClass('dis', !ok);
    }

    confirmAdminPay() {
        const a = this.admin;
        const msg = a.kind === 'long'
            ? a.driver.name + '님 장기선결제(' + this.periodLabel(a.period) + ') ' + this.format(this.adminPrices[a.period]) + '원을 결제할까요?'
            : '외부배차 ' + this.format(this.calcAdminExt()) + '원을 결제할까요?';
        $j.Common.Util.swalert(msg, () => this.payAdmin(), ['결제', '취소']);
    }

    payAdmin() {
        const a = this.admin;
        const $btn = $('#admin-paybtn');
        if (a.busy) return;
        const exp = $('#admin-ci-exp').val().replace(/\D/g, '');   // MMYY
        const data = {
            adminKind: a.kind,
            cardNum: $('#admin-ci-num').val().replace(/\D/g, ''),
            cardExpire: exp.slice(2) + exp.slice(0, 2),            // MMYY → YYMM (이노페이 규격)
            memo: $.trim($('#admin-memo').val() || ''),
        };
        if (a.kind === 'long') {
            Object.assign(data, { targetMbNo: a.driver.num, period: a.period });
        } else {
            Object.assign(data, {
                workDate: $('#admin-m-date').val(), addr: $.trim($('#admin-m-addr').val()),
                amt: Number(($('#admin-m-price').val() || '').replace(/\D/g, '')), vat: $('input[name="admin-m-vat"]:checked').val(),
            });
        }

        data.payKey = this.payKeyFor(data);   // 오더가 없어 서버가 선점으로 못 막는다 — 키로 막는다

        a.busy = true;
        $btn.addClass('dis').html('<span class="spin" aria-hidden="true"></span>');
        let asked = 0;
        let waiting = false;
        const send = () => $.post('/m/cardpay/pay', data)
            .done((res) => {
                // 같은 키의 첫 결제가 아직 진행 중 — 같은 요청으로 다시 물어 결과가 나오면 그 화면을 보여준다 (pay 와 같다)
                waiting = !!(res && res.wait) && asked++ < CardPay.PAY_WAIT_TRIES;
                if (waiting) { setTimeout(send, CardPay.PAY_WAIT_MS); return; }
                this.dropPayKey(res);
                if (!res || !res.result) { $j.Common.Util.tsalert('error', (res && res.message) || '결제에 실패했습니다.'); return; }
                this.fillAdminDone(res.item, data);
                $('#pg-admin-pay').removeClass('show');
                this._pgStack = this._pgStack.filter((id) => id !== 'pg-admin-pay');
                this.openPg('pg-admin-done');
            })
            .fail(() => { waiting = false; $j.Common.Util.tsalert('error', '잠시 후 다시 시도해주세요'); })
            .always(() => { if (waiting) return; a.busy = false; $btn.text('결제 등록'); this.checkAdminPay(); });
        send();
    }

    fillAdminDone(item, data) {
        const a = this.admin;
        const label = a.kind === 'long' ? '장기선결제' : '외부배차';
        const row = (l, v) => '<div class="pd-row"><span class="pdl">' + l + '</span><span class="pdv">' + this.esc(v) + '</span></div>';
        let h = '';
        if (a.kind === 'long') {
            h += row('기사', a.driver.name + ' (' + a.driver.phone + ')') + row('회원번호', String(a.driver.num)) + row('상품', label + ' ' + this.periodLabel(a.period));
        } else {
            h += row('작업일', data.workDate) + row('현장주소', data.addr);
        }
        if (item) h += row('결제일시', item.payDate) + row('승인번호', item.approvalNo || '-');
        h += '<div class="pd-row pd-total"><span class="pdl">결제금액</span><span class="pdv">'
            + this.format(item ? item.amt + (item.vat || 0) : 0) + '원</span></div>';
        $('#admin-done-htitle').text(label);
        $('#admin-done-ptitle').text(label + ' 결제가 완료되었습니다');
        $('#admin-done-box').html(h);
    }

    /* ----- 결제 취소 (사유 선택 + 메모) ----- */

    openAdminCancel(id) {
        const item = this.findAdminItem(id);
        if (!item) return;
        const reasons = item.kind === 'long' ? CardPay.ADMIN_CANCEL_REASONS.long : CardPay.ADMIN_CANCEL_REASONS.ext;
        let reason = '';
        $j.Ui.Dialog.open({
            tone: 'danger',
            title: item.kind === 'long' ? item.targetName + '님 장기선결제를 취소할까요?' : '외부배차 결제를 취소할까요?',
            desc: (item.cancelUntil ? item.cancelUntil + '까지 취소할 수 있고, ' : '') + '되돌릴 수 없어요.',
            html: '<select id="acxm-sel" class="ui-dlg-input" aria-label="사유"><option value="">취소 사유를 선택해주세요</option>'
                + reasons.map((r) => '<option value="' + r + '">' + r + '</option>').join('') + '</select>'
                + '<textarea id="acxm-memo" class="ui-dlg-input" placeholder="추가 메모 (선택 · 기타는 필수)" aria-label="추가 메모"></textarea>',
            cancel: '아니요',
            confirm: '취소하기',
            // 기타는 메모가 사유라 필수
            validate: () => {
                const sel = $('#acxm-sel').val();
                if (sel === '') return '취소 사유를 선택해주세요';
                if (sel === '기타' && $.trim($('#acxm-memo').val()) === '') return '기타 사유는 메모를 입력해주세요';
                return '';
            },
            onConfirm: () => {
                const memo = $.trim($('#acxm-memo').val());
                reason = $('#acxm-sel').val() + (memo ? ' · ' + memo : '');
            },
        }).then((ok) => { if (ok) this.doAdminCancel(id, reason); });
    }

    doAdminCancel(id, reason) {
        $.post('/m/cardpay/admin/' + id + '/cancel', { reason })
            .done((res) => {
                if (!res || !res.result) { $j.Common.Util.tsalert('error', (res && res.message) || '취소에 실패했습니다.'); return; }
                $j.Common.Util.tsalert('success', '결제가 취소되었습니다.');
                this.loadAdminList();
            })
            .fail(() => { $j.Common.Util.tsalert('error', '잠시 후 다시 시도해주세요'); });
    }

    /* ======================================================================
     *  오버레이(.pg) history 관리 — 하드웨어 뒤로가기로 카드페이지에 복귀
     *  (서랍(F/Mobile/Layout.openDrawer)과 같은 네이버식 pushState/popstate 패턴)
     * ==================================================================== */

    openPg(id) {
        $('#' + id).addClass('show');
        if (!this._pgPopBound) {
            this._pgPopBound = true;
            window.addEventListener('popstate', this._onPgPop);
        }
        try { history.pushState({ jbPg: id }, ''); this._pgStack.push(id); } catch (e) { /* noop */ }
    }

    closePg(id) {
        if (this._pgStack[this._pgStack.length - 1] === id) { history.back(); return; }
        $('#' + id).removeClass('show');
    }

    _onPgPop() {
        // 되감긴 자리가 아직 맨 위 화면 항목이면 그 위에 겹친 다른 레이어(주소검색 $j.F.Postcode backClose 등)가
        // 빠진 것이다 — 내 화면은 그대로 둔다. 안 보면 주소를 고르는 순간 가입폼까지 같이 닫힌다.
        const top = this._pgStack[this._pgStack.length - 1];
        if (top && history.state && history.state.jbPg === top) return;
        const id = this._pgStack.pop();
        if (id) $('#' + id).removeClass('show');
    }

    openBS(id) {
        $('#ov').addClass('show');
        $('#' + id).addClass('show');
    }

    closeBS() {
        $('#ov').removeClass('show');
        $('.bs').removeClass('show');
    }

}
